import { useEffect, useMemo, useRef, useState, Fragment } from 'react';
import API from '../api';
import FileDropZone from '../components/FileDropZone';
import SysMailConfigCard from '../components/SysMailConfigCard';
import { useAuth } from '../context/AuthContext';

// ── ⚙️ 환경설정 (2026-09 신규) ────────────────────────────────────────────
// 계정관리(mypage) 바로 아래 위치하는 신규 메뉴. 기본은 최고관리자/관리자만 접근 가능하지만,
// 계정관리 > 권한설정에서 'settings' 메뉴 권한을 개별 계정에 부여할 수도 있다(백엔드
// backend/routes/companyInfo.js의 requireMenuPermission('settings') 참고).
//
// 5개 섹션으로 구성:
//  1. 회사정보 — 기존에 계정관리(MyPage.js)에 있던 CompanyInfoModal을 그대로 이 화면으로
//     이전(로직 100% 동일, 모달이 아닌 페이지 섹션 형태로만 변경).
//  2. 브랜드 설정 — 상단 메뉴바/로그인화면의 "NINE IMS" 문구 중 "NINE" 부분(브랜드명)과
//     로그인화면 원형 배지의 이니셜을 회사에 맞게 바꿀 수 있다. brand_name/brand_initial은
//     인증 없이도 GET /company-info/brand-public으로 노출되어 로그인 전 화면에서도 쓰인다.
//  3. 로고 관리 — 한글/영문 × 가로형/세로형 + 심볼, 총 5종까지 등록. 종류별로 여러 장 등록
//     가능하며, 로고를 쓰는 화면/문서에서 필요한 종류를 선택해 적용한다.
//  4. 명함양식 관리 — 명함 제작(인쇄/외주) 시 참고할 디자인 양식 파일을 등록/보관.
//  5. 기타 정보 — 그 외 회사 관련 정보를 자유롭게 입력(company_info.etc_info).
const LOGO_TYPE_LABEL = {
  kr_h: '국문 가로형', kr_v: '국문 세로형',
  en_h: '영문 가로형', en_v: '영문 세로형',
  symbol: '심볼',
};
const LOGO_TYPES = ['kr_h', 'kr_v', 'en_h', 'en_v', 'symbol'];

export default function Settings() {
  const [tab, setTab] = useState('company'); // company / brand / logo / card / etc
  const [msg, setMsg] = useState(null); // { text, type }
  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 2500);
  };
  // 요청사항: "적용버튼을 누르면 현재 구성되어 있는 사항이 저장 및 각 문서에 적용될 수 있도록
  // 반영" — 상단 헤더의 "적용" 버튼은 이 컴포넌트(Settings) 소유지만, 실제로 저장할 값(회사약어
  // 등)은 DocNumberSection 내부 state이므로 DocNumberSection이 마운트될 때 자신의 저장 함수를
  // applyRef에 등록해두고, 헤더 버튼 클릭시 그 함수를 호출하는 방식으로 연결한다.
  const applyRef = useRef(null);
  const [applying, setApplying] = useState(false);
  const handleApply = () => {
    if (!applyRef.current) return;
    setApplying(true);
    Promise.resolve(applyRef.current()).finally(() => setApplying(false));
  };

  // 2026-09 요청: "적용버튼 왼쪽에 단일관리, 분야별관리 버튼을 생성 / 현재 구성된 문서번호는
  // 단일관리 버튼을 눌렀을때 나타남 / 분야별관리 버튼을 누르면 단일관리 왼쪽에 항목추가 버튼
  // 추가" — 백엔드가 이미 doc_number_scheme.mode('single'|'byType')를 지원하므로(문서번호
  // 등록/조회 라우트가 실제로 참조), 이 토글도 그 값을 그대로 쓴다. 초기값은 DocNumberSection이
  // GET /doc-reg/scheme으로 조회해 onSchemeLoaded로 올려준다. "항목추가"는 분야별관리에서
  // 양식번호/문서번호/기록발생/외부등록문서 4개 패널 묶음을 하나 더 늘리는 기능으로,
  // DocNumberSection 내부의 items 배열에 추가하는 함수를 registerAddItem으로 등록해둔다.
  const [docMode, setDocMode] = useState('single');
  const addItemRef = useRef(null);
  const handleAddItem = () => { if (addItemRef.current) addItemRef.current(); };
  // 2026-09(15차) — 전자결재의 "양식관리" 탭을 이 화면(문서번호 탭 바로 오른쪽)으로 그대로
  // 옮겨왔다. "양식추가" 버튼은 예전 전자결재 헤더에 있던 것과 동일하게 이 화면 헤더에 둔다.
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false); // 서식 일괄삭제(관리자/최고관리자)
  const [formCount, setFormCount] = useState(0); // 등록된 서식 건수(헤더 표기용)
  const { user: authUser } = useAuth();
  const isAdminUser = authUser && (authUser.role === 'admin' || authUser.role === 'superadmin');
  // 2026-09(로고관리 화면 개편) — 종류선택+드래그앤드롭 업로드 UI를 페이지 본문에서 빼서
  // 헤더의 "+ 로고추가" 버튼을 눌러야 뜨는 모달로 이동.
  const [showAddLogo, setShowAddLogo] = useState(false);
  // 2026-09(브랜드 설정 대개편) — 여러 브랜드(사업부/제품라인 등)를 등록/관리하는 화면으로 전환.
  // "+ 브랜드추가" 버튼은 헤더에 두고, 실제 등록 모달은 BrandSection 내부에서 열고 닫는다
  // (로고관리의 "+ 로고추가" 버튼과 동일한 리프팅 패턴).
  const [showAddBrand, setShowAddBrand] = useState(false);
  // 2026-09(명함양식 관리 개편) — "양식등록"(명함 디자인 양식 등록)과 "명함등록"(직원현황에서
  // 골라 양식에 자동반영)을 헤더 버튼 2개로 분리. 각 모달은 CardTemplateSection 내부에서
  // 열고 닫는다(브랜드추가/로고추가와 동일한 리프팅 패턴).
  const [showAddCardTemplate, setShowAddCardTemplate] = useState(false);
  const [showAddCardIssue, setShowAddCardIssue] = useState(false);
  // 2026-09 추가 — 명함양식 관리 탭에서 안내문구를 없애는 대신, 상단 탭 바 가장 우측에
  // 명함등록 목록(이름/부서/직급/연락처/이메일) 검색창을 둔다. 다른 탭에서는 의미가 없으므로
  // tab === 'card'일 때만 렌더링.
  const [cardIssueSearch, setCardIssueSearch] = useState('');
  // 2026-09(로그기록 탭 컨트롤 위치 개편) — 요청사항: "년도, 월, 비정상접속만보기를 상단 탭
  // 우측 끝으로 이동하고 비정상접속만 보기 버튼은 제일 좌측에 위치". 명함양식 관리 탭의
  // cardIssueSearch와 동일한 패턴(상위 Settings가 값을 들고 있다가 탭바에 렌더링)으로,
  // SecurityLogsTab 내부에 있던 년/월/비정상접속 상태를 이 상위 컴포넌트로 끌어올렸다.
  const now2609 = new Date();
  const [logsYear, setLogsYear] = useState(String(now2609.getFullYear()));
  const [logsMonth, setLogsMonth] = useState(String(now2609.getMonth() + 1));
  const [logsOnlyAbnormal, setLogsOnlyAbnormal] = useState(false);
  const [logsAbnormalCount, setLogsAbnormalCount] = useState(0);
  // 2026-09(회사정보 레이아웃 개편) — "사업자등록증, 통장사본 업로드는 환경설정 글자가 있는
  // 우측 끝으로 이동하여 버튼을 생성하여 모달창을 통하여 등록" 요청에 따라, 회사정보 탭일 때
  // 헤더 우측에 버튼을 두고 실제 업로드 모달은 CompanyInfoSection 내부에서 열고 닫는다.
  // 2026-09(3차) — "통장사본, 사업자등록증 버튼을 별도로" 요청에 따라 버튼/모달을 2개로 분리.
  const [showCertUpload, setShowCertUpload] = useState(false);
  const [showBankUpload, setShowBankUpload] = useState(false);
  // 2026-09(회사정보 레이아웃 6차 개편) — 요청사항: "인감도장, 대표자서명은 통장사본
  // 왼쪽편에 모달창으로 등록할 수 있게 수정(등록되면 버튼색상이 나타나고 등록되지 않으면
  // 버튼색상을 없게하여 구분)". 모달 표시여부(showStampUpload/showSignatureUpload)와
  // 등록여부(stampRegistered/signatureRegistered — 버튼 색상 결정용)를 이 상위 컴포넌트가
  // 들고 있어야 헤더 버튼 스타일을 결정할 수 있으므로, CompanyInfoSection이 폼을 불러올
  // 때마다 stamp_image/signature_image 존재 여부를 setStampRegistered/setSignatureRegistered로
  // 올려보내주는 방식(showCertUpload 등과 동일한 리프팅 패턴)으로 연결한다.
  const [showStampUpload, setShowStampUpload] = useState(false);
  const [showSignatureUpload, setShowSignatureUpload] = useState(false);
  // 2026-09(사업장정보 모달 신설) 추가 — 요청사항 "통장사본 등록 버튼 왼쪽에 사업장정보
  // 버튼 추가". 기존에 페이지 본문에 항상 펼쳐져 있던 "사업장 정보" 섹션(자가/임대 체크박스
  // + 관련 입력란)을 통장사본/사업자등록증과 동일한 모달 패턴으로 옮긴다.
  const [showPropertyUpload, setShowPropertyUpload] = useState(false);
  const [stampRegistered, setStampRegistered] = useState(false);
  const [signatureRegistered, setSignatureRegistered] = useState(false);
  // 등록여부에 따라 버튼 색상 on/off — 등록되면 채워진 PRIMARY 버튼, 안 되어 있으면 테두리만
  // 있는 무채색 버튼으로 구분한다.
  // 2026-09(우측상단 버튼 색상 통일) — 다른 메뉴(계약관리/인증관리/업무관리 등)는 헤더 버튼을
  // 모두 단색 배경(테두리 없음)으로 통일하고 있다(주요 액션은 PRIMARY, 보조/토글 액션은
  // 슬레이트 색 #475569). Settings.js만 흰바탕+테두리(outline) 버튼을 쓰고 있던 것을 이 규칙에
  // 맞춰 단색으로 교체한다. 등록됨/미등록 구분은 색상 자체(PRIMARY ↔ 슬레이트)로 유지한다.
  const SECONDARY = '#475569';
  const regBtnStyle = (registered) => btn(registered ? PRIMARY : SECONDARY);
  const modeBtnStyle = (active) => btn(active ? PRIMARY : SECONDARY);

  const tabs = [
    { key: 'company', label: '회사정보' },
    { key: 'mail', label: '메일관리' },
    { key: 'logo', label: '로고 관리' },
    { key: 'brand', label: '브랜드 설정' },
    { key: 'card', label: '명함양식 관리' },
    { key: 'docnum', label: '문서번호' },
    { key: 'forms', label: '서식관리' },
    { key: 'logs', label: '로그기록' },
    { key: 'etc', label: '기타 정보' },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1690 }}>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>⚙️ 환경설정</h2>
          <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0' }}>
            회사정보, 브랜드, 로고, 명함양식 등 시스템 전반에서 사용하는 회사 관련 정보를 관리합니다.
          </p>
        </div>
        {tab === 'docnum' && (
          <div className="hdr-actions" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {/* 2026-09(문서번호 화면 레이아웃 개편) — "관리체계" 버튼은 삭제, "문서번호 등록"은
                "적용"으로 라벨만 바꾼다. 요청사항(추가): 클릭시 현재 구성(회사약어)을 실제로
                저장해 문서번호 체계(doc_number_scheme)에 반영한다.
                2026-09 추가: 적용 버튼 왼쪽에 단일관리/분야별관리 토글, 분야별관리일 때는 그
                왼쪽에 항목추가 버튼까지 순서대로 배치(항목추가 → 단일관리 → 분야별관리 → 적용). */}
            {docMode === 'byType' && (
              <button onClick={handleAddItem} style={{ ...modeBtnStyle(false), fontWeight: 700 }}>+ 항목추가</button>
            )}
            <button onClick={() => setDocMode('single')} style={modeBtnStyle(docMode === 'single')}>단일관리</button>
            <button onClick={() => setDocMode('byType')} style={modeBtnStyle(docMode === 'byType')}>분야별관리</button>
            <button onClick={handleApply} disabled={applying} style={btn(applying ? '#aaa' : PRIMARY)}>
              {applying ? '적용 중...' : '+ 적용'}
            </button>
          </div>
        )}
        {tab === 'forms' && (
          <div className="hdr-actions" style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: '#555', marginRight: 4 }}>등록된 서식 : <b>{formCount}</b> 건</span>
            {isAdminUser && <button onClick={() => setShowBulkDelete(true)} style={btn('#dc2626')}>일괄삭제</button>}
            <button onClick={() => setShowAddForm(true)} style={btn(PRIMARY)}>+ 서식추가</button>
          </div>
        )}
        {/* 2026-09(로고관리 화면 개편) — 종류선택+드래그앤드롭 업로드 UI를 본문에서 빼고
            헤더의 "+ 로고추가" 버튼을 눌러야 뜨는 모달로 이동. */}
        {tab === 'logo' && (
          <div className="hdr-actions" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => setShowAddLogo(true)} style={btn(PRIMARY)}>+ 로고추가</button>
          </div>
        )}
        {tab === 'brand' && (
          <div className="hdr-actions" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => setShowAddBrand(true)} style={btn(PRIMARY)}>+ 브랜드추가</button>
          </div>
        )}
        {tab === 'card' && (
          <div className="hdr-actions" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => setShowAddCardTemplate(true)} style={btn(SECONDARY)}>+ 양식등록</button>
            <button onClick={() => setShowAddCardIssue(true)} style={btn(PRIMARY)}>+ 명함등록</button>
          </div>
        )}
        {tab === 'company' && (
          <div className="hdr-actions" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {/* 2026-09(회사정보 버튼 라벨 정리) 변경 — 요청사항 "통장사본, 사업자등록증
                뒤에 '등록' 글자 삭제" / "서명등록 -> '대표이사 서명'으로 수정" / "도장등록
                -> '인감도장'으로 수정". */}
            <button onClick={() => setShowStampUpload(true)} style={regBtnStyle(stampRegistered)}>🖋️ 인감도장</button>
            <button onClick={() => setShowSignatureUpload(true)} style={regBtnStyle(signatureRegistered)}>✍️ 대표이사 서명</button>
            {/* 2026-09(사업장정보 모달 신설) 추가 — 요청사항 "통장사본 등록 버튼 왼쪽에
                사업장정보 버튼 추가". */}
            <button onClick={() => setShowPropertyUpload(true)} style={btn(SECONDARY)}>🏢 사업장정보</button>
            <button onClick={() => setShowBankUpload(true)} style={btn(PRIMARY)}>📎 통장사본</button>
            <button onClick={() => setShowCertUpload(true)} style={btn(PRIMARY)}>📎 사업자등록증</button>
          </div>
        )}
      </div>

      {msg && (
        <div style={{
          marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13,
          background: msg.type === 'error' ? '#fee2e2' : '#d1fae5',
          color: msg.type === 'error' ? '#dc2626' : '#065f46',
        }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', marginBottom: 18, borderBottom: '1px solid #e5e7eb' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              color: tab === t.key ? '#185FA5' : '#888',
              borderBottom: tab === t.key ? '2px solid #185FA5' : '2px solid #e5e7eb',
              marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
        {tab === 'card' && (
          <input value={cardIssueSearch} onChange={e => setCardIssueSearch(e.target.value)}
            placeholder="🔍 이름/부서/직급/연락처/이메일 검색"
            style={{ marginLeft: 'auto', marginBottom: 8, padding: '7px 12px', fontSize: 13,
              border: '1px solid #ddd', borderRadius: 8, outline: 'none', width: 240 }} />
        )}
        {tab === 'logs' && (
          <div style={{ marginLeft: 'auto', marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setLogsOnlyAbnormal(v => !v)}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: 'none', background: logsOnlyAbnormal ? '#dc2626' : '#f1f5f9',
                color: logsOnlyAbnormal ? '#fff' : '#475569',
              }}>
              ⚠️ 비정상접속만 보기{logsAbnormalCount ? ` (${logsAbnormalCount})` : ''}
            </button>
            <select value={logsYear} onChange={e => setLogsYear(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
              {Array.from({ length: 6 }, (_, i) => now2609.getFullYear() - i).map(y => <option key={y} value={y}>{y}년</option>)}
            </select>
            <select value={logsMonth} onChange={e => setLogsMonth(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
              <option value="">전체</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}월</option>)}
            </select>
          </div>
        )}
      </div>

      {tab === 'company' && (
        <CompanyInfoSection showMsg={showMsg}
          showCertUpload={showCertUpload} setShowCertUpload={setShowCertUpload}
          showBankUpload={showBankUpload} setShowBankUpload={setShowBankUpload}
          showStampUpload={showStampUpload} setShowStampUpload={setShowStampUpload}
          showSignatureUpload={showSignatureUpload} setShowSignatureUpload={setShowSignatureUpload}
          showPropertyUpload={showPropertyUpload} setShowPropertyUpload={setShowPropertyUpload}
          setStampRegistered={setStampRegistered} setSignatureRegistered={setSignatureRegistered} />
      )}
      {tab === 'mail' && <SysMailConfigCard showMsg={showMsg} />}
      {tab === 'brand' && <BrandSection showMsg={showMsg} showAddBrand={showAddBrand} setShowAddBrand={setShowAddBrand} />}
      {tab === 'logo' && <LogoSection showMsg={showMsg} showAddLogo={showAddLogo} setShowAddLogo={setShowAddLogo} />}
      {tab === 'card' && (
        <CardTemplateSection showMsg={showMsg}
          showAddCardTemplate={showAddCardTemplate} setShowAddCardTemplate={setShowAddCardTemplate}
          showAddCardIssue={showAddCardIssue} setShowAddCardIssue={setShowAddCardIssue}
          searchTerm={cardIssueSearch} />
      )}
      {tab === 'docnum' && (
        <DocNumberSection showMsg={showMsg} registerApply={(fn) => { applyRef.current = fn; }}
          docMode={docMode} onSchemeLoaded={setDocMode} registerAddItem={(fn) => { addItemRef.current = fn; }} />
      )}
      {tab === 'forms' && <FormsTab showMsg={showMsg} showAdd={showAddForm} setShowAdd={setShowAddForm} showBulkDelete={showBulkDelete && isAdminUser} setShowBulkDelete={setShowBulkDelete} onCount={setFormCount} />}
      {tab === 'logs' && (
        <SecurityLogsTab showMsg={showMsg}
          year={logsYear} month={logsMonth} onlyAbnormal={logsOnlyAbnormal}
          onAbnormalCount={setLogsAbnormalCount} />
      )}
      {tab === 'etc' && <EtcInfoSection showMsg={showMsg} />}
    </div>
  );
}

const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 10, outline: 'none' };
const lbl = { fontSize: 13, color: '#555', fontWeight: 500, display: 'block', marginBottom: 4 };
// 2026-09(등록 모달 4종 개편) — 모달 우측상단 닫기(✕) 버튼 왼쪽에 배치하는 삭제 버튼 공용 스타일.
const headerDeleteBtn = { background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' };
const section = { fontSize: 13, fontWeight: 700, color: '#185FA5', margin: '18px 0 8px', paddingTop: 12, borderTop: '1px solid #eee' };
const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const row3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 };
const row4 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 };
const row5 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 12 };
// 2026-09(사업장정보 레이아웃 개편) 추가 — "임대부분/임대면적/보증금/임대료/관리인/관리인연락처
// 6개를 한줄에 표기" 요청에 따른 6등분 그리드.
const row6 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: 10 };
// 2026-09(사업장정보 레이아웃 개편) 추가 — 보증금/임대료는 1000단위 콤마 + "원" 표기,
// 토지·건물·임대면적은 "㎡" 단위를 입력값 뒤에 표시한다. 실제 저장값은 숫자만(콤마 없이)
// 유지하고, 화면 표시만 콤마/단위를 덧붙인다.
const onlyDigits = (s) => (s || '').toString().replace(/[^0-9]/g, '');
const withComma = (s) => {
  const d = onlyDigits(s);
  return d ? Number(d).toLocaleString('ko-KR') : '';
};
const unitWrap = { position: 'relative', marginBottom: 10 };
const unitInp = { ...inp, marginBottom: 0, paddingRight: 30 };
const unitSuffix = { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12.5, color: '#888', pointerEvents: 'none' };
// 2026-09(회사정보 레이아웃 6차 개편) — "회사명, 대표자명, 사업자등록번호, 법인등록번호,
// 전화, 팩스, 이메일까지 한줄에 표기" 요청에 따른 7등분 그리드.
const row7 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr', gap: 10 };
// 2026-09(회사정보 css 최종수정) 추가 — 요청사항 "대표자명과 사업자등록번호 사이에 설립일을
// 추가"로 상단 정보행이 7칸→8칸이 되어 row7과 별도 그리드로 둔다.
const row8 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr', gap: 10 };
// 2026-09(회사정보 css 최종수정) 추가 — 요청사항 3-1 "통장사본 등록 모달창 가로폭을 늘려서
// 은행명/예금주/계좌번호/비고/주거래/삭제를 한줄에 표기"와 4-2 "사업자등록증 등록 입력폼의
// 크기를 통장사본 입력폼 폭과 동일하게 구성" — 두 모달이 같은 상수를 공유하게 한다.
const BANK_MODAL_WIDTH = 920;
// 2026-09(사업장정보 한줄 표기 개편) 추가 — 요청사항 "본점, 자가, 소재지, 건물용도, 면적, 보증금,
// 임대료, 관리비, 계약일, 종료일, 비고, 상세 순서로 입력"에 따른 12칸 그리드.
// 2026-09(소재지 칸 폭 조정) 변경 — 요청사항 "소재지 입력칸을 약 1.5배로 늘리고 나머지는
// 균등하게 줄임": 소재지(3번째 칸)만 1.5fr, 나머지 11칸은 모두 동일한 1fr로 균등 분배한다.
// 2026-09(구분/자가/면적 칸 폭 축소) 변경 — 요청사항 "구분, 자가, 면적 입력칸을 50% 줄이고
// 소재지를 늘림": 구분(1번)·자가(2번)·면적(5번) 칸을 0.5fr로 줄이고, 그만큼(1.5fr) 소재지
// (3번) 칸에 더해 3fr로 늘린다. 나머지 칸은 기존 1fr을 유지한다.
// 2026-09(구분/자가/면적 칸 폭 재조정) 변경 — 요청사항 "글자가 잘리니 지금 있는 부분에서
// 25%정도를 늘려줌": 0.5fr → 0.625fr(0.5의 25% 증가)로 세 칸을 넓히고, 늘어난 만큼
// (0.125*3=0.375fr) 소재지(3번) 칸에서 덜어내 2.625fr로 줄인다.
// 2026-09(임대면적/비고 칸 폭 조정) 변경 — 요청사항 "임대면적 부분을 약 30% 늘리고 비고
// 부분을 줄여서 맞춤": 임대면적(5번) 칸을 0.625fr → 0.8125fr(약 30% 증가)로 늘리고,
// 늘어난 만큼(0.1875fr) 비고(11번) 칸에서 덜어내 0.8125fr로 줄인다.
const rowBiz = { display: 'grid', gridTemplateColumns: '0.625fr 0.625fr 2.625fr 1fr 0.8125fr 1fr 1fr 1fr 1fr 1fr 0.8125fr 1fr', gap: 8 };

// ── 1. 회사정보 — MyPage.js의 옛 CompanyInfoModal 로직을 그대로 페이지 섹션으로 이전 ──────
function CompanyInfoSection({ showMsg, showCertUpload, setShowCertUpload, showBankUpload, setShowBankUpload,
  showStampUpload, setShowStampUpload, showSignatureUpload, setShowSignatureUpload,
  showPropertyUpload, setShowPropertyUpload,
  setStampRegistered, setSignatureRegistered }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [bankBooks, setBankBooks] = useState([]);
  const [bankUploading, setBankUploading] = useState(false);
  // 2026-09(OCR 자동입력) 추가 — 사업자등록증/통장사본 첨부시 업체관리(Companies.js)와 동일한
  // OCR 유틸(recognizeBizCert/recognizeBankbook)로 미리 인식해서 빈 칸만 자동으로 채운다.
  // 이미 값이 있는 필드는 덮어쓰지 않고, 인식이 끝나면 노란 안내배너로 "확인 후 저장" 문구를
  // 보여준다(자동저장이 아니라 참고용 — 업체관리와 동일한 UX 패턴).
  const [ocrLoading, setOcrLoading] = useState(null); // 'cert' | 'bankbook' | null
  const [ocrNotice, setOcrNotice] = useState('');
  // 2026-09(사업장정보 한줄 표기 개편) 추가 — 요청사항 "상세는 버튼으로 구성하고 상세를 누르면
  // 해당 임대차계약서 또는 등기부등본을 미리보고, 상세 정보를 볼 수 있도록 구성". 한줄로 압축된
  // 사업장 정보 행에서 빠진 나머지 세부항목(임대부분/토지면적/건물구조/임대인/임대인연락처/
  // 관리인/관리인연락처/임대료납부계좌)과 첨부문서 미리보기를 이 모달 하나에 모아서 보여준다.
  const [showPropertyDetail, setShowPropertyDetail] = useState(false);
  // 2026-09(추가 사업장 기능) 추가 — 요청사항 "사업자정보에서 임대차계약서나 등기부등본이
  // 추가로 등록되면 지사, 공장 등으로 추가되게 수정". 위 사업장정보(본점)와 별개로, 지사/공장을
  // 여러 건 등록할 수 있는 목록(company_biz_locations)이다. 신규 등록은(2026-09 2차 수정)
  // "🏢 사업장정보" 버튼의 모달(showPropertyUpload) 안 "추가 사업장" 섹션에서 하며,
  // showBizLocDetailId가 숫자면 해당 사업장의 상세 모달을 연다.
  const [bizLocations, setBizLocations] = useState([]);
  const [showBizLocDetailId, setShowBizLocDetailId] = useState(null);
  const [bizLocNewType, setBizLocNewType] = useState('지사');
  const [bizLocNewLeaseType, setBizLocNewLeaseType] = useState('lease');
  // 2026-09(사업장정보 모달 개편) 추가 — "제일 아래쪽 지사, 임대 추가하는 부분에도 직접입력
  // 칸을 넣고 한줄에 표기" 요청에 따른, 신규 사업장 등록 시 구분이 "공장"일 때 사용할 공장번호
  // 직접입력 상태(예: "1공장"). 등록 성공 시 다른 입력값과 함께 초기화한다.
  const [bizLocNewFactoryNo, setBizLocNewFactoryNo] = useState('');
  const [bizLocUploading, setBizLocUploading] = useState(false);
  // 2026-09(추가 사업장 OCR 자동입력) 추가 — "사업장 정보 추가시 임대차계약서 및 전대차
  // 계약서도 ocr로 읽어서 자동으로 입력되게" 요청에 따른 안내문구 상태. 본점용 ocrNotice와
  // 동일한 역할이지만, 이 모달은 별도 영역이라 상태를 분리해 서로 섞이지 않게 한다.
  const [bizLocOcrNotice, setBizLocOcrNotice] = useState('');
  const loadBizLocations = () => {
    API.get('/company-info/biz-locations').then(r => setBizLocations(r.data || [])).catch(() => {});
  };
  const updateBizLoc = (id, field, value) => {
    setBizLocations(list => list.map(l => l.id === id ? { ...l, [field]: value } : l));
  };
  const saveBizLocField = (id, field) => (e) => {
    const value = e.target.value;
    API.put(`/company-info/biz-locations/${id}`, { [field]: value }).catch(() => {});
  };
  const deleteBizLocation = (id) => {
    if (!window.confirm('이 사업장을 삭제합니다. 계속할까요?')) return;
    API.delete(`/company-info/biz-locations/${id}`)
      .then(() => { showMsg('삭제되었습니다.', 'success'); loadBizLocations(); if (showBizLocDetailId === id) setShowBizLocDetailId(null); })
      .catch(() => showMsg('삭제 중 오류가 발생했습니다.', 'error'));
  };
  const deleteBizLocFile = (id, routeField) => () => {
    API.delete(`/company-info/biz-locations/${id}/${routeField}`)
      .then(() => { showMsg('삭제되었습니다.', 'success'); loadBizLocations(); })
      .catch(() => showMsg('삭제 중 오류가 발생했습니다.', 'error'));
  };
  // "+ 사업장 추가" 업로드 모달에서 임대차계약서/등기부등본/전대사용동의서 파일을 올리면, 그
  // 즉시 새 사업장(지사/공장) 1건을 만들면서 파일도 같이 저장한다(등록=추가, 별도의 "등록"
  // 버튼 없음).
  // 2026-09(추가 사업장 OCR 자동입력) 변경 — "사업장 정보 추가시 임대차계약서 및 전대차
  // 계약서도 ocr로 읽어서 자동으로 입력되게" 요청에 따라, 등록(POST) 전에 문서 종류에 맞는
  // OCR 미리보기 라우트를 먼저 호출해 인식 결과를 얻고, 그 값을 등록 요청에 함께 실어 보낸다.
  // 본점 OCR과 동일한 미리보기-then-merge 개념이지만, 이 목록은 아직 DB에 행이 없는 신규
  // 등록 시점이라 폼에 직접 채우는 대신 등록 요청 payload에 얹어 한 번에 저장한다. OCR이
  // 실패해도(로컬 AI 혼잡 등) 등록 자체는 계속 진행하고 안내문구만 남긴다.
  const uploadNewBizLocation = async (file) => {
    const isOwn = bizLocNewLeaseType === 'own';
    const isSublease = bizLocNewLeaseType === 'sublease';
    setBizLocUploading(true);
    setBizLocOcrNotice('');
    let ocrFields = null;
    if (!isOwn) {
      try {
        const ocrFd = new FormData();
        ocrFd.append('file', file);
        const ocrRoute = isSublease ? 'biz-sublease-consent' : 'biz-lease-contract';
        const r = await API.post(`/company-info/ocr/${ocrRoute}`, ocrFd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
        if (r.data?.ok && r.data.fields) {
          ocrFields = r.data.fields;
          setBizLocOcrNotice(`📋 ${isSublease ? '전대사용동의서' : '임대차계약서'}에서 자동 인식된 정보를 함께 등록했습니다. 정확하지 않을 수 있으니 상세보기에서 확인 후 필요시 수정해주세요.`);
        } else {
          setBizLocOcrNotice((r.data && r.data.message) || 'OCR 자동인식에 실패했습니다. 등록 후 상세보기에서 직접 입력해주세요.');
        }
      } catch (e) {
        const timedOut = e && (e.code === 'ECONNABORTED' || /timeout/i.test(e.message || ''));
        setBizLocOcrNotice(timedOut
          ? 'OCR 자동인식 시간이 너무 오래 걸려 중단했습니다(로컬 AI가 혼잡할 수 있습니다). 등록 후 직접 입력해주세요.'
          : 'OCR 자동인식 중 오류가 발생했습니다. 등록 후 직접 입력해주세요.');
      }
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('location_type', bizLocNewType);
    if (bizLocNewType === '공장' && bizLocNewFactoryNo.trim()) fd.append('factory_no', bizLocNewFactoryNo.trim());
    fd.append('lease_type', bizLocNewLeaseType);
    fd.append('doc_type', isOwn ? 'registry' : (isSublease ? 'sublease' : 'lease'));
    if (ocrFields) {
      // recognizeLeaseContract/recognizeSubleaseConsent는 각각 lease_*/sublease_* 접두어가
      // 붙은 키로 결과를 돌려준다(본점 company_info의 lease_* 필드용으로 설계된 함수를 그대로
      // 재사용하기 때문). company_biz_locations 컬럼은 접두어가 없으므로(BIZ_LOC_TEXT_FIELDS
      // 참고) 여기서 매핑해 변환한다. 전대차 고유 필드(tenant_name/sublessee_name/consent_date)
      // 만 sublease_ 접두어를 그대로 유지(신규 컬럼명과 동일).
      const fieldMap = isSublease
        ? {
            address: ocrFields.sublease_address,
            landlord: ocrFields.sublease_landlord,
            leased_portion: ocrFields.sublease_leased_portion,
            leased_area: ocrFields.sublease_leased_area,
            sublease_tenant_name: ocrFields.sublease_tenant_name,
            sublease_sublessee_name: ocrFields.sublease_sublessee_name,
            sublease_consent_date: ocrFields.sublease_consent_date,
          }
        : {
            address: ocrFields.lease_address,
            land_area: ocrFields.lease_land_area,
            building_structure: ocrFields.lease_building_structure,
            building_usage: ocrFields.lease_building_usage,
            building_area: ocrFields.lease_building_area,
            leased_portion: ocrFields.lease_leased_portion,
            leased_area: ocrFields.lease_leased_area,
            deposit: ocrFields.lease_deposit,
            monthly_rent: ocrFields.lease_monthly_rent,
            manager: ocrFields.lease_manager,
            manager_contact: ocrFields.lease_manager_contact,
            start_date: ocrFields.lease_start_date,
            end_date: ocrFields.lease_end_date,
            landlord: ocrFields.lease_landlord,
            landlord_contact: ocrFields.lease_landlord_contact,
          };
      Object.entries(fieldMap).forEach(([k, val]) => { if (val) fd.append(k, val); });
    }
    API.post('/company-info/biz-locations', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => {
        showMsg('새 사업장이 등록되었습니다.', 'success');
        setBizLocNewType('지사');
        setBizLocNewLeaseType('lease');
        setBizLocNewFactoryNo('');
        loadBizLocations();
      })
      .catch(err => showMsg(err.response?.data?.message || '등록 중 오류가 발생했습니다.', 'error'))
      .finally(() => setBizLocUploading(false));
  };
  // 2026-09(회사정보 css 최종수정) 추가 — 요청사항 "통장이 여러개인 경우 계좌번호, 비고를
  // 표기하여 다운로드 할 목록을 선택하여 다운". 모달 헤더의 다운로드 버튼을 누르면 통장이
  // 1개(그리고 파일이 있음)면 바로 다운로드하고, 2개 이상이면 이 목록을 펼쳐 계좌번호/비고
  // 기준으로 골라서 받을 수 있게 한다.
  const [bankDownloadOpen, setBankDownloadOpen] = useState(false);
  const [bankDownloadSel, setBankDownloadSel] = useState([]);
  const triggerFileDownload = (fileName, label) => {
    if (!fileName) return;
    const a = document.createElement('a');
    a.href = `${API.defaults.baseURL}/../uploads/company_info/${fileName}`;
    a.download = label || fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  const downloadSelectedBankBooks = () => {
    const targets = bankBooks.filter(b => bankDownloadSel.includes(b.id) && b.file_name);
    targets.forEach((b, i) => setTimeout(() => triggerFileDownload(b.file_name, b.orig_name || b.file_name), i * 300));
    setBankDownloadOpen(false);
    setBankDownloadSel([]);
  };
  const handleBankDownloadClick = () => {
    const withFile = bankBooks.filter(b => b.file_name);
    if (withFile.length === 0) return;
    if (withFile.length === 1) { triggerFileDownload(withFile[0].file_name, withFile[0].orig_name || withFile[0].file_name); return; }
    setBankDownloadSel(withFile.map(b => b.id));
    setBankDownloadOpen(v => !v);
  };

  const loadBankBooks = () => {
    API.get('/company-info/bankbooks').then(r => setBankBooks(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    API.get('/company-info')
      .then(r => {
        const data = r.data || {};
        setForm(data);
        if (setStampRegistered) setStampRegistered(!!data.stamp_image);
        if (setSignatureRegistered) setSignatureRegistered(!!data.signature_image);
      })
      .catch(() => setForm({}));
    loadBankBooks();
    loadBizLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm(v => ({ ...v, [k]: e.target.value }));

  // 빈 필드만 채우고(이미 입력된 값은 보존) OCR 인식결과를 폼에 반영.
  // 업체관리(Companies.js)의 mergeOcrFields와 동일한 패턴.
  // 2026-09(5차) 버그수정 — 요청사항: "사업자등록증을 다시 올려도 업태/이메일이 계속
  // 예전 값 그대로다". 원인은 이 함수가 "빈 칸만" 채우도록 되어 있어서, 이전에(고장난
  // 로직으로) 잘못 채워졌던 biz_type/biz_item/email 값이 이미 폼에 들어있으면 새로 인식한
  // (수정된) 값이 있어도 절대 덮어쓰지 못했다 — 즉 백엔드를 아무리 고쳐도 화면에는 항상
  // "처음 인식됐던 그 값"만 계속 남는 구조적 버그였다. forceKeys로 지정한 필드는 OCR이
  // 값을 새로 찾아냈을 때 기존 값을 덮어써서, 재업로드하면 항상 최신 인식 결과가 반영되게
  // 한다. 나머지 필드(회사명 등 사용자가 손으로 고쳤을 수 있는 값)는 기존처럼 빈 칸만 채운다.
  const mergeOcrFields = (mapping, forceKeys = []) => {
    setForm(v => {
      const next = { ...v };
      Object.entries(mapping).forEach(([k, val]) => {
        if (!val) return;
        if (next[k] && !forceKeys.includes(k)) return;
        next[k] = val;
      });
      return next;
    });
  };

  // 2026-09(업태/종목 구분자 개편) — 요청사항: 사업자등록증 OCR로 인식된 업태/종목 중
  // "전문, 과학 및 기술서비스업"처럼 항목 명칭 자체에 쉼표가 포함된 경우가 있어, 쉼표를
  // 항목 구분자로 쓰면 이런 명칭이 중간에서 잘못 잘린다. 구분자를 파이프(|)로 바꾸고,
  // 기존에 이미 쉼표로 저장되어 있던 값과의 호환을 위해 값에 파이프가 없으면 쉼표로 폴백
  // 분리한다(신규로 저장되는 값은 항상 파이프 구분).
  const toList = (v) => {
    const raw = v || '';
    const parts = raw.includes('|') ? raw.split('|') : raw.split(',');
    const arr = parts.map(s => s.trim()).filter((s, i, a) => s !== '' || i === a.length - 1);
    return arr.length ? arr : [''];
  };
  const setListField = (field) => (idx, value) => {
    setForm(v => {
      const list = toList(v[field]);
      list[idx] = value;
      return { ...v, [field]: list.join(' | ') };
    });
  };
  const addListItem = (field) => () => {
    setForm(v => {
      const list = toList(v[field]).filter(s => s !== '');
      list.push('');
      return { ...v, [field]: list.join(' | ') };
    });
  };
  const removeListItem = (field) => (idx) => {
    setForm(v => {
      const list = toList(v[field]);
      list.splice(idx, 1);
      return { ...v, [field]: list.join(' | ') };
    });
  };

  // 2026-09(업태/종목 병합표시 개편) — 요청사항: "업태가 중복되면(=같은 업태 아래 종목이
  // 여러 줄이면) 두번째 사진처럼(사업자등록증 실물의 병합된 셀처럼) 표현". 업태/종목 목록의
  // 길이가 서로 같을 때(= 종목 한 줄마다 업태가 한 칸씩 대응하는 "행 정렬" 데이터일 때)만
  // 아래 3개 헬퍼로 업태/종목을 한 세트로 다룬다 — 길이가 다르면(과거 OCR로 저장된, 업태만
  // 중복 제거되어 있는 데이터) 기존 방식(두 목록을 독립적으로 렌더링)으로 자동 대체된다.
  const setListRange = (field) => (start, count, value) => {
    setForm(v => {
      const list = toList(v[field]);
      for (let k = 0; k < count; k++) { if (list[start + k] !== undefined) list[start + k] = value; }
      return { ...v, [field]: list.join(' | ') };
    });
  };
  const removeAlignedRows = (start, count) => {
    setForm(v => {
      const types = toList(v.biz_type);
      const items = toList(v.biz_item);
      types.splice(start, count);
      items.splice(start, count);
      return { ...v, biz_type: types.join(' | '), biz_item: items.join(' | ') };
    });
  };
  const addAlignedRow = () => {
    setForm(v => {
      const types = toList(v.biz_type).filter(s => s !== '');
      const items = toList(v.biz_item).filter(s => s !== '');
      items.push('');
      types.push('');
      return { ...v, biz_type: types.join(' | '), biz_item: items.join(' | ') };
    });
  };

  // 2026-09(회사정보 일괄삭제) 추가 — 요청사항 "저장버튼 왼쪽에 일괄삭제 버튼을 추가".
  // 백엔드 companyInfo.js의 TEXT_FIELDS/NUMBER_FIELDS 중 이 섹션(회사정보+사업장정보)에서
  // 실제로 입력칸으로 노출되는 필드만 초기화 대상으로 삼는다. brand_name/brand_initial(2.
  // 브랜드 설정)과 etc_info(6. 기타 정보)는 이 화면과 별개의 섹션이 각자 자신의 state로
  // 조회/저장하므로 여기서 지워버리면 그쪽 섹션의 값까지 덩달아 날아간다 — 그래서 제외한다.
  // 도장/서명/사업자등록증/통장사본/사업장정보 첨부파일은 각자 별도의 삭제 버튼이 있으므로
  // 역시 건드리지 않는다. 초기화는 화면(폼) 상에서만 비우고, 실제 저장은 사용자가 이어서
  // "저장" 버튼을 눌러야 반영된다(즉시 서버로 지워버리지 않음).
  const RESETTABLE_FIELDS = [
    'company_name', 'ceo_name', 'establishment_date', 'biz_reg_no', 'corp_reg_no', 'biz_type', 'biz_item',
    'address', 'hq_address', 'phone', 'fax', 'email',
    'lease_type', 'lease_landlord', 'lease_landlord_contact', 'lease_address',
    'lease_start_date', 'lease_end_date', 'lease_manager', 'lease_manager_contact',
    'lease_land_area', 'lease_building_structure', 'lease_building_usage',
    'lease_building_area', 'lease_leased_portion', 'lease_leased_area',
    'lease_deposit', 'lease_monthly_rent', 'lease_management_fee',
    // 2026-09(종된사업장 명세 추가) — 일괄삭제 시 종된사업장 8개 필드도 함께 초기화한다.
    'sub_biz_reg_no', 'sub_serial_no', 'sub_company_name', 'sub_establishment_date',
    'sub_ceo_name', 'sub_address', 'sub_biz_type', 'sub_biz_item',
    // 2026-09(사업장정보 3행 레이아웃 개편) — 임대료 납부계좌 3필드도 일괄삭제 대상에 포함.
    'lease_rent_bank', 'lease_rent_account_holder', 'lease_rent_account_number',
    // 2026-09(사업장정보 한줄 표기 개편) — 사업장 구분(본점/지사/공장, 공장번호)과 비고도
    // 일괄삭제 대상에 포함.
    'biz_location_type', 'biz_location_factory_no', 'lease_remark',
  ];
  const bulkClearForm = () => {
    if (!window.confirm('입력된 회사정보/사업장정보 항목을 모두 지웁니다.\n(저장 버튼을 눌러야 실제로 반영됩니다) 계속할까요?')) return;
    setForm(v => {
      const next = { ...v };
      RESETTABLE_FIELDS.forEach(f => { next[f] = ''; });
      return next;
    });
    showMsg('입력값을 모두 지웠습니다. 저장 버튼을 눌러야 적용됩니다.');
  };

  const save = () => {
    setSaving(true);
    const cleaned = {
      ...form,
      biz_type: toList(form.biz_type).filter(s => s !== '').join(' | '),
      biz_item: toList(form.biz_item).filter(s => s !== '').join(' | '),
    };
    API.put('/company-info', cleaned)
      .then(() => showMsg('회사정보가 저장되었습니다.', 'success'))
      .catch(err => showMsg(err.response?.data?.message || '저장 중 오류가 발생했습니다.', 'error'))
      .finally(() => setSaving(false));
  };

  const uploadFile = (routeField, formField) => (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append(formField, file);
    setUploadingField(routeField);
    API.post(`/company-info/${routeField}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => {
        setForm(v => ({ ...v, [formField]: r.data.filename, ...(r.data.orig_name ? { [`${formField.replace(/_file$/, '')}_orig_name`]: r.data.orig_name } : {}) }));
        if (routeField === 'stamp' && setStampRegistered) setStampRegistered(true);
        if (routeField === 'signature' && setSignatureRegistered) setSignatureRegistered(true);
        showMsg('업로드되었습니다.', 'success');
      })
      .catch(err => showMsg(err.response?.data?.message || '업로드 중 오류가 발생했습니다.', 'error'))
      .finally(() => setUploadingField(null));

    // 사업자등록증 첨부시 OCR로 빈 칸을 자동으로 채운다 (업로드와 별개로 병행 실행).
    // 2026-09(6차) 버그수정 — 요청사항: "사업자등록증을 올려도 결과가 나오지 않는다".
    // 원인: 이 아래 .then()이 r.data.ok가 false인 경우(백엔드가 OCR 인식 실패를 정상
    // 200 응답으로 내려주는 케이스 — routes/companyInfo.js 참고)에 대한 처리가 전혀 없었고,
    // .catch(() => {})가 네트워크/타임아웃 등 모든 오류를 완전히 무시하고 있었다. 즉
    // OCR이 실패하거나 시간이 오래 걸려 타임아웃 나도 사용자에게는 "🔎 인식하는 중입니다..."
    // 문구가 사라질 뿐 아무 안내도 뜨지 않아, 마치 아무 일도 없었던 것처럼(결과가 전혀
    // 나오지 않는 것처럼) 보였다. 같은 OCR 흐름을 쓰는 업체관리(Companies.js)는 이미 실패시
    // ocrNotice로 안내를 띄우고 있었다 — 그 패턴을 그대로 가져와 실패/오류 시에도 반드시
    // 안내 문구를 표시하고, 요청이 과도하게 오래 걸리는 경우(로컬 AI가 혼잡할 때)에 대비해
    // 클라이언트 타임아웃도 넉넉히(3분) 명시한다.
    if (routeField === 'biz-cert') {
      const ocrFd = new FormData();
      ocrFd.append('file', file);
      setOcrLoading('cert');
      setOcrNotice('');
      API.post('/company-info/ocr/biz-cert', ocrFd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 })
        .then(r => {
          const f = r.data?.fields;
          if (r.data?.ok && f) {
            mergeOcrFields({
              // 요청사항: OCR 자동입력시 "주식회사/사단법인/(주)" 같은 법인격 표기도 함께
              // 채워지도록, 법인격이 제거된 company_name 대신 원문 그대로인 company_name_raw를
              // 우선 사용한다(company_name_raw가 비어있는 구버전 백엔드 대비 폴백 유지).
              company_name: f.company_name_raw || f.company_name,
              ceo_name: f.ceo_name,
              biz_reg_no: f.biz_reg_no,
              // 2026-09(OCR 개선) — 법인등록번호/본점소재지가 아예 매핑되지 않아 자동입력되지
              // 않던 문제 수정. 백엔드(companyOcr.js)가 이제 이 두 필드를 반환한다.
              corp_reg_no: f.corp_reg_no,
              address: f.biz_address,
              hq_address: f.hq_address,
              biz_type: f.biz_type,
              biz_item: f.biz_item,
              email: f.email,
              // 2026-09(설립일 OCR 자동입력) 추가 — 사업자등록증의 "개업연월일"을 백엔드가
              // ISO(YYYY-MM-DD)로 정규화해 반환하면 설립일 <input type="date"> 칸에 그대로
              // 채운다(이미 값이 있으면 mergeOcrFields 기본 동작대로 덮어쓰지 않음).
              establishment_date: f.establishment_date,
              // 2026-09(종된사업장 명세 자동입력) 추가 — 사업자단위과세 적용 종된사업장이
              // 있는 사업자등록증(2페이지)을 인식하면 백엔드가 sub_* 필드를 함께 반환한다.
              // 재업로드시 항상 최신 인식 결과가 반영되도록 forceKeys에도 포함한다.
              sub_biz_reg_no: f.sub_biz_reg_no,
              sub_serial_no: f.sub_serial_no,
              sub_company_name: f.sub_company_name,
              sub_establishment_date: f.sub_establishment_date,
              sub_ceo_name: f.sub_ceo_name,
              sub_address: f.sub_address,
              sub_biz_type: f.sub_biz_type,
              sub_biz_item: f.sub_biz_item,
            }, [
              'biz_type', 'biz_item', 'email',
              'sub_biz_reg_no', 'sub_serial_no', 'sub_company_name', 'sub_establishment_date',
              'sub_ceo_name', 'sub_address', 'sub_biz_type', 'sub_biz_item',
            ]);
            // 2026-09(명함 OCR 완전공백 버그와 동일한 클래스 수정) — 백엔드가 ok:true를
            // 반환해도 실제로는 아무 항목도 인식하지 못했을 수 있다(recognized===false,
            // 정규식/비전/LLM 전부 실패). 이 경우 "빈 칸을 채웠습니다"라는 거짓 성공 메시지
            // 대신 백엔드가 보낸 실제 사유를 그대로 보여준다.
            setOcrNotice(r.data.recognized === false
              ? (r.data.message || '사업자등록증에서 정보를 인식하지 못했습니다. 직접 입력해주세요.')
              : '📋 사업자등록증에서 자동 인식된 정보로 빈 칸을 채웠습니다. 정확하지 않을 수 있으니 반드시 확인 후 저장해주세요.');
          } else {
            setOcrNotice((r.data && r.data.message) || '사업자등록증 자동인식에 실패했습니다. 직접 입력해주세요.');
          }
        })
        .catch(err => {
          const timedOut = err && (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || ''));
          setOcrNotice(timedOut
            ? '사업자등록증 자동인식 시간이 너무 오래 걸려 중단했습니다(로컬 AI가 혼잡할 수 있습니다). 직접 입력해주세요.'
            : (err.response?.data?.message || '사업자등록증 자동인식 중 오류가 발생했습니다. 직접 입력해주세요.'));
        })
        .finally(() => setOcrLoading(null));
    }

    // 2026-09(사업장정보 모달 신설) 추가 — 임대차계약서 첨부시 OCR로 소재지/토지/건물/
    // 임대할부분/보증금/임대료/계약기간/임대인 정보를 자동 인식해 빈 칸을 채운다. 사업자등록증과
    // 동일한 미리보기(preview)-then-merge 패턴을 그대로 재사용한다.
    if (routeField === 'lease-contract') {
      const ocrFd = new FormData();
      ocrFd.append('file', file);
      setOcrLoading('property');
      setOcrNotice('');
      API.post('/company-info/ocr/lease-contract', ocrFd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 })
        .then(r => {
          const f = r.data?.fields;
          if (r.data?.ok && f) {
            mergeOcrFields({
              lease_address: f.lease_address,
              lease_land_area: f.lease_land_area,
              lease_building_structure: f.lease_building_structure,
              lease_building_usage: f.lease_building_usage,
              lease_building_area: f.lease_building_area,
              lease_leased_portion: f.lease_leased_portion,
              lease_leased_area: f.lease_leased_area,
              lease_deposit: f.lease_deposit,
              lease_monthly_rent: f.lease_monthly_rent,
              lease_manager: f.lease_manager,
              lease_manager_contact: f.lease_manager_contact,
              lease_start_date: f.lease_start_date,
              lease_end_date: f.lease_end_date,
              lease_landlord: f.lease_landlord,
              lease_landlord_contact: f.lease_landlord_contact,
            });
            setOcrNotice('📋 임대차계약서에서 자동 인식된 정보로 빈 칸을 채웠습니다. 정확하지 않을 수 있으니 반드시 확인 후 저장해주세요.');
          } else {
            setOcrNotice((r.data && r.data.message) || '임대차계약서 자동인식에 실패했습니다. 직접 입력해주세요.');
          }
        })
        .catch(err => {
          const timedOut = err && (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || ''));
          setOcrNotice(timedOut
            ? '임대차계약서 자동인식 시간이 너무 오래 걸려 중단했습니다(로컬 AI가 혼잡할 수 있습니다). 직접 입력해주세요.'
            : (err.response?.data?.message || '임대차계약서 자동인식 중 오류가 발생했습니다. 직접 입력해주세요.'));
        })
        .finally(() => setOcrLoading(null));
    }
  };
  const deleteFile = (routeField, formFields) => () => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/company-info/${routeField}`)
      .then(() => {
        setForm(v => { const nv = { ...v }; formFields.forEach(f => { nv[f] = null; }); return nv; });
        if (routeField === 'stamp' && setStampRegistered) setStampRegistered(false);
        if (routeField === 'signature' && setSignatureRegistered) setSignatureRegistered(false);
        showMsg('삭제되었습니다.', 'success');
      })
      .catch(err => showMsg(err.response?.data?.message || '삭제 중 오류가 발생했습니다.', 'error'));
  };

  const uploadBankBook = (file) => {
    if (!file) return;
    setBankUploading(true);
    setOcrLoading('bankbook');

    // 통장사본은 OCR로 은행명/예금주/계좌번호를 먼저 인식한 뒤, 업로드 요청에 함께 실어
    // 보낸다(업체관리와 달리 은행정보가 폼이 아니라 통장 행 단위로 저장되기 때문).
    const ocrFd = new FormData();
    ocrFd.append('file', file);
    API.post('/company-info/ocr/bankbook', ocrFd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => r.data?.ok ? (r.data.fields || {}) : {})
      .catch(() => ({}))
      .finally(() => setOcrLoading(null))
      .then((bankFields) => {
        const fd = new FormData();
        fd.append('file', file);
        if (bankFields.bank_name) fd.append('bank_name', bankFields.bank_name);
        if (bankFields.account_holder) fd.append('account_holder', bankFields.account_holder);
        if (bankFields.account_number) fd.append('account_number', bankFields.account_number);
        return API.post('/company-info/bankbooks', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
          .then(() => {
            loadBankBooks();
            showMsg(
              bankFields.bank_name || bankFields.account_number
                ? '통장사본이 업로드되었습니다. 📋 은행/계좌 정보가 자동 인식되었으니 확인해주세요.'
                : '통장사본이 업로드되었습니다.',
              'success'
            );
          });
      })
      .catch(err => showMsg(err.response?.data?.message || '업로드 중 오류가 발생했습니다.', 'error'))
      .finally(() => setBankUploading(false));
  };
  // 2026-09(거래은행 정보 개편) — 은행명/예금주/계좌번호/비고를 행별로 직접 입력·수정할 수 있도록
  // 범용 필드 업데이트 함수로 통합(기존 비고 전용 updateBankNote/saveBankNote를 대체).
  // 비고(note)는 추후 회계관리 메뉴에서 그대로 참조할 수 있도록 company_bank_books.note 컬럼에
  // 독립된 값으로 저장된다(자유서술 텍스트에 섞이지 않고 행 단위 변수로 관리됨).
  const updateBankField = (id, field, value) => setBankBooks(v => v.map(b => (b.id === id ? { ...b, [field]: value } : b)));
  const saveBankField = (id, field, value) => {
    API.put(`/company-info/bankbooks/${id}`, { [field]: value }).catch(() => showMsg('저장 중 오류가 발생했습니다.', 'error'));
  };
  // 2026-09(회사정보 css 추가수정 2차) 추가 — 요청사항 "주거래은행, 외화통장 등 입력값은
  // 비고에 입력". 기존엔 주거래/외화 여부를 은행명 옆 별도 배지로만 표시했는데(그마저도
  // is_primary/is_foreign이 0/1 정수라 falsy일 때 "0"이 그대로 찍히는 버그가 있었다), 이제는
  // 배지를 없애고 그 값 자체를 비고(note) 텍스트에 "주거래"/"외화통장" 단어로 써넣는다.
  // 콤마(,)로 구분된 태그 목록 앞쪽에 추가/제거하며, 사용자가 직접 적어둔 나머지 비고
  // 내용은 그대로 보존한다.
  const addNoteTag = (note, tag) => {
    const parts = String(note || '').split(',').map(s => s.trim()).filter(Boolean);
    if (!parts.includes(tag)) parts.unshift(tag);
    return parts.join(', ');
  };
  const removeNoteTag = (note, tag) => {
    const parts = String(note || '').split(',').map(s => s.trim()).filter(Boolean);
    return parts.filter(p => p !== tag).join(', ');
  };
  const setPrimaryBankBook = (id) => {
    // 2026-09(주거래 비고 태그 문구 수정) — 요청사항 "주거래, 외화에 체크가 되면 비고에
    // 주거래통장, 외화통장 으로 자동입력되게 수정". 기존엔 주거래 체크시 비고에 "주거래"만
    // 붙었는데(외화 쪽은 이미 "외화통장"으로 붙고 있었음), 두 태그의 표현을 "○○통장" 형태로
    // 통일해달라는 요청이라 태그 문구를 "주거래통장"으로 바꾼다. 주거래는 한 은행만 가능하므로,
    // 지정된 행은 비고에 "주거래통장" 태그를 추가하고 나머지 행에서는 이 태그를 제거한다.
    bankBooks.forEach((b) => {
      // 과거(수정 전) 버전에서 붙었을 수 있는 구(舊) 태그 "주거래"도 함께 제거해, 문구를
      // "주거래통장"으로 바꾼 뒤에도 두 태그가 동시에 남아 비고가 지저분해지는 일이 없게 한다.
      const cleaned = removeNoteTag(b.note, '주거래');
      const nextNote = b.id === id ? addNoteTag(cleaned, '주거래통장') : removeNoteTag(cleaned, '주거래통장');
      if (nextNote !== (b.note || '')) {
        updateBankField(b.id, 'note', nextNote);
        saveBankField(b.id, 'note', nextNote);
      }
    });
    API.patch(`/company-info/bankbooks/${id}/primary`)
      .then(() => { loadBankBooks(); showMsg('주거래 은행으로 지정되었습니다.', 'success'); })
      .catch(() => showMsg('지정 중 오류가 발생했습니다.', 'error'));
  };
  const handleToggleForeign = (id, checked) => {
    const target = bankBooks.find((b) => b.id === id);
    const nextNote = checked ? addNoteTag(target && target.note, '외화통장') : removeNoteTag(target && target.note, '외화통장');
    updateBankField(id, 'note', nextNote);
    saveBankField(id, 'note', nextNote);
    updateBankField(id, 'is_foreign', checked);
    saveBankField(id, 'is_foreign', checked);
  };
  const deleteBankBook = (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/company-info/bankbooks/${id}`)
      .then(() => { loadBankBooks(); showMsg('삭제되었습니다.', 'success'); })
      .catch(() => showMsg('삭제 중 오류가 발생했습니다.', 'error'));
  };
  // "+ 거래은행 추가" — 스캔파일 없이 직접입력만으로 빈 행을 하나 추가한다(백엔드가 파일없는
  // 등록을 허용하도록 수정됨. file_name은 빈 문자열로 저장).
  const addBankRow = () => {
    const fd = new FormData();
    API.post('/company-info/bankbooks', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => loadBankBooks())
      .catch(err => showMsg(err.response?.data?.message || '추가 중 오류가 발생했습니다.', 'error'));
  };

  // 2026-09(등록 모달 4종 개편) — 요청사항: "1. 모달창 우측 상단 닫기버튼 왼쪽에 삭제버튼
  // 위치 / 2. 아래쪽은 등록된 이미지 또는 pdf를 이미지로 보여주고 제일 하단에 첨부파일 등록
  // 화면을 보여줌". 삭제 버튼은 각 모달의 headerExtra로 옮기고(아래 모달 블록 참고), 본문은
  // 기존의 "등록된 파일 보기 OR 업로드" 양자택일 구조를 버리고 항상 미리보기(있으면)+맨 아래
  // 업로드 영역을 동시에 보여주는 구조로 바꾼다(재업로드로 교체 가능).
  const ImageField = ({ label, routeField, formField, value }) => (
    <div style={{ marginBottom: 10 }}>
      {value ? (
        <div style={{ marginBottom: 12, textAlign: 'center' }}>
          <img src={`${API.defaults.baseURL}/../uploads/company_info/${value}`} alt={label}
            style={{ maxWidth: '100%', maxHeight: 320, border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }} />
        </div>
      ) : (
        <div style={{ marginBottom: 12, padding: '18px 0', textAlign: 'center', fontSize: 12.5, color: '#aaa',
          border: '1px dashed #ddd', borderRadius: 8, background: '#fafafa' }}>
          등록된 {label}이(가) 없습니다
        </div>
      )}
      <label style={lbl}>{value ? '새 이미지로 교체' : label}</label>
      <FileDropZone onFile={uploadFile(routeField, formField)} accept="image/*"
        fileName={uploadingField === routeField ? '업로드 중...' : undefined} />
    </div>
  );

  const FileField = ({ label, routeField, formField, origField, value, origValue }) => {
    const url = value ? `${API.defaults.baseURL}/../uploads/company_info/${value}` : null;
    const name = (origValue || value || '').toLowerCase();
    const isPdf = name.endsWith('.pdf');
    const isImage = /\.(png|jpe?g|gif|webp|bmp)$/.test(name);
    // 2026-10 — 요청사항: 드래그앤드롭 영역을 모달 맨 아래로, 파일 표시 영역은 나머지 공간을 모두 채움.
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, minHeight: 0, marginBottom: 12, display: 'flex', flexDirection: 'column' }}>
          {value ? (
            isPdf ? (
              <iframe src={url} title={label}
                style={{ flex: 1, width: '100%', minHeight: 0, border: '1px solid #eee', borderRadius: 8 }} />
            ) : isImage ? (
              <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid #eee', borderRadius: 8, background: '#fafafa', overflow: 'auto' }}>
                <img src={url} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#333', padding: '8px 0' }}>📄 {origValue || value}</div>
            )
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, color: '#aaa',
              border: '1px dashed #ddd', borderRadius: 8, background: '#fafafa' }}>
              등록된 {label}이(가) 없습니다
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0 }}>
          <label style={lbl}>{value ? '새 파일로 교체' : label}</label>
          <FileDropZone onFile={uploadFile(routeField, formField)}
            fileName={uploadingField === routeField ? '업로드 중...' : undefined} />
        </div>
      </div>
    );
  };

  if (!form) return <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 13 }}>불러오는 중...</div>;

  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
        여기에 입력한 정보는 견적서/거래명세서 등 이 시스템에서 발행하는 문서의 공급자(우리 회사)
        정보로 사용됩니다.
      </div>

      {ocrLoading && (
        <div style={{ fontSize: 12, color: '#3a7afe', marginBottom: 10 }}>
          🔎 {ocrLoading === 'cert' ? '사업자등록증' : '통장사본'}에서 정보를 인식하는 중입니다...
        </div>
      )}
      {ocrNotice && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          background: '#FAEEDA', color: '#854F0B', fontSize: 12.5, borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
          <span>{ocrNotice}</span>
          <button type="button" onClick={() => setOcrNotice('')}
            style={{ background: 'none', border: 'none', color: '#854F0B', cursor: 'pointer', fontSize: 14, lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>
      )}

      {/* 2026-09(회사정보 레이아웃 6차 개편) — 요청사항: "회사명, 대표자명, 사업자등록번호,
          법인등록번호, 전화, 팩스, 이메일까지 한줄에 표기". 7등분 그리드(row7) 하나로 통합. */}
      <div style={row8}>
        <div>
          <label style={lbl}>회사명</label>
          <input style={inp} value={form.company_name || ''} onChange={set('company_name')} />
        </div>
        <div>
          <label style={lbl}>대표자명</label>
          <input style={inp} value={form.ceo_name || ''} onChange={set('ceo_name')} />
        </div>
        <div>
          <label style={lbl}>설립일</label>
          <input type="date" style={inp} value={form.establishment_date || ''} onChange={set('establishment_date')} />
        </div>
        <div>
          <label style={lbl}>사업자등록번호</label>
          <input style={inp} value={form.biz_reg_no || ''} onChange={set('biz_reg_no')} />
        </div>
        <div>
          <label style={lbl}>법인등록번호</label>
          <input style={inp} value={form.corp_reg_no || ''} onChange={set('corp_reg_no')} />
        </div>
        <div>
          <label style={lbl}>전화</label>
          <input style={inp} value={form.phone || ''} onChange={set('phone')} />
        </div>
        <div>
          <label style={lbl}>팩스</label>
          <input style={inp} value={form.fax || ''} onChange={set('fax')} />
        </div>
        <div>
          <label style={lbl}>이메일</label>
          <input style={inp} value={form.email || ''} onChange={set('email')} />
        </div>
      </div>
      {/* 자체(시스템) 메일: 시스템메일/외부메일 선택 + 도메인 — 회사정보 저장 버튼과 별개로 자체 저장한다 */}
      {/* 2026-09(회사정보 레이아웃 6차 개편) — 요청사항: "주소를 본점소재지, 사업장소재지로
          구분하여 입력될 수 있도록 입력폼 수정". 기존 address 컬럼은 그대로 사업장소재지로
          쓰고, 본점소재지는 신규 컬럼(hq_address)으로 분리했다. */}
      <div style={row2}>
        <div>
          <label style={lbl}>본점소재지</label>
          <input style={inp} value={form.hq_address || ''} onChange={set('hq_address')} />
        </div>
        <div>
          <label style={lbl}>사업장소재지</label>
          <input style={inp} value={form.address || ''} onChange={set('address')} />
        </div>
      </div>
      {/* 2026-09(회사정보 레이아웃 5차 개편) — 요청사항: "종목 입력칸을 대표자명 입력칸까지
          줄을 맞추는데 업태, 종목 입력칸의 비율을 동일하게 적용". row5가 5등분 그리드이므로
          1열(업태)은 회사명 칸, 2열(종목)은 대표자명 칸과 같은 폭(1fr)으로 맞추고, 나머지
          3칸만큼을 거래은행에 배정해(3fr) 위 5등분 그리드와 열 경계가 일치하도록 했다. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 3fr', gap: 12 }}>
        {/* 2026-09(업태/종목 병합표시 개편) — 요청사항: "업태가 중복되면(같은 업태 아래 종목이
            여러 줄이면) 사업자등록증 실물처럼 업태 칸을 세로로 병합해서 표현". 업태/종목 목록의
            길이가 같을 때(행이 1:1로 정렬된 데이터일 때)만 업태의 연속 중복 값을 하나의 큰
            칸으로 병합해서 보여준다. 길이가 다르면(과거 OCR로 저장되어 업태만 중복 제거되어
            있는 데이터) 병합할 짝을 알 수 없으므로 기존 방식(두 목록을 독립적으로 렌더링)으로
            자동 대체된다. */}
        {(() => {
          const typeArr = toList(form.biz_type);
          const itemArr = toList(form.biz_item);
          const aligned = typeArr.length === itemArr.length && itemArr.length > 0 && itemArr.some(s => s !== '');
          const ROW_H = 36;
          const ROW_GAP = 6;

          if (!aligned) {
            // 업태/종목 항목 수가 서로 다른 경우 — 병합 표시를 위한 행 대응을 알 수 없으므로
            // 두 목록을 독립적으로(기존 방식) 보여준다.
            return (
              <>
                <div>
                  <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6 }}>
                    업태
                    <button type="button" onClick={() => addListItem('biz_type')()}
                      style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#3a7afe', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: 0 }}>+</button>
                  </label>
                  {typeArr.map((v, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <input style={{ ...inp, flex: 1, marginBottom: 0 }} value={v} onChange={(e) => setListField('biz_type')(i, e.target.value)} />
                      <button type="button" onClick={() => removeListItem('biz_type')(i)}
                        style={{ width: 28, height: 28, flexShrink: 0, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
                <div>
                  <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6 }}>
                    종목
                    <button type="button" onClick={() => addListItem('biz_item')()}
                      style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#3a7afe', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: 0 }}>+</button>
                  </label>
                  {itemArr.map((v, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <input style={{ ...inp, flex: 1, marginBottom: 0 }} value={v} onChange={(e) => setListField('biz_item')(i, e.target.value)} />
                      <button type="button" onClick={() => removeListItem('biz_item')(i)}
                        style={{ width: 28, height: 28, flexShrink: 0, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
              </>
            );
          }

          // 행이 정렬된 데이터 — 업태의 연속 중복 값을 그룹으로 묶어 하나의 큰 칸으로 병합한다.
          const groups = [];
          for (let i = 0; i < typeArr.length; i++) {
            if (groups.length && groups[groups.length - 1].value === typeArr[i]) groups[groups.length - 1].count += 1;
            else groups.push({ value: typeArr[i], start: i, count: 1 });
          }
          return (
            <>
              <div>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6 }}>
                  업태
                  <button type="button" onClick={addAlignedRow}
                    style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#3a7afe', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: 0 }}>+</button>
                </label>
                {groups.map((g, gi) => (
                  // 2026-09(업태 병합 정렬 버그수정) — 요청사항: "제조업은 기계제조와 같은
                  // 줄에, 도매및소매업은 기계부품및소모품과 같은 줄에 위치시켜달라". 실제
                  // 사업자등록증도 업태값이 병합된 칸의 맨 윗줄(=그 업태의 첫 번째 종목 행)에
                  // 표기된다. 그런데 기존 코드는 이 병합 칸(그룹 전체 높이)에 alignItems:
                  // 'center'를 줘서, 작은 입력창(높이 ROW_H)이 칸 한가운데로 내려와 버려 첫
                  // 번째 종목 행과 어긋나 보였다. alignItems를 'flex-start'로 바꿔 입력창을
                  // 병합 칸의 맨 위(=그룹의 첫 행)에 고정한다.
                  <div key={gi} style={{ display: 'flex', alignItems: 'flex-start', gap: 6,
                    height: g.count * ROW_H + (g.count - 1) * ROW_GAP, marginBottom: ROW_GAP }}>
                    <input style={{ ...inp, flex: 1, marginBottom: 0, height: ROW_H }} value={g.value}
                      onChange={(e) => setListRange('biz_type')(g.start, g.count, e.target.value)} />
                    <button type="button" onClick={() => removeAlignedRows(g.start, g.count)}
                      style={{ width: 28, height: 28, flexShrink: 0, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
              <div>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6 }}>
                  종목
                  <button type="button" onClick={addAlignedRow}
                    style={{ width: 16, height: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#3a7afe', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: 0 }}>+</button>
                </label>
                {itemArr.map((v, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, height: ROW_H, marginBottom: ROW_GAP }}>
                    <input style={{ ...inp, flex: 1, marginBottom: 0, height: ROW_H }} value={v} onChange={(e) => setListField('biz_item')(i, e.target.value)} />
                    <button type="button" onClick={() => removeAlignedRows(i, 1)}
                      style={{ width: 28, height: 28, flexShrink: 0, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            </>
          );
        })()}
        <div>
          <label style={lbl}>거래은행</label>
          {bankBooks.length === 0 && (
            <button type="button" onClick={addBankRow}
              style={{ width: '100%', padding: '8px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#3a7afe', border: '1px solid #3a7afe', borderRadius: 8, cursor: 'pointer' }}>
              + 거래은행 추가
            </button>
          )}
          {/* 2026-09(회사정보 css 최종수정) 변경 — 요청사항 "주거래, 추가, 삭제, 첨부됨 버튼을
              삭제"(메인 화면의 이 인라인 목록에서만 — 전체 관리(추가/삭제/주거래 지정)는 우측상단
              "통장사본 등록" 모달로 일원화했다). 여기서는 읽기 위주의 간단한 표시만 남기고, 색상만
              은행 상태를 알려준다: 주거래는 기존 파란색 유지, 외화통장은 파스텔 오렌지로 구분.
              2026-09(회사정보 css 추가수정 2차) 변경 — 요청사항 "하나은행 글자앞에 0과
              외화통장, 0, O 등을 모두 삭제" / "주거래은행·외화통장 등 입력값은 비고에 입력".
              기존엔 은행명 앞에 {b.is_foreign && <span>외화</span>}, 비고 옆에
              {b.is_primary && <span>O</span>} 식으로 배지를 따로 띄웠는데, b.is_foreign/
              b.is_primary가 SQLite에서 boolean이 아니라 0/1 정수로 내려오다 보니 조건이
              거짓(0)일 때 `0 && <span>`이 그대로 0으로 평가되어 React가 그 "0"을 화면에
              그대로 찍어버리는 문제가 있었다(리액트에서 falsy 값 중 0만 렌더링되는 특성).
              이번 수정으로 배지/오버레이를 아예 없애고, 주거래·외화통장 여부는 아래
              통장사본 등록 모달의 체크박스를 켤 때 "주거래"/"외화통장" 텍스트를 비고
              입력값 자체에 자동으로 추가/제거하는 방식으로 옮겨(handleSetPrimary/
              handleToggleForeign), 비고 칸 하나만 보면 상태를 알 수 있게 한다.
              2026-09(통장사본 비고 자유입력 버그) 수정 — 요청사항 "비고란은 통장사본 등록
              모달창에서 자유롭게 수정 가능하도록". 비고 입력창을 defaultValue+key(`note-${
              b.note}`) 방식으로 만들어뒀는데, 태그 토글 때문에 넣은 이 key가 note 값을 그대로
              물고 있다 보니 사용자가 타이핑해서 onChange로 note가 바뀔 때마다 key도 함께
              바뀌어 React가 입력창을 통째로 다시 마운트했다 — 그 결과 한 글자 입력할 때마다
              포커스가 날아가서 사실상 한 글자 이상 이어 칠 수 없었다. key 리마운트 방식을
              버리고 value={b.note}인 완전한 controlled input으로 바꿔, 타이핑도 자유롭고
              체크박스로 태그를 넣을 때도(controlled라 value가 바뀌면 그대로 반영) 입력창이
              깨지지 않게 했다. */}
          {bankBooks.map((b) => (
            <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', marginBottom: 6,
              background: b.is_primary ? '#eef5ff' : (b.is_foreign ? '#fff2e2' : '#fafafa'),
              border: b.is_primary ? '1px solid #3a7afe' : (b.is_foreign ? '1px solid #ffb366' : '1px solid #eee'), borderRadius: 8 }}>
              <input style={{ ...inp, flex: 1, marginBottom: 0, padding: '6px 10px', fontSize: 13 }}
                defaultValue={b.bank_name || ''} placeholder="거래은행 (예: 국민은행)"
                onChange={(e) => updateBankField(b.id, 'bank_name', e.target.value)}
                onBlur={(e) => saveBankField(b.id, 'bank_name', e.target.value)} />
              <input style={{ ...inp, flex: 1, marginBottom: 0, padding: '6px 10px', fontSize: 13 }}
                defaultValue={b.account_holder || ''} placeholder="예금주"
                onChange={(e) => updateBankField(b.id, 'account_holder', e.target.value)}
                onBlur={(e) => saveBankField(b.id, 'account_holder', e.target.value)} />
              <input style={{ ...inp, flex: 1.3, marginBottom: 0, padding: '6px 10px', fontSize: 13 }}
                defaultValue={b.account_number || ''} placeholder="계좌번호"
                onChange={(e) => updateBankField(b.id, 'account_number', e.target.value)}
                onBlur={(e) => saveBankField(b.id, 'account_number', e.target.value)} />
              <input
                style={{ ...inp, flex: 1, marginBottom: 0, padding: '6px 10px', fontSize: 13 }}
                value={b.note || ''} placeholder="비고"
                onChange={(e) => updateBankField(b.id, 'note', e.target.value)}
                onBlur={(e) => saveBankField(b.id, 'note', e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      {/* 2026-09(회사정보 레이아웃 3차 개편) — 요청사항: "우측상단 통장사본, 사업자등록증
          버튼을 별도로 만들어서 등록할 수 있도록 모달창을 구성". 기존엔 두 파일을 한 모달에
          같이 두었으나, 이번엔 완전히 분리된 버튼+모달 2세트로 나눈다. 업로드 로직(uploadFile/
          uploadBankBook)과 OCR 자동입력(mergeOcrFields 등)은 그대로 두고, 모달 표시 여부만
          상위(Settings) 헤더의 버튼 2개가 각각 제어한다(showCertUpload/showBankUpload). */}
      {showCertUpload && (
        // 2026-09(사업자등록증 모달 세로높이 조정) 추가 — 요청사항 "사업자등록증 모달창 세로높이:
        // 기존 577.6px -> 860px로 수정".
        <Modal title="사업자등록증 등록" onClose={() => setShowCertUpload(false)} width={BANK_MODAL_WIDTH} height={860} fill
          headerExtra={form.biz_cert_file && (
            <>
              {/* 2026-09(회사정보 css 최종수정) 추가 — 요청사항 "삭제버튼 왼쪽에 다운로드
                  버튼을 추가". uploads/company_info는 쿠키 기반 인증(requireUploadAuth)이라
                  별도 토큰 없이 그냥 <a download>로 받을 수 있다. */}
              <a href={`${API.defaults.baseURL}/../uploads/company_info/${form.biz_cert_file}`}
                download={form.biz_cert_orig_name || form.biz_cert_file}
                style={{ background: '#fff', color: '#3a7afe', border: '1px solid #3a7afe', borderRadius: 6, padding: '5px 10px', fontSize: 12, textDecoration: 'none' }}>다운로드</a>
              <button onClick={deleteFile('biz-cert', ['biz_cert_file', 'biz_cert_orig_name'])}
                style={headerDeleteBtn}>삭제</button>
            </>
          )}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            등록하면 OCR이 자동으로 인식하여 회사명·대표자명·사업자등록번호·주소·업태·종목의
            빈 칸을 채워줍니다(이미 입력된 값은 덮어쓰지 않습니다).
          </div>
          <FileField label="사업자등록증" routeField="biz-cert" formField="biz_cert_file" origField="biz_cert_orig_name"
            value={form.biz_cert_file} origValue={form.biz_cert_orig_name} />
        </Modal>
      )}
      {showBankUpload && (
        <Modal title="통장사본 등록" onClose={() => setShowBankUpload(false)} width={BANK_MODAL_WIDTH}
          headerExtra={bankBooks.some(b => b.file_name) && (
            <div style={{ position: 'relative' }}>
              {/* 2026-09(회사정보 css 최종수정) 추가 — 요청사항 3-2 "우측상단 닫기버튼 옆에
                  사본 다운로드 버튼을 추가. 통장이 여러개인 경우 계좌번호, 비고를 표기하여
                  다운로드 할 목록을 선택하여 다운". 파일이 1개면 바로 받고, 여러개면 아래
                  체크리스트를 펼쳐 계좌번호/비고 기준으로 선택 다운로드한다. */}
              <button type="button" onClick={handleBankDownloadClick}
                style={{ background: '#fff', color: '#3a7afe', border: '1px solid #3a7afe', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>다운로드</button>
              {bankDownloadOpen && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, background: '#fff',
                  border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 10, width: 240, zIndex: 10 }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>다운로드할 통장사본 선택</div>
                  {bankBooks.filter(b => b.file_name).map(b => (
                    <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '4px 0', cursor: 'pointer' }}>
                      <input type="checkbox" checked={bankDownloadSel.includes(b.id)}
                        onChange={(e) => setBankDownloadSel(v => e.target.checked ? [...v, b.id] : v.filter(id => id !== b.id))} />
                      {(b.account_number || '계좌번호 미입력')}{b.note ? ` (${b.note})` : ''}
                    </label>
                  ))}
                  <button type="button" onClick={downloadSelectedBankBooks} disabled={bankDownloadSel.length === 0}
                    style={{ width: '100%', marginTop: 8, padding: '6px 0', fontSize: 12.5, fontWeight: 600,
                      background: bankDownloadSel.length ? '#3a7afe' : '#ccc', color: '#fff', border: 'none', borderRadius: 6,
                      cursor: bankDownloadSel.length ? 'pointer' : 'not-allowed' }}>선택 다운로드 ({bankDownloadSel.length})</button>
                </div>
              )}
            </div>
          )}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            등록하면 OCR이 은행명·예금주·계좌번호를 자동 인식하여 아래 목록에 새 항목으로
            추가합니다. 통장사본은 2개 이상 등록할 수 있으며, 위 회사정보 화면의 거래은행
            목록과 동일하게 관리됩니다.
          </div>
          {bankBooks.length === 0 && (
            <button type="button" onClick={addBankRow}
              style={{ width: '100%', padding: '10px 14px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#3a7afe', border: '1px solid #3a7afe', borderRadius: 8, cursor: 'pointer', marginBottom: 12 }}>
              + 거래은행 추가
            </button>
          )}
          {bankBooks.map((b) => (
            <div key={b.id} style={{ padding: '10px 12px', marginBottom: 8,
              background: b.is_primary ? '#eef5ff' : (b.is_foreign ? '#fff2e2' : '#fafafa'),
              border: b.is_primary ? '1px solid #3a7afe' : (b.is_foreign ? '1px solid #ffb366' : '1px solid #eee'), borderRadius: 8 }}>
              {/* 2026-09(통장사본 등록 모달 미리보기 삭제) 수정 — 요청사항 "통장사본 등록
                  모달창에서 미리보기 기능 삭제". 기존엔 행마다 b.file_name이 있으면 iframe(pdf)
                  또는 img(이미지)로 통장사본 원본을 통째로 미리보여줬는데, 모달 안에 은행 행이
                  여러 개 쌓이면 그만큼 큰 미리보기가 줄줄이 늘어나 모달이 지나치게 길어지고
                  스크롤이 늘어나는 문제가 있었다. 요청에 따라 이 미리보기 블록 자체를 제거하고,
                  아래 은행명/예금주/계좌번호/비고 입력 행만 남긴다(파일 자체는 여전히 서버에
                  저장되어 있고, 필요하면 새 파일로 교체(재업로드)는 계속 가능하다). */}
              {/* 2026-09(회사정보 css 최종수정) 변경 — 요청사항 3-1 "은행명, 예금주, 계좌번호,
                  비고, 주거래, 삭제를 한줄에 표기"(모달 폭을 BANK_MODAL_WIDTH로 늘려 한 줄에
                  다 들어가게 했다). 외화통장 지정 체크박스도 여기(전체 관리가 모이는 곳)에 둔다. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input style={{ ...inp, flex: 1.1, marginBottom: 0, padding: '6px 10px', fontSize: 13 }}
                  defaultValue={b.bank_name || ''} placeholder="거래은행 (예: 국민은행)"
                  onChange={(e) => updateBankField(b.id, 'bank_name', e.target.value)}
                  onBlur={(e) => saveBankField(b.id, 'bank_name', e.target.value)} />
                <input style={{ ...inp, flex: 1, marginBottom: 0, padding: '6px 10px', fontSize: 13 }}
                  defaultValue={b.account_holder || ''} placeholder="예금주"
                  onChange={(e) => updateBankField(b.id, 'account_holder', e.target.value)}
                  onBlur={(e) => saveBankField(b.id, 'account_holder', e.target.value)} />
                <input style={{ ...inp, flex: 1.3, marginBottom: 0, padding: '6px 10px', fontSize: 13 }}
                  defaultValue={b.account_number || ''} placeholder="계좌번호"
                  onChange={(e) => updateBankField(b.id, 'account_number', e.target.value)}
                  onBlur={(e) => saveBankField(b.id, 'account_number', e.target.value)} />
                <input
                  style={{ ...inp, flex: 1, marginBottom: 0, padding: '6px 10px', fontSize: 13 }}
                  value={b.note || ''} placeholder="비고"
                  onChange={(e) => updateBankField(b.id, 'note', e.target.value)}
                  onBlur={(e) => saveBankField(b.id, 'note', e.target.value)} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, fontSize: 12, color: '#555', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!b.is_primary} onChange={() => setPrimaryBankBook(b.id)} />
                  주거래
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, fontSize: 12, color: '#c97a2b', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!b.is_foreign}
                    onChange={(e) => handleToggleForeign(b.id, e.target.checked)} />
                  외화
                </label>
                <button type="button" onClick={() => deleteBankBook(b.id)}
                  style={{ flexShrink: 0, padding: '6px 10px', fontSize: 12, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer' }}>삭제</button>
                {/* 2026-09(통장사본 모달 행별 다운로드 버튼 추가) — 요청사항 "삭제버튼 우측에 다운로드
                    버튼을 각각 추가". 파일이 등록된 행에만 표시. */}
                {b.file_name && (
                  <button type="button" onClick={() => triggerFileDownload(b.file_name, b.orig_name || b.file_name)}
                    style={{ flexShrink: 0, padding: '6px 10px', fontSize: 12, background: '#fff', color: '#2f6fed', border: '1px solid #2f6fed', borderRadius: 6, cursor: 'pointer' }}>다운로드</button>
                )}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 14 }}>
            <label style={lbl}>새 통장사본 추가 (OCR 자동인식)</label>
            <FileDropZone onFile={uploadBankBook} accept="image/*,.pdf" fileName={bankUploading ? '업로드 중...' : undefined} />
          </div>
        </Modal>
      )}
      {showStampUpload && (
        <Modal title="도장(직인) 등록" onClose={() => setShowStampUpload(false)} width={520}
          headerExtra={form.stamp_image && (
            <button onClick={deleteFile('stamp', ['stamp_image'])} style={headerDeleteBtn}>삭제</button>
          )}>
          <ImageField label="도장(직인) 이미지" routeField="stamp" formField="stamp_image" value={form.stamp_image} />
        </Modal>
      )}
      {showSignatureUpload && (
        <Modal title="대표자 서명 등록" onClose={() => setShowSignatureUpload(false)} width={520}
          headerExtra={form.signature_image && (
            <button onClick={deleteFile('signature', ['signature_image'])} style={headerDeleteBtn}>삭제</button>
          )}>
          <ImageField label="대표자 서명 이미지" routeField="signature" formField="signature_image" value={form.signature_image} />
        </Modal>
      )}

      {/* 2026-09(사업장정보 모달 신설) 추가 — 요청사항: "통장사본 등록 버튼 왼쪽에 사업장정보
          버튼 추가 / 사업장정보 버튼을 누르면 자가, 임대 버튼을 우측 상단 닫기버튼 왼쪽에 위치 /
          자가인 경우 등기부등본을 첨부하면 정보를 입력 / 임대인 경우 임대차계약서를 등록".
          기존에 페이지 본문에 항상 펼쳐져 있던 "사업장 정보" 섹션(자가/임대 체크박스 + 관련
          입력란)을 이 모달로 이전한다(통장사본/사업자등록증과 동일한 모달 패턴). 자가/임대
          토글은 headerExtra로 닫기(✕) 버튼 왼쪽에 배치한다. */}
      {showPropertyUpload && (
        <Modal title="사업장정보" onClose={() => setShowPropertyUpload(false)} width={BANK_MODAL_WIDTH}>
          {/* 2026-09(사업장정보 모달 개편 2차) — 요청사항 "상단 자가/임대 체크박스 삭제, 그 구분은
              본점 카드의 구분(select)에서 지사/공장 항목과 같은 형태로 표시 및 적용" 등에 따라
              모달 헤더의 자가/임대 체크박스 토글을 제거하고, 아래 본점 카드 안에 추가 사업장과
              동일한 형태(구분 select + 자가/임대 select)로 옮겼다. */}
          {ocrLoading === 'property' && (
            <div style={{ fontSize: 12, color: '#3a7afe', marginBottom: 10 }}>
              🔎 임대차계약서에서 정보를 인식하는 중입니다...
            </div>
          )}
          {ocrNotice && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              background: '#FAEEDA', color: '#854F0B', fontSize: 12.5, borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
              <span>{ocrNotice}</span>
              <button type="button" onClick={() => setOcrNotice('')}
                style={{ background: 'none', border: 'none', color: '#854F0B', cursor: 'pointer', fontSize: 14, lineHeight: 1, flexShrink: 0 }}>×</button>
            </div>
          )}
          {(() => {
            const isOwn = (form.lease_type || 'lease') === 'own';
            const label = isOwn ? '등기부등본' : '임대차계약서';
            const routeField = isOwn ? 'registry-cert' : 'lease-contract';
            const formField = isOwn ? 'registry_cert_file' : 'lease_contract_file';
            const origField = isOwn ? 'registry_cert_orig_name' : 'lease_contract_orig_name';
            const fileName = form[formField];
            const origName = form[origField];
            return (
              <>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                  {isOwn
                    ? '자가 소유 사업장입니다. 등기부등본을 첨부해 보관할 수 있습니다.'
                    : '임대차계약서를 등록하면 OCR이 소재지·토지·건물·임대할부분·보증금·임대료·계약기간·임대인 정보를 자동 인식하여, 아래 "환경설정 - 회사정보" 화면의 사업장 정보란에 빈 칸을 채워줍니다(이미 입력된 값은 덮어쓰지 않습니다). 인식 결과는 모달을 닫은 뒤 회사정보 화면에서 확인·수정할 수 있습니다.'}
                </div>
                {/* 2026-09(사업장정보 모달 개편 2차) — 요청사항 1,2,3,4 반영:
                    1) 파일명 위에 "첨부된 ○○" 라벨 추가
                    2) 구분(본점 고정)+자가/임대 select를 추가 사업장 행과 같은 형태로 표시
                    3) 상단 헤더의 자가/임대 체크박스는 삭제(위에서 제거) — 이 select가 그 대체
                    4) "새 ○○로 교체" 드롭존을 파일 행과 같은 카드 안, 오른쪽에 한 줄로 배치 */}
                <div style={{ padding: '10px 12px', marginBottom: 10, background: '#fafafa', border: '1px solid #eee', borderRadius: 8 }}>
                  <label style={lbl}>첨부된 {label}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    <select style={{ ...inp, width: 90, flexShrink: 0, marginBottom: 0 }} value="본점" disabled>
                      <option value="본점">본점</option>
                    </select>
                    <select style={{ ...inp, width: 80, flexShrink: 0, marginBottom: 0 }} value={form.lease_type || 'lease'}
                      onChange={(e) => setForm(v => ({ ...v, lease_type: e.target.value }))}>
                      <option value="lease">임대</option>
                      <option value="own">자가</option>
                    </select>
                    <div style={{ flex: 1, fontSize: 13, color: fileName ? '#333' : '#aaa', minWidth: 120 }}>
                      {fileName ? `📄 ${origName || fileName}` : `등록된 ${label}이(가) 없습니다`}
                    </div>
                    {fileName && (
                      <>
                        <button type="button" onClick={() => triggerFileDownload(fileName, origName || fileName)}
                          style={{ flexShrink: 0, padding: '6px 10px', fontSize: 12, background: '#fff', color: '#3a7afe', border: '1px solid #3a7afe', borderRadius: 6, cursor: 'pointer' }}>다운로드</button>
                        <button type="button" onClick={deleteFile(routeField, [formField, origField])}
                          style={{ flexShrink: 0, padding: '6px 10px', fontSize: 12, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer' }}>삭제</button>
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: '#555', fontWeight: 500, flexShrink: 0 }}>
                      {fileName ? `새 ${label}로 교체 (OCR 자동인식)` : `${label} 등록 (OCR 자동인식)`}
                    </span>
                    <FileDropZone onFile={uploadFile(routeField, formField)}
                      fileName={uploadingField === routeField ? '업로드 중...' : undefined}
                      style={{ flex: 1, marginBottom: 0 }} />
                  </div>
                </div>
              </>
            );
          })()}

          {/* 2026-09(추가 사업장 등록을 사업장정보 모달로 이관) — 요청사항 "사업장추가 버튼은
              삭제하고 사업장정보에서 사업장을 추가할 수 있도록 수정(모달창의 형태는 통장사본을
              추가하는 형태로 구성)". 본점(위 카드)과 별개로 지사/공장을 여러 건 등록하는 목록을
              통장사본 모달과 동일한 카드 리스트 + 하단 드롭존 패턴으로 이 모달 안에 둔다. 카드마다
              구분(지사/공장)·자가/임대를 바로 고를 수 있고, 드롭존에 파일을 올리면 그 즉시 새
              사업장 1건이 생성된다. */}
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #eee' }}>
            <label style={lbl}>추가 사업장(지사/공장)</label>
            {bizLocations.map(loc => {
              const locIsOwn = loc.lease_type === 'own';
              const locIsSublease = loc.lease_type === 'sublease';
              const locFileName = locIsOwn ? loc.registry_cert_file : (locIsSublease ? loc.sublease_consent_file : loc.lease_contract_file);
              const locOrigName = locIsOwn ? loc.registry_cert_orig_name : (locIsSublease ? loc.sublease_consent_orig_name : loc.lease_contract_orig_name);
              const locRouteField = locIsOwn ? 'registry-cert' : (locIsSublease ? 'sublease-consent' : 'lease-contract');
              const locDocLabel = locIsOwn ? '등기부등본' : (locIsSublease ? '전대사용동의서' : '임대차계약서');
              return (
                <div key={loc.id} style={{ padding: '10px 12px', marginBottom: 8, background: '#fafafa', border: '1px solid #eee', borderRadius: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <select style={{ ...inp, width: 90, flexShrink: 0 }} value={loc.location_type || '지사'}
                      onChange={(e) => { const val = e.target.value; updateBizLoc(loc.id, 'location_type', val); API.put(`/company-info/biz-locations/${loc.id}`, { location_type: val, ...(val !== '공장' ? { factory_no: '' } : {}) }).then(loadBizLocations).catch(() => {}); }}>
                      <option value="지사">지사</option>
                      <option value="공장">공장</option>
                    </select>
                    {loc.location_type === '공장' && (
                      <input style={{ ...inp, width: 90, flexShrink: 0 }} placeholder="예: 1공장" value={loc.factory_no || ''}
                        onChange={(e) => updateBizLoc(loc.id, 'factory_no', e.target.value)} onBlur={saveBizLocField(loc.id, 'factory_no')} />
                    )}
                    <select style={{ ...inp, width: 80, flexShrink: 0 }} value={loc.lease_type || 'lease'}
                      onChange={(e) => { const val = e.target.value; updateBizLoc(loc.id, 'lease_type', val); API.put(`/company-info/biz-locations/${loc.id}`, { lease_type: val }).catch(() => {}); }}>
                      <option value="lease">임대</option>
                      <option value="own">자가</option>
                      {/* 2026-09(추가 사업장 OCR 자동입력) 추가 — "임대차계약서 및 전대차 계약서도
                          ocr로 읽어서 자동으로 입력되게" 요청에 따른 전대차 옵션. */}
                      <option value="sublease">전대차</option>
                    </select>
                    <div style={{ flex: 1, fontSize: 13, color: locFileName ? '#333' : '#aaa', minWidth: 120 }}>
                      {locFileName ? `📄 ${locOrigName || locFileName}` : `등록된 ${locDocLabel}이(가) 없습니다`}
                    </div>
                    {locFileName && (
                      <>
                        <button type="button" onClick={() => triggerFileDownload(locFileName, locOrigName || locFileName)}
                          style={{ flexShrink: 0, padding: '6px 10px', fontSize: 12, background: '#fff', color: '#3a7afe', border: '1px solid #3a7afe', borderRadius: 6, cursor: 'pointer' }}>다운로드</button>
                        {/* 2026-09(사업장정보 모달 개편 2차) — 요청사항 5 "파일삭제는 '삭제'로 수정,
                            사업장삭제 버튼은 삭제". 사업장 전체를 지우는 별도 버튼은 없애고, 파일
                            삭제 버튼 라벨만 다른 카드들과 동일하게 "삭제"로 통일했다. */}
                        <button type="button" onClick={deleteBizLocFile(loc.id, locRouteField)}
                          style={{ flexShrink: 0, padding: '6px 10px', fontSize: 12, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer' }}>삭제</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            {/* 2026-09(사업장정보 모달 개편 2차) — 요청사항 6 "제일 아래쪽 지사, 임대 추가하는
                부분에도 직접입력 칸을 넣고 한줄에 표기". 구분이 "공장"일 때 쓸 공장번호 직접입력
                칸(예: 1공장)을 select들 옆에 추가하고, 안내문구·드롭존까지 전부 한 줄에 배치했다. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <select style={{ ...inp, width: 90, marginBottom: 0 }} value={bizLocNewType} onChange={(e) => setBizLocNewType(e.target.value)}>
                <option value="지사">지사</option>
                <option value="공장">공장</option>
              </select>
              {bizLocNewType === '공장' && (
                <input style={{ ...inp, width: 90, marginBottom: 0 }} placeholder="예: 1공장" value={bizLocNewFactoryNo}
                  onChange={(e) => setBizLocNewFactoryNo(e.target.value)} />
              )}
              <select style={{ ...inp, width: 80, marginBottom: 0 }} value={bizLocNewLeaseType} onChange={(e) => setBizLocNewLeaseType(e.target.value)}>
                <option value="lease">임대</option>
                <option value="own">자가</option>
                <option value="sublease">전대차</option>
              </select>
              <FileDropZone onFile={uploadNewBizLocation} fileName={bizLocUploading ? '등록 중... (OCR 자동인식 포함, 다소 시간이 걸릴 수 있습니다)' : undefined}
                style={{ flex: 1, minWidth: 200, marginBottom: 0 }} />
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>구분/자가·임대·전대차 선택 후 문서를 등록하면 새 사업장이 추가됩니다(임대차계약서·전대사용동의서는 OCR로 자동 인식됩니다).</div>
            {bizLocOcrNotice && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#3a7afe' }}>{bizLocOcrNotice}</div>
            )}
          </div>
        </Modal>
      )}

      {/* 2026-09(사업장정보 한줄 표기 개편) 추가 — 요청사항 "상세는 버튼으로 구성하고 상세를
          누르면 해당 임대차계약서 또는 등기부등본을 미리보고, 상세 정보를 볼 수 있도록 구성".
          문서 미리보기(이미지는 <img>, PDF 등은 <iframe>)와 한줄 표에서 빠진 나머지 세부항목을
          모두 이 모달 하나에 모은다. */}
      {showPropertyDetail && (
        // 2026-09(사업장정보 상세 모달 헤더 삭제버튼 추가) — 요청사항 "우측 닫기버튼 왼쪽에
        // 삭제버튼 추가". 현재 구분(자가/임대)에 해당하는 첨부파일(등기부등본/임대차계약서)이
        // 있을 때만 표시하고, 클릭 시 기존 deleteFile 패턴으로 파일을 삭제한다.
        <Modal title="사업장정보 상세" onClose={() => setShowPropertyDetail(false)} width={BANK_MODAL_WIDTH}
          headerExtra={(() => {
            const isOwn = (form.lease_type || 'lease') === 'own';
            const routeField = isOwn ? 'registry-cert' : 'lease-contract';
            const formField = isOwn ? 'registry_cert_file' : 'lease_contract_file';
            const origField = isOwn ? 'registry_cert_orig_name' : 'lease_contract_orig_name';
            return form[formField] && (
              <button type="button" onClick={deleteFile(routeField, [formField, origField])} style={headerDeleteBtn}>삭제</button>
            );
          })()}
        >
          {(() => {
            const isOwn = (form.lease_type || 'lease') === 'own';
            const fileName = isOwn ? form.registry_cert_file : form.lease_contract_file;
            if (!fileName) {
              return (
                <div style={{ fontSize: 12.5, color: '#888', background: '#f8fafc', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                  첨부된 {isOwn ? '등기부등본' : '임대차계약서'}가 없습니다. 상단 "🏢 사업장정보" 버튼에서 먼저 첨부해 주세요.
                </div>
              );
            }
            const fileUrl = `${API.defaults.baseURL}/../uploads/company_info/${fileName}`;
            const isImage = /\.(png|jpe?g|gif|bmp|webp)$/i.test(fileName);
            return (
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>{isOwn ? '등기부등본' : '임대차계약서'} 미리보기</label>
                {isImage ? (
                  <img src={fileUrl} alt="문서 미리보기" style={{ width: '100%', maxHeight: 420, objectFit: 'contain', border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }} />
                ) : (
                  <iframe src={fileUrl} title="문서 미리보기" style={{ width: '100%', height: 420, border: '1px solid #eee', borderRadius: 8 }} />
                )}
              </div>
            );
          })()}

          {/* 2026-09(사업장정보 상세 2행 재구성) — 요청사항 "임대부분, 토지면적, 건물구조,
              임대면적, 임대인, 임대인연락처 한줄에 표기 / 관리인, 관리인연락처, 임대료 납부계좌,
              예금주, 계좌번호, 비고 한줄에 표기 / 2,3번은 세로줄을 맞춰서 표기". 기존 row4(4칸)+
              row6(6칸) 조합을 동일한 row6(6칸) 그리드 2줄로 바꿔 두 행의 세로 열이 정확히 맞도록
              한다. */}
          <div style={row6}>
            <div>
              <label style={lbl}>임대부분</label>
              <input style={inp} value={form.lease_leased_portion || ''} onChange={set('lease_leased_portion')} />
            </div>
            <div>
              <label style={lbl}>토지 면적</label>
              <div style={unitWrap}>
                <input style={unitInp} value={form.lease_land_area || ''} onChange={set('lease_land_area')} />
                <span style={unitSuffix}>㎡</span>
              </div>
            </div>
            <div>
              <label style={lbl}>건물 구조</label>
              <input style={inp} value={form.lease_building_structure || ''} onChange={set('lease_building_structure')} />
            </div>
            <div>
              <label style={lbl}>임대면적</label>
              <div style={unitWrap}>
                <input style={unitInp} value={form.lease_leased_area || ''} onChange={set('lease_leased_area')} />
                <span style={unitSuffix}>㎡</span>
              </div>
            </div>
            <div>
              <label style={lbl}>임대인</label>
              <input style={inp} value={form.lease_landlord || ''} onChange={set('lease_landlord')} />
            </div>
            <div>
              <label style={lbl}>임대인 연락처</label>
              <input style={inp} value={form.lease_landlord_contact || ''} onChange={set('lease_landlord_contact')} />
            </div>
          </div>
          <div style={row6}>
            <div>
              <label style={lbl}>관리인</label>
              <input style={inp} value={form.lease_manager || ''} onChange={set('lease_manager')} />
            </div>
            <div>
              <label style={lbl}>관리인 연락처</label>
              <input style={inp} value={form.lease_manager_contact || ''} onChange={set('lease_manager_contact')} />
            </div>
            <div>
              <label style={lbl}>임대료 납부계좌</label>
              <input style={inp} value={form.lease_rent_bank || ''} onChange={set('lease_rent_bank')} placeholder="은행명" />
            </div>
            <div>
              <label style={lbl}>예금주</label>
              <input style={inp} value={form.lease_rent_account_holder || ''} onChange={set('lease_rent_account_holder')} placeholder="예금주" />
            </div>
            <div>
              <label style={lbl}>계좌번호</label>
              <input style={inp} value={form.lease_rent_account_number || ''} onChange={set('lease_rent_account_number')} placeholder="계좌번호" />
            </div>
            <div>
              <label style={lbl}>비고</label>
              <input style={inp} value={form.lease_remark || ''} onChange={set('lease_remark')} />
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: '#999', marginTop: 4 }}>
            이 모달의 입력값도 "환경설정 - 회사정보" 저장 버튼을 눌러야 실제로 반영됩니다.
          </div>
        </Modal>
      )}

      {/* 2026-09(추가 사업장 기능) 추가 — 목록의 "상세" 버튼으로 여는 모달. 기존 본점
          사업장정보 상세 모달(showPropertyDetail)과 동일한 미리보기+row6×2 레이아웃을
          해당 사업장(loc) 데이터로 렌더링한다. */}
      {showBizLocDetailId != null && (() => {
        const loc = bizLocations.find(l => l.id === showBizLocDetailId);
        if (!loc) return null;
        const isOwn = loc.lease_type === 'own';
        const isSublease = loc.lease_type === 'sublease';
        const routeField = isOwn ? 'registry-cert' : (isSublease ? 'sublease-consent' : 'lease-contract');
        const fileName = isOwn ? loc.registry_cert_file : (isSublease ? loc.sublease_consent_file : loc.lease_contract_file);
        const origName = isOwn ? loc.registry_cert_orig_name : (isSublease ? loc.sublease_consent_orig_name : loc.lease_contract_orig_name);
        const docLabel = isOwn ? '등기부등본' : (isSublease ? '전대사용동의서' : '임대차계약서');
        return (
          <Modal title={`사업장 상세 (${loc.location_type === '공장' ? (loc.factory_no || '공장') : loc.location_type})`}
            onClose={() => setShowBizLocDetailId(null)} width={BANK_MODAL_WIDTH}
            headerExtra={
              <button type="button" onClick={() => deleteBizLocation(loc.id)} style={headerDeleteBtn}>삭제</button>
            }
          >
            {fileName ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <label style={{ ...lbl, marginBottom: 0, flex: 1 }}>{docLabel} 미리보기</label>
                  <button type="button" onClick={() => triggerFileDownload(fileName, origName || fileName)}
                    style={{ padding: '5px 10px', fontSize: 12, background: '#fff', color: '#3a7afe', border: '1px solid #3a7afe', borderRadius: 6, cursor: 'pointer' }}>다운로드</button>
                  <button type="button" onClick={deleteBizLocFile(loc.id, routeField)}
                    style={{ padding: '5px 10px', fontSize: 12, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer' }}>파일삭제</button>
                </div>
                {/\.(png|jpe?g|gif|bmp|webp)$/i.test(fileName) ? (
                  <img src={`${API.defaults.baseURL}/../uploads/company_info/${fileName}`} alt="문서 미리보기"
                    style={{ width: '100%', maxHeight: 420, objectFit: 'contain', border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }} />
                ) : (
                  <iframe src={`${API.defaults.baseURL}/../uploads/company_info/${fileName}`} title="문서 미리보기"
                    style={{ width: '100%', height: 420, border: '1px solid #eee', borderRadius: 8 }} />
                )}
              </div>
            ) : (
              <div style={{ fontSize: 12.5, color: '#888', background: '#f8fafc', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                첨부된 {docLabel}가 없습니다.
              </div>
            )}
            {isSublease && (
              <div style={row6}>
                <div>
                  <label style={lbl}>전차인(우리 회사)</label>
                  <input style={inp} value={loc.sublease_sublessee_name || ''} onChange={(e) => updateBizLoc(loc.id, 'sublease_sublessee_name', e.target.value)} onBlur={saveBizLocField(loc.id, 'sublease_sublessee_name')} />
                </div>
                <div>
                  <label style={lbl}>임차인(전대인)</label>
                  <input style={inp} value={loc.sublease_tenant_name || ''} onChange={(e) => updateBizLoc(loc.id, 'sublease_tenant_name', e.target.value)} onBlur={saveBizLocField(loc.id, 'sublease_tenant_name')} />
                </div>
                <div>
                  <label style={lbl}>동의일자</label>
                  <input type="date" style={inp} value={loc.sublease_consent_date || ''} onChange={(e) => updateBizLoc(loc.id, 'sublease_consent_date', e.target.value)} onBlur={saveBizLocField(loc.id, 'sublease_consent_date')} />
                </div>
              </div>
            )}
            <div style={row6}>
              <div>
                <label style={lbl}>임대부분</label>
                <input style={inp} value={loc.leased_portion || ''} onChange={(e) => updateBizLoc(loc.id, 'leased_portion', e.target.value)} onBlur={saveBizLocField(loc.id, 'leased_portion')} />
              </div>
              <div>
                <label style={lbl}>토지 면적</label>
                <div style={unitWrap}>
                  <input style={unitInp} value={loc.land_area || ''} onChange={(e) => updateBizLoc(loc.id, 'land_area', e.target.value)} onBlur={saveBizLocField(loc.id, 'land_area')} />
                  <span style={unitSuffix}>㎡</span>
                </div>
              </div>
              <div>
                <label style={lbl}>건물 구조</label>
                <input style={inp} value={loc.building_structure || ''} onChange={(e) => updateBizLoc(loc.id, 'building_structure', e.target.value)} onBlur={saveBizLocField(loc.id, 'building_structure')} />
              </div>
              <div>
                <label style={lbl}>임대면적</label>
                <div style={unitWrap}>
                  <input style={unitInp} value={loc.leased_area || ''} onChange={(e) => updateBizLoc(loc.id, 'leased_area', e.target.value)} onBlur={saveBizLocField(loc.id, 'leased_area')} />
                  <span style={unitSuffix}>㎡</span>
                </div>
              </div>
              <div>
                <label style={lbl}>임대인</label>
                <input style={inp} value={loc.landlord || ''} onChange={(e) => updateBizLoc(loc.id, 'landlord', e.target.value)} onBlur={saveBizLocField(loc.id, 'landlord')} />
              </div>
              <div>
                <label style={lbl}>임대인 연락처</label>
                <input style={inp} value={loc.landlord_contact || ''} onChange={(e) => updateBizLoc(loc.id, 'landlord_contact', e.target.value)} onBlur={saveBizLocField(loc.id, 'landlord_contact')} />
              </div>
            </div>
            <div style={row6}>
              <div>
                <label style={lbl}>관리인</label>
                <input style={inp} value={loc.manager || ''} onChange={(e) => updateBizLoc(loc.id, 'manager', e.target.value)} onBlur={saveBizLocField(loc.id, 'manager')} />
              </div>
              <div>
                <label style={lbl}>관리인 연락처</label>
                <input style={inp} value={loc.manager_contact || ''} onChange={(e) => updateBizLoc(loc.id, 'manager_contact', e.target.value)} onBlur={saveBizLocField(loc.id, 'manager_contact')} />
              </div>
              <div>
                <label style={lbl}>임대료 납부계좌</label>
                <input style={inp} value={loc.rent_bank || ''} placeholder="은행명" onChange={(e) => updateBizLoc(loc.id, 'rent_bank', e.target.value)} onBlur={saveBizLocField(loc.id, 'rent_bank')} />
              </div>
              <div>
                <label style={lbl}>예금주</label>
                <input style={inp} value={loc.rent_account_holder || ''} placeholder="예금주" onChange={(e) => updateBizLoc(loc.id, 'rent_account_holder', e.target.value)} onBlur={saveBizLocField(loc.id, 'rent_account_holder')} />
              </div>
              <div>
                <label style={lbl}>계좌번호</label>
                <input style={inp} value={loc.rent_account_number || ''} placeholder="계좌번호" onChange={(e) => updateBizLoc(loc.id, 'rent_account_number', e.target.value)} onBlur={saveBizLocField(loc.id, 'rent_account_number')} />
              </div>
              <div>
                <label style={lbl}>비고</label>
                <input style={inp} value={loc.remark || ''} onChange={(e) => updateBizLoc(loc.id, 'remark', e.target.value)} onBlur={saveBizLocField(loc.id, 'remark')} />
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* 2026-09(2차 수정) 추가 — 요청사항 "OCR로 읽은 내용이 입력되는 곳은 환경설정 - 회사정보에
          입력되게 수정". 자가/임대 토글 자체는 위 사업장정보 모달 헤더에만 두고, 여기서는 같은
          form.lease_type 값을 그대로 읽어 현재 구분에 맞는 입력 필드만 보여준다(모달과 상태를
          공유하므로 모달을 닫아도 입력값은 그대로 유지된다). */}
      {/* 2026-09(종된사업장 명세 추가) — 요청사항 "사업자등록증 업로드시 종된사업장이 있는경우
          사업장 정보 바로 위에 종된사업장 항목 추가". 사업자단위과세 적용 종된사업장이 있는
          사업자등록증(2페이지)을 OCR로 인식했을 때만 아래 8개 필드가 채워지므로, 8개 필드 중
          하나라도 값이 있을 때만 섹션 자체를 노출한다(대다수 사업자등록증에는 이 항목이 없다). */}
      {(form.sub_biz_reg_no || form.sub_serial_no || form.sub_company_name || form.sub_establishment_date ||
        form.sub_ceo_name || form.sub_address || form.sub_biz_type || form.sub_biz_item) && (
        <>
          <div style={section}>종된사업장</div>
          {/* 2026-09(종된사업장 입력칸 폭 조정) — 사업장소재지는 주소가 길어 다른 칸의 2배
              폭으로, 나머지 7칸은 남는 폭을 균등하게 분배한다(기존 1fr 8칸 중 소재지 칸만
              2fr로 변경). */}
          <div style={{ ...row8, gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 2fr 1fr 1fr' }}>
            <div>
              <label style={lbl}>사업자등록번호</label>
              <input style={inp} value={form.sub_biz_reg_no || ''} onChange={set('sub_biz_reg_no')} />
            </div>
            <div>
              <label style={lbl}>일련번호</label>
              <input style={inp} value={form.sub_serial_no || ''} onChange={set('sub_serial_no')} />
            </div>
            <div>
              <label style={lbl}>상호</label>
              <input style={inp} value={form.sub_company_name || ''} onChange={set('sub_company_name')} />
            </div>
            <div>
              <label style={lbl}>개설일</label>
              <input type="date" style={inp} value={form.sub_establishment_date || ''} onChange={set('sub_establishment_date')} />
            </div>
            <div>
              <label style={lbl}>대표자</label>
              <input style={inp} value={form.sub_ceo_name || ''} onChange={set('sub_ceo_name')} />
            </div>
            <div>
              <label style={lbl}>사업장소재지</label>
              <input style={inp} value={form.sub_address || ''} onChange={set('sub_address')} />
            </div>
            <div>
              <label style={lbl}>업태</label>
              <input style={inp} value={form.sub_biz_type || ''} onChange={set('sub_biz_type')} />
            </div>
            <div>
              <label style={lbl}>종목</label>
              <input style={inp} value={form.sub_biz_item || ''} onChange={set('sub_biz_item')} />
            </div>
          </div>
        </>
      )}
      <div style={section}>사업장 정보</div>
      {/* 2026-09(사업장정보 한줄 표기 개편) — 요청사항 "본점, 자가, 소재지, 건물용도, 면적, 보증금,
          임대료, 관리비, 계약일, 종료일, 비고, 상세 순서로 입력"에 따라 기존 3행(5+7+7칸) 구조를
          12칸 한 줄로 통합한다. 자가/임대 구분은 이 행의 "자가" 선택칸이 곧 기존 lease_type
          토글이며(상단 "🏢 사업장정보" 모달 헤더의 토글과 값을 공유), 자가로 선택하면 보증금·
          임대료 칸의 라벨만 대출원금·대출상환으로 바뀌고 저장 필드는 그대로 재사용한다(별도
          컬럼을 늘리지 않음). 나머지 세부항목(임대부분/토지면적/건물구조/임대인/임대인연락처/
          관리인/관리인연락처/임대료납부계좌)과 첨부문서 미리보기는 "상세" 버튼의 모달로 옮겼다. */}
      <div style={rowBiz}>
        <div>
          {/* 2026-09(사업장정보 라벨 변경) — 요청사항 "본점을 구분으로 수정". 단일 레코드
              구조는 유지하고(자동 1공장/2공장 부여는 여러 사업장을 등록하는 구조가 없어
              보류) 라벨만 "구분"으로 변경, 공장 선택 시 사용자가 직접 번호를 입력하는
              기존 방식은 그대로 둔다. */}
          <label style={lbl}>구분</label>
          <select style={inp} value={form.biz_location_type || '본점'}
            onChange={(e) => { const val = e.target.value; setForm(v => ({ ...v, biz_location_type: val, ...(val !== '공장' ? { biz_location_factory_no: '' } : {}) })); }}>
            <option value="본점">본점</option>
            <option value="지사">지사</option>
            <option value="공장">공장</option>
          </select>
          {form.biz_location_type === '공장' && (
            <input style={{ ...inp, marginTop: -4 }} placeholder="예: 1공장" value={form.biz_location_factory_no || ''} onChange={set('biz_location_factory_no')} />
          )}
        </div>
        <div>
          <label style={lbl}>자가/임대</label>
          <select style={inp} value={form.lease_type || 'lease'} onChange={(e) => setForm(v => ({ ...v, lease_type: e.target.value }))}>
            <option value="lease">임대</option>
            <option value="own">자가</option>
          </select>
        </div>
        <div>
          <label style={lbl}>소재지</label>
          <input style={inp} value={form.lease_address || ''} onChange={set('lease_address')} />
        </div>
        <div>
          <label style={lbl}>건물 용도</label>
          <input style={inp} value={form.lease_building_usage || ''} onChange={set('lease_building_usage')} />
        </div>
        <div>
          {/* 2026-09(사업장정보 면적 필드 수정) — 요청사항 "면적에 임대면적을 기입". 기존에는
              건물 전체면적(lease_building_area, 예: 3361.24)이 표시되어 실제 임대(사용)하는
              면적과 달랐다. 건물 전체면적이 아니라 임대면적(lease_leased_area)을 표시/저장하도록
              변경. 건물 전체면적 자체는 "상세" 모달의 별도 입력칸(토지/건물구조 옆)에 그대로
              남아있어 데이터 손실은 없다. */}
          <label style={lbl}>임대면적</label>
          <div style={unitWrap}>
            <input style={unitInp} value={form.lease_leased_area || ''} onChange={set('lease_leased_area')} />
            <span style={unitSuffix}>㎡</span>
          </div>
        </div>
        <div>
          <label style={lbl}>{(form.lease_type || 'lease') === 'own' ? '대출원금' : '보증금'}</label>
          <div style={unitWrap}>
            <input style={unitInp} value={withComma(form.lease_deposit)}
              onChange={(e) => setForm(v => ({ ...v, lease_deposit: onlyDigits(e.target.value) }))} />
            <span style={unitSuffix}>원</span>
          </div>
        </div>
        <div>
          <label style={lbl}>{(form.lease_type || 'lease') === 'own' ? '대출상환' : '임대료(VAT제외)'}</label>
          <div style={unitWrap}>
            <input style={unitInp} value={withComma(form.lease_monthly_rent)}
              onChange={(e) => setForm(v => ({ ...v, lease_monthly_rent: onlyDigits(e.target.value) }))} />
            <span style={unitSuffix}>원</span>
          </div>
        </div>
        <div>
          <label style={lbl}>관리비</label>
          <div style={unitWrap}>
            <input style={unitInp} value={withComma(form.lease_management_fee)} placeholder="관리비"
              onChange={(e) => setForm(v => ({ ...v, lease_management_fee: onlyDigits(e.target.value) }))} />
            <span style={unitSuffix}>원</span>
          </div>
        </div>
        <div>
          <label style={lbl}>계약일</label>
          <input type="date" style={inp} value={form.lease_start_date || ''} onChange={set('lease_start_date')} />
        </div>
        <div>
          <label style={lbl}>종료일</label>
          <input type="date" style={inp} value={form.lease_end_date || ''} onChange={set('lease_end_date')} />
        </div>
        <div>
          <label style={lbl}>비고</label>
          <input style={inp} value={form.lease_remark || ''} placeholder="비고" onChange={set('lease_remark')} />
        </div>
        <div>
          <label style={lbl}>&nbsp;</label>
          <button type="button" onClick={() => setShowPropertyDetail(true)}
            style={{ width: '100%', padding: '9px 8px', background: '#fff', color: '#185FA5', border: '1px solid #185FA5',
              borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            상세
          </button>
        </div>
      </div>

      {/* 2026-09(추가 사업장 기능) 추가 — 요청사항 "사업자정보에서 임대차계약서나 등기부등본이
          추가로 등록되면 지사, 공장 등으로 추가되게 수정". 위 사업장정보(본점)와 별개로 지사/공장
          등을 여러 건 등록할 수 있는 목록. 목록의 각 행도 본점 행과 동일한 rowBiz 12칸 레이아웃을
          그대로 재사용해 세로줄을 맞춘다. */}
      {bizLocations.map(loc => (
        <div key={loc.id} style={{ ...rowBiz, marginTop: 8 }}>
          <div>
            <select style={inp} value={loc.location_type || '지사'}
              onChange={(e) => { const val = e.target.value; updateBizLoc(loc.id, 'location_type', val); API.put(`/company-info/biz-locations/${loc.id}`, { location_type: val, ...(val !== '공장' ? { factory_no: '' } : {}) }).then(loadBizLocations).catch(() => {}); }}>
              <option value="지사">지사</option>
              <option value="공장">공장</option>
            </select>
          </div>
          <div>
            <select style={inp} value={loc.lease_type || 'lease'}
              onChange={(e) => { const val = e.target.value; updateBizLoc(loc.id, 'lease_type', val); API.put(`/company-info/biz-locations/${loc.id}`, { lease_type: val }).catch(() => {}); }}>
              <option value="lease">임대</option>
              <option value="own">자가</option>
            </select>
          </div>
          <div>
            <input style={inp} value={loc.address || ''} placeholder="소재지"
              onChange={(e) => updateBizLoc(loc.id, 'address', e.target.value)} onBlur={saveBizLocField(loc.id, 'address')} />
          </div>
          <div>
            <input style={inp} value={loc.building_usage || ''} placeholder="건물 용도"
              onChange={(e) => updateBizLoc(loc.id, 'building_usage', e.target.value)} onBlur={saveBizLocField(loc.id, 'building_usage')} />
          </div>
          <div>
            {/* 2026-09(사업장정보 면적 필드 수정) — 본점과 동일하게 건물 전체면적(building_area)
                대신 임대면적(leased_area)을 표시/저장한다. */}
            <div style={unitWrap}>
              <input style={unitInp} value={loc.leased_area || ''}
                onChange={(e) => updateBizLoc(loc.id, 'leased_area', e.target.value)} onBlur={saveBizLocField(loc.id, 'leased_area')} />
              <span style={unitSuffix}>㎡</span>
            </div>
          </div>
          <div>
            <div style={unitWrap}>
              <input style={unitInp} value={withComma(loc.deposit)} placeholder={(loc.lease_type === 'own') ? '대출원금' : '보증금'}
                onChange={(e) => updateBizLoc(loc.id, 'deposit', onlyDigits(e.target.value))} onBlur={saveBizLocField(loc.id, 'deposit')} />
              <span style={unitSuffix}>원</span>
            </div>
          </div>
          <div>
            <div style={unitWrap}>
              <input style={unitInp} value={withComma(loc.monthly_rent)} placeholder={(loc.lease_type === 'own') ? '대출상환' : '임대료(VAT제외)'}
                onChange={(e) => updateBizLoc(loc.id, 'monthly_rent', onlyDigits(e.target.value))} onBlur={saveBizLocField(loc.id, 'monthly_rent')} />
              <span style={unitSuffix}>원</span>
            </div>
          </div>
          <div>
            <div style={unitWrap}>
              <input style={unitInp} value={withComma(loc.management_fee)} placeholder="관리비"
                onChange={(e) => updateBizLoc(loc.id, 'management_fee', onlyDigits(e.target.value))} onBlur={saveBizLocField(loc.id, 'management_fee')} />
              <span style={unitSuffix}>원</span>
            </div>
          </div>
          <div>
            <input type="date" style={inp} value={loc.start_date || ''}
              onChange={(e) => updateBizLoc(loc.id, 'start_date', e.target.value)} onBlur={saveBizLocField(loc.id, 'start_date')} />
          </div>
          <div>
            <input type="date" style={inp} value={loc.end_date || ''}
              onChange={(e) => updateBizLoc(loc.id, 'end_date', e.target.value)} onBlur={saveBizLocField(loc.id, 'end_date')} />
          </div>
          <div>
            <input style={inp} value={loc.remark || ''} placeholder="비고"
              onChange={(e) => updateBizLoc(loc.id, 'remark', e.target.value)} onBlur={saveBizLocField(loc.id, 'remark')} />
          </div>
          <div>
            <button type="button" onClick={() => setShowBizLocDetailId(loc.id)}
              style={{ width: '100%', padding: '9px 8px', background: '#fff', color: '#185FA5', border: '1px solid #185FA5',
                borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              상세
            </button>
          </div>
        </div>
      ))}
      {/* 2026-09(추가 사업장 등록 이관) — 요청사항 "사업장추가 버튼은 삭제하고 사업장정보에서
          사업장을 추가할 수 있도록 수정". 이 자리에 있던 "+ 사업장 추가" 버튼은 삭제하고, 등록은
          상단 "🏢 사업장정보" 버튼의 모달(showPropertyUpload) 안 "추가 사업장" 섹션에서 한다. */}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={bulkClearForm}
          style={{ flex: 1, padding: '10px 18px', background: '#fff', color: '#d33', border: '1px solid #d33',
            borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          일괄삭제
        </button>
        <button onClick={save} disabled={saving}
          style={{ flex: 1, padding: '10px 18px', background: saving ? '#aaa' : '#185FA5', color: '#fff', border: 'none',
            borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
    </div>
  );
}

// ── 2. 브랜드 설정 — 2026-09 대개편: 여러 브랜드(사업부/제품라인 등) 등록/관리 ─────────
// 요청사항: 우측상단 "브랜드추가" 버튼 → 브랜드명+로고 첨부 등록 → 등록된 브랜드를 3분할
// 카드로 나열 → 각 브랜드가 따를 문서번호체계(분야)도 지정 가능. 상단 메뉴바/로그인화면의
// "NINE IMS" 문구 자체(회사 전체의 기본 브랜드 표시)는 그대로 로고 관리 탭에서 관리하고,
// 여기서는 그와 별개로 회사 안의 여러 브랜드를 등록해두는 목록 화면이다.
function BrandSection({ showMsg, showAddBrand, setShowAddBrand }) {
  const [brands, setBrands] = useState([]);
  const [fields, setFields] = useState([]); // doc_reg_fields — 분야별관리일 때의 분야 목록
  const [schemeMode, setSchemeMode] = useState('single'); // 'single' | 'byType'
  const [editing, setEditing] = useState(null); // 수정 대상 브랜드(null이면 신규등록)

  const load = () => API.get('/company-info/brands').then(r => setBrands(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    API.get('/approval/doc-reg/scheme').then(r => setSchemeMode((r.data && r.data.mode) || 'single')).catch(() => {});
    API.get('/approval/doc-reg/fields').then(r => setFields(r.data || [])).catch(() => {});
  }, []);

  const logoUrl = (b) => b.logo_file ? `${API.defaults.baseURL}/../uploads/brand_logos/${b.logo_file}` : null;

  const openEdit = (brand) => { setEditing(brand); setShowAddBrand(true); };
  const closeModal = () => { setShowAddBrand(false); setEditing(null); };

  const del = (id) => {
    if (!window.confirm('이 브랜드를 삭제하시겠습니까?')) return;
    API.delete(`/company-info/brands/${id}`)
      .then(() => { load(); showMsg('브랜드가 삭제되었습니다.', 'success'); })
      .catch(err => showMsg(err.response?.data?.message || '삭제 중 오류가 발생했습니다.', 'error'));
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
        회사 안에 여러 브랜드(사업부, 제품라인 등)를 두고 있다면, 브랜드마다 이름과 로고를
        등록해두고 각 브랜드로 발행되는 문서가 어떤 문서번호체계(분야)를 따를지 지정할 수
        있습니다. 우측 상단의 "+ 브랜드추가" 버튼으로 새 브랜드를 등록하세요.
      </div>

      {brands.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 13,
          background: '#fff', border: '1px solid #eee', borderRadius: 12 }}>
          등록된 브랜드가 없습니다. "+ 브랜드추가" 버튼으로 등록해주세요.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {brands.map(b => (
            <div key={b.id} onClick={() => openEdit(b)}
              style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 18,
                cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#f8fafc', borderRadius: 8, overflow: 'hidden' }}>
                {logoUrl(b)
                  ? <img src={logoUrl(b)} alt={b.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <span style={{ color: '#bbb', fontSize: 12 }}>로고 없음</span>}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, textAlign: 'center' }}>{b.name}</div>
              <div style={{ fontSize: 12, color: '#888', textAlign: 'center' }}>
                {schemeMode === 'byType'
                  ? (b.doc_field_name ? `문서번호 분야: ${b.doc_field_name}(${b.doc_field_abbr})` : '문서번호 분야: 미지정')
                  : '문서번호: 회사 공통 단일체계 사용'}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }} onClick={(e) => e.stopPropagation()}>
                <button onClick={() => openEdit(b)}
                  style={{ flex: 1, padding: '7px 0', background: '#475569', color: '#fff', border: 'none',
                    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>수정</button>
                <button onClick={() => del(b.id)}
                  style={{ flex: 1, padding: '7px 0', background: '#fff', color: '#d33', border: '1px solid #d33',
                    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddBrand && (
        <BrandFormModal
          brand={editing}
          fields={fields}
          schemeMode={schemeMode}
          onClose={closeModal}
          onSaved={() => { closeModal(); load(); showMsg('브랜드가 저장되었습니다.', 'success'); }}
          showMsg={showMsg}
        />
      )}
    </div>
  );
}

// 브랜드 등록/수정 모달 — 브랜드명, 로고 첨부, (분야별관리일 때만) 문서번호 분야 선택.
function BrandFormModal({ brand, fields, schemeMode, onClose, onSaved, showMsg }) {
  const [name, setName] = useState(brand ? brand.name : '');
  const [docFieldId, setDocFieldId] = useState(brand && brand.doc_field_id ? String(brand.doc_field_id) : '');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(
    brand && brand.logo_file ? `${API.defaults.baseURL}/../uploads/brand_logos/${brand.logo_file}` : null
  );
  const [saving, setSaving] = useState(false);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const save = () => {
    if (!name.trim()) { showMsg('브랜드명을 입력해주세요.', 'error'); return; }
    const fd = new FormData();
    fd.append('name', name.trim());
    fd.append('doc_field_id', docFieldId || '');
    if (file) fd.append('file', file);
    setSaving(true);
    const req = brand
      ? API.put(`/company-info/brands/${brand.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      : API.post('/company-info/brands', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    req.then(onSaved)
      .catch(err => showMsg(err.response?.data?.message || '저장 중 오류가 발생했습니다.', 'error'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 440 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{brand ? '브랜드 수정' : '브랜드 추가'}</div>

        <label style={lbl}>브랜드명</label>
        <input style={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="예: NINE, 세컨드 브랜드명 등" />

        <label style={lbl}>로고</label>
        <FileDropZone
          onFile={pickFile}
          accept="image/*"
          fileName={file ? file.name : null}
          currentLabel={!file && brand && brand.logo_orig ? brand.logo_orig : null}
        />
        {preview && (
          <div style={{ marginTop: 10, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#f8fafc', borderRadius: 8 }}>
            <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </div>
        )}

        <label style={{ ...lbl, marginTop: 14 }}>문서번호 분야</label>
        {schemeMode === 'byType' ? (
          <select style={inp} value={docFieldId} onChange={(e) => setDocFieldId(e.target.value)}>
            <option value="">지정 안 함</option>
            {fields.map(f => (
              <option key={f.id} value={f.id}>{f.name}({f.abbr})</option>
            ))}
          </select>
        ) : (
          <div style={{ ...inp, background: '#f8fafc', color: '#888', display: 'flex', alignItems: 'center' }}>
            현재 회사 공통 단일 문서번호체계를 사용 중이라, 모든 브랜드가 이 체계를 그대로
            따릅니다. 분야별관리로 전환하면(환경설정 &gt; 문서번호) 브랜드별로 분야를 지정할
            수 있습니다.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '10px 0', background: '#fff', color: '#555', border: '1px solid #ddd',
              borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>취소</button>
          <button onClick={save} disabled={saving}
            style={{ flex: 1, padding: '10px 0', background: saving ? '#aaa' : PRIMARY, color: '#fff', border: 'none',
              borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 3. 로고 관리 — 5종(국문/영문 × 가로/세로 + 심볼) 등록, 종류별 여러 장 ─────────────────
// 2026-09(로고관리 화면 개편) — 요청사항 3건:
//   1) 안내문구+종류선택+드래그앤드롭 업로드 UI를 본문에서 빼고, 헤더의 "+ 로고추가" 버튼을
//      눌러야 뜨는 모달로 이동(showAddLogo/setShowAddLogo는 부모 Settings가 헤더 버튼과 함께 관리).
//   2) 5종 로고를 작은 카드(썸네일+파일명+삭제버튼) 목록이 아니라, 종류명 아래 큰 이미지 1장만
//      보여주는 형태로 변경 — 여러 장이 등록된 경우 최신 등록분을 대표로 표시한다.
//   3) 그 큰 이미지를 클릭하면 모달이 뜨고, 그 안에서 등록된 버전 전체를 다운로드/삭제할 수 있다.
function LogoSection({ showMsg, showAddLogo, setShowAddLogo }) {
  const [logos, setLogos] = useState([]);
  const [uploadType, setUploadType] = useState('kr_h');
  const [uploading, setUploading] = useState(false);
  const [detailType, setDetailType] = useState(null); // 클릭한 로고 종류(모달에 표시할 대상)
  // 2026-09(로고관리 화면 개편 5차) — 요청사항: 브랜드 설정 카드(미리보기+브랜드명 입력+이니셜
  // 입력+설명 전체)를 가장 좌측에 표시, 편집도 이 탭에서 바로 가능하도록 복원.
  const [brandForm, setBrandForm] = useState(null);
  const [brandSaving, setBrandSaving] = useState(false);

  const load = () => API.get('/company-info/logos').then(r => setLogos(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    API.get('/company-info').then(r => setBrandForm(r.data || {})).catch(() => setBrandForm({}));
  }, []);

  const setBrand = (k) => (e) => setBrandForm(v => ({ ...v, [k]: e.target.value }));
  const saveBrand = () => {
    setBrandSaving(true);
    API.put('/company-info', {
      brand_name: brandForm.brand_name || '',
      brand_initial: brandForm.brand_initial || '',
      // 2026-10 요청: "IMS" 고정 문구도 수정 가능하도록 추가
      brand_suffix: brandForm.brand_suffix || '',
    })
      .then(() => showMsg('브랜드 설정이 저장되었습니다.', 'success'))
      .catch(err => showMsg(err.response?.data?.message || '저장 중 오류가 발생했습니다.', 'error'))
      .finally(() => setBrandSaving(false));
  };

  const logoUrl = (l) => `${API.defaults.baseURL}/../uploads/company_logos/${l.file_name}`;

  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('logo_type', uploadType);
    setUploading(true);
    API.post('/company-info/logos', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => { load(); showMsg('로고가 등록되었습니다.', 'success'); })
      .catch(err => showMsg(err.response?.data?.message || '업로드 중 오류가 발생했습니다.', 'error'))
      .finally(() => setUploading(false));
  };
  const del = (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/company-info/logos/${id}`)
      .then(() => { load(); showMsg('삭제되었습니다.', 'success'); })
      .catch(() => showMsg('삭제 중 오류가 발생했습니다.', 'error'));
  };

  const brandName = brandForm ? (brandForm.brand_name || 'NINE') : 'NINE';
  const brandInitial = brandForm ? (brandForm.brand_initial || 'N') : 'N';
  const brandSuffix = brandForm ? (brandForm.brand_suffix || 'IMS') : 'IMS';

  // 로고 이미지 1종을 표시하는 박스(클릭시 상세/다운로드/삭제 모달)
  // scale: 1 = 기본크기, 1.5 = 심볼처럼 1.5배 크게 표시
  const LogoBox = ({ type, scale = 1 }) => {
    const items = logos.filter(l => l.logo_type === type);
    const primary = items[0]; // 최신 등록분이 먼저 오는 순서라고 가정(백엔드 기준)
    const h = Math.round(130 * scale);
    const w = Math.round(220 * scale);
    const boxW = Math.round(160 * scale);
    return (
      <div style={{ minWidth: boxW }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 10 }}>{LOGO_TYPE_LABEL[type]}</div>
        {primary ? (
          <img src={logoUrl(primary)} alt={primary.orig_name} onClick={() => setDetailType(type)}
            style={{ height: h, maxWidth: w, objectFit: 'contain', display: 'block', cursor: 'pointer' }} />
        ) : (
          <div onClick={() => setDetailType(type)}
            style={{ height: h, width: boxW, border: '1px dashed #ccc', borderRadius: 8, display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#aaa', cursor: 'pointer' }}>
            등록된 로고가 없습니다.
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20 }}>
      {/* 2026-09(로고관리 화면 개편 6차) — 요청사항 2건:
          1) 가로폭에 맞춰 4개 컬럼(브랜드설정/심볼/국문2종/영문2종) 사이 간격을 넓게 배분
          2) 심볼 이미지 크기를 약 1.5배로 확대 */}
      <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        {/* 가장 좌측: 브랜드 설정 카드 (미리보기 + 브랜드명 + 이니셜 + 설명) */}
        <div style={{ minWidth: 260, maxWidth: 300 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 40, height: 38, background: '#185FA5', borderRadius: 8, display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 20 }}>
              {brandInitial}
            </div>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{brandName} {brandSuffix}</span>
          </div>
          <label style={lbl}>브랜드명(상단 메뉴바 "○○○ {brandSuffix}")</label>
          <input style={inp} value={brandForm ? (brandForm.brand_name || '') : ''} onChange={setBrand('brand_name')} placeholder="NINE" />
          <label style={lbl}>이니셜(로그인화면 원형 배지)</label>
          <input style={inp} value={brandForm ? (brandForm.brand_initial || '') : ''} onChange={setBrand('brand_initial')} placeholder="N" maxLength={2} />
          <label style={lbl}>뒷부분 문구(상단 메뉴바 "NINE ○○○")</label>
          <input style={inp} value={brandForm ? (brandForm.brand_suffix || '') : ''} onChange={setBrand('brand_suffix')} placeholder="IMS" />
          <div style={{ fontSize: 11, color: '#999', margin: '10px 0 12px', lineHeight: 1.5 }}>
            상단 메뉴바와 로그인화면에 표시되는 "NINE IMS" 문구 중 회사 이름 부분/뒷부분 문구와,
            로그인화면 원형 배지에 표시되는 이니셜을 우리 회사에 맞게 바꿀 수 있습니다. 값을 비워두면
            기본값(NINE/N/IMS)이 사용됩니다.
          </div>
          <button onClick={saveBrand} disabled={brandSaving}
            style={{ width: '100%', padding: '8px 14px', background: brandSaving ? '#aaa' : '#185FA5', color: '#fff', border: 'none',
              borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: brandSaving ? 'not-allowed' : 'pointer' }}>
            {brandSaving ? '저장 중...' : '저장'}
          </button>
        </div>

        {/* 심볼: 단독 컬럼, 1.5배 확대 */}
        <LogoBox type="symbol" scale={1.5} />

        {/* 국문세로형(위) + 국문가로형(아래) 한 컬럼 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <LogoBox type="kr_v" />
          <LogoBox type="kr_h" />
        </div>

        {/* 영문세로형(위) + 영문가로형(아래) 한 컬럼 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <LogoBox type="en_v" />
          <LogoBox type="en_h" />
        </div>
      </div>

      {/* 헤더 "+ 로고추가" 버튼으로 여는 업로드 모달 */}
      {showAddLogo && (
        <Modal title="로고추가" onClose={() => setShowAddLogo(false)} width={520}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
            국문/영문 × 가로형/세로형과 심볼, 총 5종의 회사로고를 등록해두면 시스템 내 문서나 화면에서
            필요한 로고 종류를 선택해 적용할 수 있습니다. 종류당 여러 장을 등록할 수 있으며, 최신
            등록분이 우선 사용됩니다.
          </div>
          <label style={lbl}>로고 종류</label>
          <select value={uploadType} onChange={e => setUploadType(e.target.value)} style={inp}>
            {LOGO_TYPES.map(t => <option key={t} value={t}>{LOGO_TYPE_LABEL[t]}</option>)}
          </select>
          <FileDropZone onFile={(f) => { upload(f); }} accept="image/*" fileName={uploading ? '업로드 중...' : undefined} />
        </Modal>
      )}

      {/* 로고 이미지를 클릭하면 뜨는, 종류별 등록 버전 목록 + 다운로드/삭제 모달 */}
      {detailType && (
        <Modal title={LOGO_TYPE_LABEL[detailType]} onClose={() => setDetailType(null)} width={480}>
          {logos.filter(l => l.logo_type === detailType).length === 0 ? (
            <div style={{ fontSize: 13, color: '#aaa', padding: '10px 0' }}>등록된 로고가 없습니다.</div>
          ) : (
            logos.filter(l => l.logo_type === detailType).map(l => (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                <img src={logoUrl(l)} alt={l.orig_name} style={{ height: 44, maxWidth: 100, objectFit: 'contain' }} />
                <div style={{ flex: 1, fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.orig_name || l.file_name}
                </div>
                <a href={logoUrl(l)} download={l.orig_name || l.file_name}
                  style={{ ...btn('#3a7afe'), textDecoration: 'none', display: 'inline-block' }}>다운로드</a>
                <button onClick={() => del(l.id)} style={headerDeleteBtn}>삭제</button>
              </div>
            ))
          )}
        </Modal>
      )}
    </div>
  );
}

// ── 4. 명함양식 관리 — 2026-09 대개편. 명함 제작(인쇄/외주)에 쓸 디자인 양식 파일을
// "양식등록"으로 올려두고(기본적으로 AI 파일), 그 양식이 이름/직급/연락처/이메일 중 어떤
// 필드를 담고 있는지 골라둔다. "명함등록"을 누르면 인사관리(직원현황)에 등록된 직원 목록이
// 뜨고, 원하는 사람을 고르면 그 필드값(+환경설정 > 회사정보의 회사명/전화/주소/이메일)이
// 자동으로 채워진 "명함 발행 대상" 행이 만들어진다. 디자인업체에 넘길 때는 하단 목록에서
// 엑셀로 내보내면 된다. 명함관리(영업관리 > 업체관리 > 연락처관리, business_cards — 명함
// 사진을 OCR로 읽어 전자명함화하는 기능)와는 완전히 별개의 "인쇄 발행용" 목록이다.
const CARD_FIELD_DEFS = [
  { key: 'name', label: '이름' },
  { key: 'position', label: '직급' },
  { key: 'phone', label: '연락처' },
  { key: 'email', label: '이메일' },
];

function CardTemplateSection({ showMsg, showAddCardTemplate, setShowAddCardTemplate, showAddCardIssue, setShowAddCardIssue, searchTerm }) {
  const [items, setItems] = useState([]);
  const [issues, setIssues] = useState([]);
  const [editingIssue, setEditingIssue] = useState(null);

  const loadTemplates = () => API.get('/company-info/card-templates').then(r => setItems(r.data || [])).catch(() => {});
  const loadIssues = () => API.get('/company-info/card-issues').then(r => setIssues(r.data || [])).catch(() => {});
  useEffect(() => { loadTemplates(); loadIssues(); }, []);

  const setDefault = (id) => {
    API.patch(`/company-info/card-templates/${id}/default`)
      .then(() => { loadTemplates(); showMsg('기본 양식으로 지정되었습니다.', 'success'); })
      .catch(() => showMsg('지정 중 오류가 발생했습니다.', 'error'));
  };
  const delTemplate = (id) => {
    if (!window.confirm('이 양식을 삭제하시겠습니까? 이 양식으로 발행된 명함 목록도 함께 삭제됩니다.')) return;
    API.delete(`/company-info/card-templates/${id}`)
      .then(() => { loadTemplates(); loadIssues(); showMsg('삭제되었습니다.', 'success'); })
      .catch(() => showMsg('삭제 중 오류가 발생했습니다.', 'error'));
  };
  const delIssue = (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    API.delete(`/company-info/card-issues/${id}`)
      .then(() => { loadIssues(); showMsg('삭제되었습니다.', 'success'); })
      .catch(() => showMsg('삭제 중 오류가 발생했습니다.', 'error'));
  };
  const exportIssues = () => {
    API.get('/company-info/card-issues/export', { responseType: 'blob' })
      .then(r => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = url; a.download = `명함등록목록_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => showMsg('내보내기 중 오류가 발생했습니다.', 'error'));
  };

  const fieldBadges = (fieldsUsedJson) => {
    let keys = [];
    try { keys = JSON.parse(fieldsUsedJson) || []; } catch { keys = ['name', 'position', 'phone', 'email']; }
    return CARD_FIELD_DEFS.filter(f => keys.includes(f.key)).map(f => f.label).join(' · ') || '-';
  };

  const filteredIssues = issues.filter(row => {
    if (!searchTerm || !searchTerm.trim()) return true;
    const q = searchTerm.trim().toLowerCase();
    return [row.name, row.department, row.position, row.phone, row.email]
      .some(v => (v || '').toLowerCase().includes(q));
  });

  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20 }}>
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: '#aaa', padding: '10px 0' }}>등록된 명함양식이 없습니다. 우측 상단 "+ 양식등록"으로 먼저 등록해주세요.</div>
      ) : (
        items.map(it => (
          <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', marginBottom: 6,
            background: it.is_default ? '#eef5ff' : '#fafafa', border: it.is_default ? '1px solid #3a7afe' : '1px solid #eee', borderRadius: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#333', whiteSpace: 'nowrap' }}>{it.name}</span>
            <span style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {it.orig_name || it.file_name}</span>
            <span style={{ fontSize: 11, color: '#3a7afe', background: '#eef5ff', border: '1px solid #cfe0fb', borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>
              사용필드: {fieldBadges(it.fields_used)}
            </span>
            <span style={{ flex: 1 }} />
            <button onClick={() => setDefault(it.id)}
              style={{ flexShrink: 0, padding: '6px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                background: it.is_default ? '#3a7afe' : '#fff', color: it.is_default ? '#fff' : '#3a7afe', border: '1px solid #3a7afe' }}>
              {it.is_default ? '기본양식' : '기본으로 지정'}
            </button>
            <button onClick={() => delTemplate(it.id)}
              style={{ flexShrink: 0, padding: '6px 10px', fontSize: 12, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer' }}>삭제</button>
          </div>
        ))
      )}

      <div style={{ ...section, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>명함등록 목록 ({filteredIssues.length}건{searchTerm && searchTerm.trim() ? ` / 전체 ${issues.length}건` : ''})</span>
        {issues.length > 0 && (
          <button onClick={exportIssues} style={{ ...btn('#16a34a'), padding: '6px 12px', fontSize: 12 }}>⬇ 내보내기(엑셀)</button>
        )}
      </div>

      {filteredIssues.length === 0 ? (
        <div style={{ fontSize: 12, color: '#aaa', padding: '10px 0' }}>
          {issues.length === 0 ? '등록된 명함이 없습니다. 우측 상단 "+ 명함등록"으로 직원을 선택해 만들어보세요.' : '검색 결과가 없습니다.'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', color: '#666' }}>
              {['양식', '부서', '직급', '이름', '연락처', '이메일', ''].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredIssues.map(row => (
              <tr key={row.id} style={{ borderBottom: '1px solid #f2f2f2' }}>
                <td style={{ padding: '8px 10px', color: '#888' }}>{row.template_name || '-'}</td>
                <td style={{ padding: '8px 10px' }}>{row.department || '-'}</td>
                <td style={{ padding: '8px 10px' }}>{row.position || '-'}</td>
                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{row.name || '-'}</td>
                <td style={{ padding: '8px 10px' }}>{row.phone || '-'}</td>
                <td style={{ padding: '8px 10px' }}>{row.email || '-'}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button onClick={() => setEditingIssue(row)}
                    style={{ padding: '4px 8px', fontSize: 11, background: '#fff', color: '#185FA5', border: '1px solid #185FA5', borderRadius: 5, cursor: 'pointer', marginRight: 6 }}>수정</button>
                  <button onClick={() => delIssue(row.id)}
                    style={{ padding: '4px 8px', fontSize: 11, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 5, cursor: 'pointer' }}>삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAddCardTemplate && (
        <CardTemplateFormModal
          onClose={() => setShowAddCardTemplate(false)}
          onSaved={() => { loadTemplates(); setShowAddCardTemplate(false); showMsg('명함양식이 등록되었습니다.', 'success'); }}
          showMsg={showMsg}
        />
      )}
      {showAddCardIssue && (
        <CardIssueFormModal
          templates={items}
          onClose={() => setShowAddCardIssue(false)}
          onSaved={(msgText) => { loadIssues(); setShowAddCardIssue(false); showMsg(msgText, 'success'); }}
          showMsg={showMsg}
        />
      )}
      {editingIssue && (
        <CardIssueEditModal
          issue={editingIssue}
          onClose={() => setEditingIssue(null)}
          onSaved={() => { loadIssues(); setEditingIssue(null); showMsg('수정되었습니다.', 'success'); }}
          showMsg={showMsg}
        />
      )}
    </div>
  );
}

// 양식등록 모달 — 이름 + 사용필드 체크박스 + 파일(기본 AI 파일) 업로드.
function CardTemplateFormModal({ onClose, onSaved, showMsg }) {
  const [name, setName] = useState('');
  const [fields, setFields] = useState(['name', 'position', 'phone', 'email']);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggleField = (key) => {
    setFields(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const save = () => {
    if (!name.trim()) { showMsg('양식 이름을 입력해주세요.', 'error'); return; }
    if (!file) { showMsg('디자인 파일을 첨부해주세요.(AI 파일 권장)', 'error'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name.trim());
    fd.append('fields_used', JSON.stringify(fields));
    setSaving(true);
    API.post('/company-info/card-templates', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => onSaved())
      .catch(err => showMsg(err.response?.data?.message || '업로드 중 오류가 발생했습니다.', 'error'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>📇 양식등록</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>

        <label style={lbl}>양식 이름</label>
        <input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="예: 2026년 기본 명함, 영업팀 전용" />

        <label style={lbl}>이 양식이 담고 있는 필드</label>
        <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
          {CARD_FIELD_DEFS.map(f => (
            <label key={f.key} style={{ fontSize: 13, color: '#444', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="checkbox" checked={fields.includes(f.key)} onChange={() => toggleField(f.key)} />
              {f.label}
            </label>
          ))}
        </div>

        <label style={lbl}>디자인 파일 (AI 파일 기본, PDF/이미지도 가능)</label>
        <FileDropZone onFile={setFile} accept=".ai,.eps,.pdf,image/*" fileName={file ? file.name : null} />

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button onClick={onClose} style={{ flex: 1, ...btn('#eee', '#555') }}>취소</button>
          <button onClick={save} disabled={saving} style={{ flex: 1, ...btn(saving ? '#aaa' : PRIMARY) }}>
            {saving ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>
    </div>
  );
}

// 명함등록 모달 — 명함양식 선택 + 직원현황(인사관리) 인원 다중선택 → 자동반영.
function CardIssueFormModal({ templates, onClose, onSaved, showMsg }) {
  const [templateId, setTemplateId] = useState(templates.find(t => t.is_default)?.id || templates[0]?.id || '');
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    API.get('/company-info/employees-lite').then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const toggle = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filtered = employees.filter(e => !search.trim() ||
    (e.name || '').includes(search) || (e.department || '').includes(search) || (e.position || '').includes(search));

  const apply = () => {
    if (!templateId) { showMsg('명함양식을 먼저 선택해주세요.', 'error'); return; }
    if (!selected.length) { showMsg('직원을 1명 이상 선택해주세요.', 'error'); return; }
    setSaving(true);
    API.post('/company-info/card-issues/from-employees', { template_id: templateId, user_ids: selected })
      .then(r => onSaved(r.data?.message || '명함이 등록되었습니다.'))
      .catch(err => showMsg(err.response?.data?.message || '등록 중 오류가 발생했습니다.', 'error'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 560, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>📇 명함등록</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>

        <label style={lbl}>명함양식</label>
        {templates.length === 0 ? (
          <div style={{ fontSize: 12, color: '#e05a5a', marginBottom: 14 }}>등록된 명함양식이 없습니다. 먼저 "양식등록"으로 디자인 파일을 등록해주세요.</div>
        ) : (
          <select style={inp} value={templateId} onChange={e => setTemplateId(e.target.value)}>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (기본)' : ''}</option>)}
          </select>
        )}

        <label style={lbl}>직원현황(인사관리)에서 선택 — {selected.length}명 선택됨</label>
        <input style={{ ...inp, marginBottom: 8 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="이름/부서/직급 검색" />
        <div style={{ border: '1px solid #eee', borderRadius: 8, maxHeight: 260, overflowY: 'auto', marginBottom: 14 }}>
          {filtered.length === 0 ? (
            <div style={{ fontSize: 12, color: '#aaa', padding: 14, textAlign: 'center' }}>표시할 직원이 없습니다.</div>
          ) : (
            filtered.map(e => (
              <label key={e.id} onClick={() => toggle(e.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                  borderBottom: '1px solid #f5f5f5', background: selected.includes(e.id) ? '#eef5ff' : '#fff' }}>
                <input type="checkbox" checked={selected.includes(e.id)} onChange={() => {}} />
                <span style={{ fontWeight: 600, minWidth: 56 }}>{e.name}</span>
                <span style={{ color: '#888' }}>{e.department || '-'} · {e.position || '-'}</span>
                <span style={{ flex: 1 }} />
                {e.employment_status && e.employment_status !== '재직' && (
                  <span style={{ fontSize: 11, color: '#e05a5a' }}>{e.employment_status}</span>
                )}
              </label>
            ))
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, ...btn('#eee', '#555') }}>취소</button>
          <button onClick={apply} disabled={saving} style={{ flex: 1, ...btn(saving ? '#aaa' : PRIMARY) }}>
            {saving ? '등록 중...' : `선택한 ${selected.length}명 등록`}
          </button>
        </div>
      </div>
    </div>
  );
}

// 명함등록 목록의 개별 행 수정 — 오탈자 교정, 디자인업체 전달 전 최종확인용.
function CardIssueEditModal({ issue, onClose, onSaved, showMsg }) {
  const [form, setForm] = useState({
    name: issue.name || '', department: issue.department || '', position: issue.position || '',
    phone: issue.phone || '', email: issue.email || '',
    company_name: issue.company_name || '', company_phone: issue.company_phone || '',
    company_address: issue.company_address || '', company_email: issue.company_email || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const save = () => {
    setSaving(true);
    API.put(`/company-info/card-issues/${issue.id}`, form)
      .then(() => onSaved())
      .catch(() => showMsg('수정 중 오류가 발생했습니다.', 'error'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>명함정보 수정</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>

        <div style={row2}>
          <div><label style={lbl}>이름</label><input style={inp} value={form.name} onChange={set('name')} /></div>
          <div><label style={lbl}>직급</label><input style={inp} value={form.position} onChange={set('position')} /></div>
        </div>
        <div style={row2}>
          <div><label style={lbl}>부서</label><input style={inp} value={form.department} onChange={set('department')} /></div>
          <div><label style={lbl}>연락처</label><input style={inp} value={form.phone} onChange={set('phone')} /></div>
        </div>
        <label style={lbl}>이메일</label>
        <input style={inp} value={form.email} onChange={set('email')} />

        <div style={section}>회사정보</div>
        <label style={lbl}>회사명</label>
        <input style={inp} value={form.company_name} onChange={set('company_name')} />
        <div style={row2}>
          <div><label style={lbl}>회사 전화</label><input style={inp} value={form.company_phone} onChange={set('company_phone')} /></div>
          <div><label style={lbl}>회사 이메일</label><input style={inp} value={form.company_email} onChange={set('company_email')} /></div>
        </div>
        <label style={lbl}>회사 주소</label>
        <input style={inp} value={form.company_address} onChange={set('company_address')} />

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button onClick={onClose} style={{ flex: 1, ...btn('#eee', '#555') }}>취소</button>
          <button onClick={save} disabled={saving} style={{ flex: 1, ...btn(saving ? '#aaa' : PRIMARY) }}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 5. 문서번호 — 2026-09(문서번호 화면 레이아웃 개편). 첨부 이미지("전사 단일 문서번호
// 체계") 기준으로 화면을 재구성한다: 상단 3개 번호미리보기 패널(양식번호/문서번호/기록·발생,
// 회사약어는 세 패널이 하나의 상태를 공유해 한 곳만 입력해도 자동 동기화) + 하단 대분류/
// 중분류 분류표(doc_reg_classes, 좌우 두 컬럼으로 절반씩 표시, 인라인 편집+행추가+삭제).
// "관리체계" 버튼/모달은 삭제되었고 "문서번호 등록" 버튼은 "적용"으로 라벨만 바뀌어 지금은
// 동작 없이 자리만 차지한다(안내문구: "우선 화면 레이아웃만 먼저 구성하고 최종 확정되면 적용").
const PRIMARY = '#185FA5';
const btn = (bg, color = '#fff') => ({ background: bg, color, border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' });

const docNumBoxStyle = {
  width: 64, textAlign: 'center', padding: '7px 4px', border: '1px solid #ddd', borderRadius: 6,
  fontFamily: 'monospace', fontWeight: 700, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

function NumFormatPanel({ title, fields }) {
  return (
    <div style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 14px', background: '#fff' }}>
      <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 12 }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
        {fields.map((f, i) => (
          <Fragment key={f.key}>
            {i > 0 && !f.noDash && <span style={{ padding: '0 2px 9px', color: '#aaa', fontWeight: 700 }}>-</span>}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10.5, color: '#888', marginBottom: 3, whiteSpace: 'nowrap' }}>{f.label}</div>
              {/* 요청사항: "대분류는 아래쪽 문서분류코드와 매칭하여 수정" — 대분류 필드는
                  자유입력이 아니라 문서분류코드 표에 등록된 대분류 중에서 선택하도록 한다. */}
              {f.type === 'select' ? (
                <select value={f.value} onChange={f.onChange} spellCheck={false}
                  style={{ ...docNumBoxStyle, width: f.width || 64, fontFamily: 'inherit', cursor: 'pointer' }}>
                  {(f.options || []).length === 0 && <option value="">-</option>}
                  {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input value={f.value} onChange={f.onChange} spellCheck={false} style={{ ...docNumBoxStyle, width: f.width || 64 }} />
              )}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// 요청사항 — "표 간격을 적절히 조절하여 글자 전체가 보이게 수정": 분류명/주요문서 칸은
// 내용이 길어 한 줄 input으로는 잘려 보이므로, 내용 길이에 맞춰 자동으로 높이가 늘어나는
// textarea로 교체한다(줄바꿈 허용). rowSpan 유무와 무관하게 각 행마다 독립적으로 크기 조절.
function AutoTextarea({ defaultValue, placeholder, onCommit, style }) {
  const fit = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  return (
    <textarea
      ref={fit}
      defaultValue={defaultValue}
      placeholder={placeholder}
      rows={1}
      spellCheck={false}
      onInput={(e) => fit(e.target)}
      onBlur={(e) => onCommit(e.target.value)}
      style={{
        ...style, resize: 'none', overflow: 'hidden', whiteSpace: 'pre-wrap',
        wordBreak: 'break-word', lineHeight: 1.5, display: 'block', fontFamily: 'inherit',
      }}
    />
  );
}

// 요청사항 — "대분류를 기준으로 중분류를 한번에 묶기": 같은 major_code를 가진 행들을
// 화면상 연속된 하나의 그룹으로 모아, 대분류 칸을 rowSpan으로 병합해 한 번만 표시한다.
// (원본 정렬상 떨어져 있어도 major_code 값이 같으면 하나의 그룹으로 합친다.)
function buildClassGroups(rows) {
  const map = new Map();
  const order = [];
  rows.forEach(r => {
    if (!map.has(r.major_code)) { map.set(r.major_code, []); order.push(r.major_code); }
    map.get(r.major_code).push(r);
  });
  // 대분류 2칸 분리(요청사항: "품질매뉴얼 M" 형태) — 대분류명(major_name)은 그룹의
  // 첫 행 값을 그룹 대표값으로 사용한다(같은 그룹 내 모든 행이 major_code처럼 공유).
  return order.map(code => {
    const groupRows = map.get(code);
    return { major_code: code, major_name: groupRows[0]?.major_name || '', rows: groupRows };
  });
}
// 좌/우 두 컬럼으로 나눌 때 그룹이 컬럼 경계에서 잘리지 않도록, 그룹 단위로만 절반을 나눈다.
function splitClassGroups(groups) {
  const total = groups.reduce((s, g) => s + g.rows.length, 0);
  const target = Math.ceil(total / 2);
  const left = []; const right = [];
  let count = 0;
  groups.forEach(g => {
    if (count < target) { left.push(g); count += g.rows.length; } else { right.push(g); }
  });
  return [left, right];
}

const classCellInput = { padding: '8px 8px', border: 'none', borderRadius: 0, fontSize: 12.5, outline: 'none', width: '100%', boxSizing: 'border-box', background: 'transparent' };
const classTh = { padding: '7px 6px', fontSize: 11.5, fontWeight: 700, color: '#888', textAlign: 'center', border: '1px solid #eee', background: '#fafafa' };
const classTd = { padding: 0, border: '1px solid #eee', verticalAlign: 'middle' };

// 요청사항 — "분류명의 칸 간격을 반으로 줄이고 주요문서 설명칸을 늘림": 분류명 칸을 좁게
// (기존엔 남은 폭을 그대로 차지했음), 주요문서 칸을 넓게 재배분한다.
function ClassTable({ groups, onUpdate, onDelete, onRenameMajor, onRenameMajorName }) {
  return (
    <table style={{ flex: 1, minWidth: 0, width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <colgroup>
        {/* 요청사항: "분류명의 간격을 조금 더 늘려줘 (한글 기준으로 3글자)" — 분류명 칸을
            한글 3자(약 5%p)만큼 더 넓히고, 그만큼 주요문서 칸에서 덜어낸다(합계는 유지). */}
        <col style={{ width: 70 }} /><col style={{ width: 42 }} /><col style={{ width: 60 }} /><col style={{ width: '18%' }} /><col style={{ width: '47%' }} /><col style={{ width: 34 }} />
      </colgroup>
      <thead>
        <tr>
          <th style={classTh} colSpan={2}>대분류</th>
          <th style={classTh}>중분류</th>
          <th style={classTh}>분류명</th>
          <th style={classTh}>주요문서</th>
          <th style={classTh}></th>
        </tr>
      </thead>
      <tbody>
        {groups.length === 0 ? (
          <tr><td colSpan={6} style={{ ...classTd, textAlign: 'center', color: '#aaa', fontSize: 13, padding: '12px 0' }}>등록된 분류가 없습니다.</td></tr>
        ) : groups.map(g => g.rows.map((r, idx) => (
          <tr key={r.id} style={{ background: '#fafafa' }}>
            {idx === 0 && (
              <>
                {/* 요청사항: "대분류 아래쪽 이니셜 칸을 2칸으로 나누고 앞쪽에 입력칸을 추가"
                    — 앞칸(대분류명, 예: 품질매뉴얼)과 뒷칸(이니셜, 예: M)을 함께 병합 표시 */}
                <td rowSpan={g.rows.length} style={{ ...classTd, textAlign: 'center', background: '#f3f5f7' }}>
                  <input defaultValue={g.major_name || ''} placeholder="-" spellCheck={false}
                    onBlur={e => {
                      const v = e.target.value.trim();
                      if (v !== (g.major_name || '')) onRenameMajorName(g.rows.map(x => x.id), v);
                    }}
                    style={{ ...classCellInput, textAlign: 'center' }} />
                </td>
                <td rowSpan={g.rows.length} style={{ ...classTd, textAlign: 'center', background: '#f3f5f7' }}>
                  <input defaultValue={g.major_code} placeholder="-" spellCheck={false}
                    onBlur={e => {
                      const v = e.target.value.trim();
                      if (v && v !== g.major_code) onRenameMajor(g.rows.map(x => x.id), v);
                    }}
                    style={{ ...classCellInput, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }} />
                </td>
              </>
            )}
            <td style={classTd}>
              <input defaultValue={r.minor_code} placeholder="-" spellCheck={false}
                onBlur={e => e.target.value.trim() !== r.minor_code && onUpdate(r.id, { minor_code: e.target.value.trim() })}
                style={{ ...classCellInput, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }} />
            </td>
            <td style={classTd}>
              <AutoTextarea defaultValue={r.name || ''} placeholder="-"
                onCommit={v => v !== (r.name || '') && onUpdate(r.id, { name: v })}
                style={classCellInput} />
            </td>
            <td style={classTd}>
              <AutoTextarea defaultValue={r.example || ''} placeholder="-"
                onCommit={v => v !== (r.example || '') && onUpdate(r.id, { example: v })}
                style={classCellInput} />
            </td>
            <td style={{ ...classTd, textAlign: 'center' }}>
              <button onClick={() => onDelete(r.id)} title="삭제"
                style={{ width: 24, height: 24, background: '#fff', color: '#e05a5a', border: '1px solid #e05a5a', borderRadius: 6, cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0 }}>×</button>
            </td>
          </tr>
        )))}
      </tbody>
    </table>
  );
}


// ═══════════════════════════════════════════════════════════════
// 문서양식관리 (2026-09, 15차 — 전자결재 화면에서 이 화면으로 이전됨)
// ═══════════════════════════════════════════════════════════════
// 아래 스타일 상수/공용 컴포넌트(C/Section/Modal/Field/input/th/td/useSort/SortTh/EmptyRow)는
// 전자결재(ElectronicApproval.js)에서 쓰던 것과 동일한 값을 그대로 옮겨왔다. 이 화면(환경설정)
// 에 이미 있는 PRIMARY/btn 과 값이 같아 btn은 재사용하고, 나머지는 이름이 겹치지 않아 그대로
// 옮겼다.
const C = { primary:'#185FA5', border:'#e5e7eb', bg:'#f8fafc', text:'#333', sub:'#888' };
const input = { padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' };
const th = { padding:'10px 14px', fontSize:14, fontWeight:600, color:'#666', background:'#f8fafc', textAlign:'center', whiteSpace:'nowrap', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 14px', fontSize:14, color:C.text, borderTop:'1px solid #f0f0f0', textAlign:'center' };

function Section({ title, action, children }) {
  return (
    <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
      {(title || action) && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:10 }}>
          <div style={{ fontSize:16, fontWeight:600 }}>{title}</div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}
function useSort(list, defaultKey = null) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  let sorted = list;
  if (sortKey) {
    sorted = [...list].sort((a, b) => {
      let va = a ? a[sortKey] : null, vb = b ? b[sortKey] : null;
      if (va === null || va === undefined) va = '';
      if (vb === null || vb === undefined) vb = '';
      let cmp;
      const na = Number(va), nb = Number(vb);
      if (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
      else cmp = String(va).localeCompare(String(vb), 'ko');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }
  return { sorted, sortKey, sortDir, toggleSort };
}
function SortTh({ label, sortKey, currentKey, currentDir, onSort, align }) {
  if (!sortKey) return <th style={{ ...th, textAlign: align || th.textAlign }}>{label}</th>;
  const active = currentKey === sortKey;
  return (
    <th style={{ ...th, textAlign: align || th.textAlign, cursor:'pointer', userSelect:'none', color: active ? C.primary : th.color }}
      title={active ? (currentDir === 'asc' ? '오름차순 (클릭시 내림차순)' : '내림차순 (클릭시 오름차순)') : '클릭하여 정렬'}
      onClick={() => onSort(sortKey)}>
      {label}
    </th>
  );
}
function EmptyRow({ span, text='표시할 문서가 없습니다.' }) {
  return <tr><td colSpan={span} style={{ ...td, textAlign:'center', color:'#aaa', padding:24 }}>{text}</td></tr>;
}
// 2026-09(사업자등록증 모달 세로높이 조정) 추가 — 요청사항 "사업자등록증 모달창 세로높이:
// 기존 577.6px -> 860px로 수정". 기존엔 width만 지정 가능했고 세로 높이는 항상 내용에 따라
// 자동(auto, 최대 86vh)이었다. 특정 모달만 세로 높이를 고정하고 싶을 때 쓸 수 있도록 선택적
// height prop을 추가한다(지정 안 하면 기존과 동일하게 auto).
function Modal({ title, onClose, children, width=520, height, headerExtra, maxWidth='92vw', fill=false }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:14, width, maxWidth, height: height || undefined, maxHeight:'86vh', overflowY: fill ? 'hidden' : 'auto',
        ...(fill ? { display:'flex', flexDirection:'column' } : {}) }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:'18px 22px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontSize:16, fontWeight:700, margin:0 }}>{title}</h3>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {headerExtra}
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#999', cursor:'pointer' }}>✕</button>
          </div>
        </div>
        <div style={{ padding:'16px 22px 22px', ...(fill ? { flex:1, minHeight:0, display:'flex', flexDirection:'column' } : {}) }}>{children}</div>
      </div>
    </div>
  );
}
// responseType:'blob' 요청이 실패하면 서버의 JSON 오류 본문이 Blob으로 들어온다 — 메시지를 꺼내 쓴다.
async function blobErrorMessage(err, fallback) {
  try {
    const d = err?.response?.data;
    if (d && typeof d.text === 'function') {
      const j = JSON.parse(await d.text());
      if (j && j.message) return j.message;
    } else if (d && d.message) return d.message;
  } catch (_) { /* ignore */ }
  return fallback;
}
function Field({ label, children }) {
  return <div style={{ marginBottom:12 }}><div style={{ fontSize:12, color:'#666', marginBottom:5, fontWeight:600 }}>{label}</div>{children}</div>;
}
// 양식관리 - 구분 드롭다운 선택지
const FORM_CATEGORIES = ['인사', '회계', '계약', '납품', '업무'];
// 결재라인 단계 수(3/4/5)별 라벨 조합 — 백엔드 approval.js의 APPROVAL_STEP_PRESETS와 동일하다.
const APPROVAL_STEP_PRESETS = {
  3: ['작성', '검토', '승인'],
  4: ['작성', '검토', '검토', '승인'],
  5: ['작성', '검토', '검토', '부서장', '승인'],
};

// 문서양식관리
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// 업로드 양식 빌더 — 회사에서 쓰던 실제 문서(docx/xlsx/hwp)를 업로드하면 그 표 구조를
// 그대로 인식해 편집 가능한 표로 보여주고, 관리자가 칸을 직접 다듬은 뒤 저장한다.
// 저장된 레이아웃은 1) 빈 서식 PDF 다운로드, 2) 전자결재 기안 작성 시 실제 문서 작성 폼으로
// 재사용된다(기안 작성 연동은 DraftTab에서 처리).
// ═══════════════════════════════════════════════════════════════
function blankCell() {
  return { rowSpan: 1, colSpan: 1, text: '', bold: false, align: 'left', fontSize: 10,
    isField: false, fieldKey: null, fieldType: 'text', merged: false, image: null,
    // 2026-09(엑셀 100% 동일 서식편집) — 배경색/글자색/글꼴/줄바꿈/세로정렬/테두리
    bgColor: null, textColor: null, fontFamily: null, wrapText: false, valign: 'middle', borders: null,
    italic: false, underline: false };
}

// 서식 도구 — 셀 테두리 아이콘(16x16). on=굵은 실선, off=옅은 점선.
function BorderIcon({ kind }) {
  const on = { top: false, right: false, bottom: false, left: false, cross: false };
  if (kind === 'all') { on.top = on.right = on.bottom = on.left = on.cross = true; }
  else if (kind === 'outer') { on.top = on.right = on.bottom = on.left = true; }
  else if (['top', 'right', 'bottom', 'left'].includes(kind)) { on[kind] = true; }
  const line = (key, x1, y1, x2, y2) => (
    <line key={key} x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={on[key === 'ch' || key === 'cv' ? 'cross' : key] ? '#222' : '#c4c4c4'}
      strokeWidth={on[key === 'ch' || key === 'cv' ? 'cross' : key] ? 1.8 : 1}
      strokeDasharray={on[key === 'ch' || key === 'cv' ? 'cross' : key] ? undefined : '1.5 1.5'} />
  );
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" style={{ display: 'block' }}>
      {line('cv', 8, 2, 8, 14)}{line('ch', 2, 8, 14, 8)}
      {line('top', 2, 2, 14, 2)}{line('bottom', 2, 14, 14, 14)}
      {line('left', 2, 2, 2, 14)}{line('right', 14, 2, 14, 14)}
    </svg>
  );
}
function blankRowClient(cols) {
  return { heightPx: null, cells: Array.from({ length: cols }, blankCell) };
}
function blankLayoutClient(rows = 5, cols = 4) {
  const w = +(100 / cols).toFixed(2);
  return {
    colWidthsPct: Array.from({ length: cols }, () => w),
    pages: [{ rows: Array.from({ length: rows }, () => blankRowClient(cols)) }],
  };
}
// 2026-09 후속 요청: "화면을 1페이지에 인쇄될 수 있도록 수정하고 2장으로 넘어갈 경우
// 2장부터는 동일한 양식으로 계속 작성될 수 있도록" — 이제 레이아웃이 여러 페이지
// ({ colWidthsPct, pages: [{rows}, ...] })를 가질 수 있다. 예전에 저장된 단일 페이지 형태
// ({ colWidthsPct, rows })도 그대로 열어서 편집할 수 있도록 정규화한다.
function normalizeLayoutClient(layout) {
  if (!layout) return blankLayoutClient();
  // 2026-09(환경설정-양식관리 개선) — 엑셀 원본에서 읽어온 실제 페이지 속성(pageSetup:
  // 용지방향/페이지나누기)이 있으면 정규화 과정에서 유실되지 않도록 그대로 들고 다닌다.
  if (Array.isArray(layout.pages) && layout.pages.length) return layout;
  return { colWidthsPct: layout.colWidthsPct || [100], colWidthsPx: layout.colWidthsPx || null, pages: [{ rows: layout.rows || [] }], pageSetup: layout.pageSetup || null };
}
// 한 페이지 분량(대략 A4 한 장 인쇄 가능 높이, px)을 넘으면 자동으로 다음 페이지를 만든다.
// 가로형은 용지 높이가 210mm라 한도가 더 작다.
const PAGE_HEIGHT_LIMIT_PX = 1000;
const PAGE_HEIGHT_LIMIT_LANDSCAPE_PX = 680;
const rowHeightOf = (row) => row.heightPx || 32;
const pageContentHeight = (rows) => (rows || []).reduce((s, r) => s + rowHeightOf(r), 0);
// "2장부터는 동일한 양식으로" — 기존 페이지의 칸 구조(병합/스타일/라벨 텍스트)는 그대로
// 복제하되, 입력값(isField 칸의 값)과 이미지는 비우고 fieldKey는 페이지 번호를 붙여
// 중복되지 않게 한다.
function clonePageAsTemplate(page, newPageIndex) {
  return {
    rows: (page.rows || []).map(row => ({
      heightPx: row.heightPx,
      cells: row.cells.map(cell => ({
        ...cell,
        text: cell.isField ? '' : cell.text,
        image: null,
        fieldKey: (cell.isField && cell.fieldKey) ? `${cell.fieldKey}_p${newPageIndex + 1}` : cell.fieldKey,
      })),
    })),
  };
}

// 엑셀처럼 표 위/왼쪽에 열문자(A,B,C...)·행번호(1,2,3...) 머리글을 붙이기 위한 헬퍼.
// 저장되는 레이아웃 JSON에는 포함되지 않는, 화면 표시 전용 라벨이다.
function colLetter(n) {
  let s = ''; n += 1;
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

// 2026-09 요청: "양식에서 서식수정을 할 때 엑셀화면과 같이 수정할 수 있게" + "이미지도
// 붙여넣기 할 수 있게" — 기존 셀 단위 편집기에 아래를 더해 엑셀에 가깝게 만든다.
//  1) 방향키/Tab/Enter로 셀 간 이동(텍스트 커서가 칸 경계에 있을 때만 좌우 이동, 위/아래는
//     항상 이동 — 입력 중인 텍스트 편집을 방해하지 않도록).
//  2) 열 머리(컬럼 경계) 드래그로 열 폭 조정, 각 행 첫 칸 우측하단 그립으로 행 높이 조정.
//  3) Shift+클릭으로 사각형 범위를 선택해 한 번에 병합(기존 "오른쪽과 병합"보다 엑셀에 가까움).
//  4) 셀을 선택한 뒤 이미지를 클립보드에서 Ctrl+V로 붙여넣으면 그 칸에 이미지로 삽입된다
//     (base64 데이터URL로 레이아웃 JSON에 그대로 저장 — 별도 업로드 라우트 불필요).
//  5) 셀 복사/붙여넣기, 내용지우기 버튼(엑셀의 Ctrl+C/V, Delete에 해당).
//  6) "결재란 삽입" — 담당/팀장/대표 같은 결재 구분 칸 + 서명 공간을 표 안에 바로 삽입.
//  7) 여러 페이지 지원 — 1페이지 인쇄 분량을 넘으면 자동으로(또는 버튼으로) 같은 양식의
//     새 페이지가 추가된다.
//  8) 다른 양식에서 이미 만들어둔 레이아웃을 그대로 불러와 이어서 편집할 수 있다.
/* eslint-disable no-unused-vars */
// 원본(엑셀 그대로) 화면에서 직접 고친 칸을 찾아 xEdit(고친 속성 목록)로 표시한다.
// 서버는 xEdit 가 있는 칸만 원본 xlsx 에 덮어쓰므로, 건드리지 않은 칸은 엑셀 서식이 그대로 유지된다.
const XEDIT_KEYS = ['text', 'bold', 'italic', 'underline', 'fontSize', 'fontFamily', 'textColor', 'bgColor', 'align', 'valign', 'wrapText', 'borders', 'image', 'imageBox'];
const xNv = (v) => ((v === undefined || v === null || v === '' || v === false) ? null : (typeof v === 'object' ? JSON.stringify(v) : v));
function flagXlsxEdits(layout, orig) {
  if (!layout || !Array.isArray(layout.pages)) return layout;
  return {
    ...layout,
    pages: layout.pages.map((pg, pi) => ({
      ...pg,
      rows: (pg.rows || []).map((row, ri) => ({
        ...row,
        cells: (row.cells || []).map((c, ci) => {
          if (c.merged || !c.address) return c;
          const o = orig?.pages?.[pi]?.rows?.[ri]?.cells?.[ci];
          const set = new Set(Array.isArray(c.xEdit) ? c.xEdit : []);
          if (o) XEDIT_KEYS.forEach((k) => { if (xNv(c[k]) !== xNv(o[k])) set.add(k); });
          return set.size ? { ...c, xEdit: Array.from(set) } : c;
        }),
      })),
    })),
  };
}
function xlsxEditSig(layout) {
  const out = [];
  (layout?.pages || []).forEach((pg) => (pg.rows || []).forEach((row) => (row.cells || []).forEach((c) => {
    if (Array.isArray(c.xEdit) && c.xEdit.length) out.push([c.address, c.xEdit, ...XEDIT_KEYS.map((k) => c[k])]);
  })));
  return JSON.stringify(out);
}

function FormLayoutEditorModal({ formId, formName, docNo, enactedDate, revNo, revDate, isRevision, onClose, showMsg }) {
  // 하단 Rev 표기 — 목록화면(FormsTab)의 revLabel()과 동일한 규칙("Rev01 2026-08-23" / 개정없으면 '-')
  const revLabelText = revNo ? `Rev${String(revNo).padStart(2, '0')}${revDate ? ' ' + revDate : ''}` : '-';
  // 2026-09(양식개정 버튼) — 목록의 "양식개정" 버튼으로 열렸을 때만 true. 이 모드로 저장하면
  // 서버가 rev_no를 +1 올리고 rev_date를 오늘로 갱신한다(단순 "서식편집"으로 표 구조만
  // 고칠 때는 개정으로 치지 않는다 — approval.js PATCH /forms/:id의 기존 규칙과 별개로,
  // 레이아웃 저장(PUT /forms/:id/layout)에도 개정 여부를 함께 실어 보낸다).
  const revNext = `Rev${String((revNo || 0) + 1).padStart(2, '0')}`;
  const [layout, setLayout] = useState(null); // { colWidthsPct, pages: [{rows}, ...] }
  const [currentPage, setCurrentPage] = useState(0);
  const [layoutSource, setLayoutSource] = useState('manual');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { r, c } — c는 해당 행의 cells 배열 인덱스
  const [editing, setEditing] = useState(null); // 엑셀 화면에서 더블클릭으로 직접 글자를 고치는 칸 { p, r, c }
  const [rangeEnd, setRangeEnd] = useState(null); // Shift+클릭으로 지정한 범위의 반대쪽 끝
  const [colResize, setColResize] = useState(null); // { index, startX, startWidths, tableWidth }
  const [rowResize, setRowResize] = useState(null); // { index, startY, startHeight }
  const tableWrapRef = useRef(null);
  const clipboardCellRef = useRef(null);
  // 2026-09(양식 자동인식) — 업로드한 실제 서식에서 자동으로 찾아낸 결재란(작성/검토/승인
  // 등)과 라벨-옆칸 필드(문서번호/작성일/작성자 등)를 화면에 안내하고, 저장 시 결재란은
  // approval_steps로 함께 저장한다.
  const detectedApprovalSteps = null; // 업로드 기능 제거 — 저장 payload 호환용
  // 2026-09(엑셀 원본 100% 반영) — 방금 업로드한 원본 파일(xlsx일 때만)을 들고 있다가 저장
  // 시점에 함께 서버로 보낸다. 서버는 이 원본 파일을 그대로 보관해 두고, 이후 PDF 생성 시
  // 재구성된 표가 아니라 이 원본 파일에 값만 채워 넣는 방식을 써서 서식을 100% 유지한다.
  const [uploadedTemplateFile, setUploadedTemplateFile] = useState(null);
  // 엑셀 원본 그대로 보기 — 서버가 원본 xlsx를 인쇄와 동일한 방식으로 렌더링한 페이지 이미지 + 입력란 위치
  const [viewMode, setViewMode] = useState('excel'); // 'excel' | 'table'
  const [renderData, setRenderData] = useState(null);
  const [renderLoading, setRenderLoading] = useState(false);
  const [renderErr, setRenderErr] = useState('');
  const [renderBusy, setRenderBusy] = useState(false);
  const origLayoutRef = useRef(null);   // 불러온/업로드 직후 레이아웃 — 이후 고친 칸을 찾는 기준
  const lastSigRef = useRef('');        // 마지막으로 그린 미리보기의 편집 서명
  const renderSeqRef = useRef(0);

  useEffect(() => {
    API.get(`/approval/forms/${formId}/layout`).then(r => {
      const _l = normalizeLayoutClient(r.data.layout);
      origLayoutRef.current = _l;
      lastSigRef.current = xlsxEditSig(_l);
      setLayout(_l);
      setLayoutSource(r.data.layout_source || 'manual');
      if (r.data.has_xlsx) {
        setRenderLoading(true);
        API.get(`/approval/forms/${formId}/layout/render`)
          .then(rr => { setRenderData(rr.data); setRenderErr(''); })
          .catch(e => setRenderErr(e.response?.data?.message || '원본 미리보기를 만들지 못했습니다.'))
          .finally(() => setRenderLoading(false));
      }
    }).catch(() => setLayout(blankLayoutClient()))
      .finally(() => setLoading(false));
    setCurrentPage(0);
  }, [formId]);

  // 원본(엑셀 그대로) 화면에서 고친 칸 표시(xEdit) + 편집 후 미리보기 자동 재생성(0.9초 지연)
  const flagged = useMemo(() => flagXlsxEdits(layout, origLayoutRef.current), [layout]);
  const hasRender = !!renderData;
  useEffect(() => {
    if (!hasRender || !flagged) return undefined;
    const sig = xlsxEditSig(flagged);
    if (sig === lastSigRef.current) return undefined;
    const timer = setTimeout(() => {
      lastSigRef.current = sig;
      const seq = ++renderSeqRef.current;
      setRenderBusy(true);
      let req;
      if (uploadedTemplateFile) {
        const fd = new FormData();
        fd.append('file', uploadedTemplateFile);
        fd.append('layout', JSON.stringify(flagged));
        fd.append('name', formName || '');
        req = API.post('/approval/forms/layout/render', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        req = API.post(`/approval/forms/${formId}/layout/render`, { layout: flagged });
      }
      req.then(rr => { if (seq === renderSeqRef.current) { setRenderData(rr.data); setRenderErr(''); } })
        .catch(e => { if (seq === renderSeqRef.current) showMsg(e.response?.data?.message || '미리보기를 갱신하지 못했습니다.'); })
        .finally(() => { if (seq === renderSeqRef.current) setRenderBusy(false); });
    }, 900);
    return () => clearTimeout(timer);
  }, [flagged, hasRender, formId, formName, uploadedTemplateFile]); // eslint-disable-line react-hooks/exhaustive-deps

  // 엑셀 주소(예: B3) → 레이아웃 칸 위치 { p, r, c }
  const addrMap = useMemo(() => {
    const m = {};
    (layout?.pages || []).forEach((pg, p) => (pg.rows || []).forEach((row, r) => (row.cells || []).forEach((cell, c) => {
      if (!cell.merged && cell.address) m[cell.address] = { p, r, c };
    })));
    return m;
  }, [layout]);

  const rows = layout ? layout.pages[currentPage].rows : [];

  const updateCell = (r, c, patch) => {
    setLayout(prev => ({
      ...prev,
      pages: prev.pages.map((page, pi) => {
        if (pi !== currentPage) return page;
        const rows = page.rows.map((row, ri) => {
          if (ri !== r) return row;
          const cells = row.cells.map((cell, ci) => ci === c ? { ...cell, ...patch } : cell);
          return { ...row, cells };
        });
        return { ...page, rows };
      }),
    }));
  };

  const addRow = () => {
    setLayout(prev => {
      const pages = prev.pages.map((page, pi) => pi !== currentPage ? page : {
        ...page, rows: [...page.rows, blankRowClient(prev.colWidthsPct.length)],
      });
      const isLastPage = currentPage === pages.length - 1;
      // 업로드한 원본(엑셀/워드/한글)에서 가져온 서식은 원본의 쪽 구성을 그대로 따르므로 자동 쪽추가 대상이 아니다.
      const limitPx = prev.pageSetup?.orientation === 'landscape' ? PAGE_HEIGHT_LIMIT_LANDSCAPE_PX : PAGE_HEIGHT_LIMIT_PX;
      if (isLastPage && layoutSource === 'manual' && pageContentHeight(pages[currentPage].rows) > limitPx) {
        const newPage = clonePageAsTemplate(pages[currentPage], pages.length);
        const nextIndex = pages.length;
        setTimeout(() => { showMsg('1페이지 인쇄 분량을 초과하여 같은 양식의 새 페이지가 자동으로 추가되었습니다.'); setCurrentPage(nextIndex); }, 0);
        return { ...prev, pages: [...pages, newPage] };
      }
      return { ...prev, pages };
    });
  };
  const removeLastRow = () => setLayout(prev => ({
    ...prev,
    pages: prev.pages.map((page, pi) => {
      if (pi !== currentPage || page.rows.length <= 1) return page;
      return { ...page, rows: page.rows.slice(0, -1) };
    }),
  }));
  const addColumn = () => setLayout(prev => {
    const newColCount = prev.colWidthsPct.length + 1;
    const colWidthsPct = Array.from({ length: newColCount }, () => +(100 / newColCount).toFixed(2));
    const pages = prev.pages.map(page => ({
      ...page,
      rows: page.rows.map(row => ({ ...row, cells: [...row.cells, blankCell()] })),
    }));
    return { colWidthsPct, pages, pageSetup: prev.pageSetup };
  });
  const removeLastColumn = () => setLayout(prev => {
    if (prev.colWidthsPct.length <= 1) return prev;
    for (const page of prev.pages) {
      for (const row of page.rows) {
        const last = row.cells[row.cells.length - 1];
        if (last && (last.colSpan > 1 || last.rowSpan > 1)) {
          alert('마지막 열이 다른 칸과 병합되어 있어 삭제할 수 없습니다. 먼저 병합을 해제해주세요.');
          return prev;
        }
      }
    }
    const newColCount = prev.colWidthsPct.length - 1;
    const colWidthsPct = Array.from({ length: newColCount }, () => +(100 / newColCount).toFixed(2));
    const pages = prev.pages.map(page => ({ ...page, rows: page.rows.map(row => ({ ...row, cells: row.cells.slice(0, -1) })) }));
    return { colWidthsPct, pages };
  });

  // "2장으로 넘어가는 경우 2장 양식을 추가로 생성" — 수동으로도 같은 양식의 새 페이지를
  // 추가할 수 있다(자동 추가와 동일한 템플릿 복제 로직 재사용).
  const addPage = () => {
    const srcPage = layout.pages[currentPage];
    const newPage = clonePageAsTemplate(srcPage, layout.pages.length);
    const nextIndex = layout.pages.length;
    setLayout(prev => ({ ...prev, pages: [...prev.pages, newPage] }));
    setSelected(null); setRangeEnd(null);
    setCurrentPage(nextIndex);
  };
  const removePage = (idx) => {
    if (layout.pages.length <= 1) { alert('최소 1페이지는 있어야 합니다.'); return; }
    if (!window.confirm(`${idx + 1}페이지를 삭제하시겠습니까?`)) return;
    setLayout(prev => ({ ...prev, pages: prev.pages.filter((_, i) => i !== idx) }));
    setSelected(null); setRangeEnd(null); setCurrentPage(0);
  };

  // 2026-10(서식관리 서식도구) — 선택한 범위(Shift+클릭으로 지정한 사각형, 없으면 한 칸) 전체에
  // 글자 서식/테두리를 한 번에 적용한다. 토글류(굵게/기울임/밑줄)는 기준 칸(처음 선택한 칸)의 현재 값을 뒤집어 범위 전체에 동일 적용.
  const patchRange = (fn) => {
    const rect = selectionRect();
    if (!rect) return;
    setLayout(prev => ({
      ...prev,
      pages: prev.pages.map((page, pi) => pi !== currentPage ? page : {
        ...page,
        rows: page.rows.map((row, ri) => (ri < rect.r1 || ri > rect.r2) ? row : {
          ...row,
          cells: row.cells.map((cell, ci) => (ci < rect.c1 || ci > rect.c2 || cell.merged) ? cell : { ...cell, ...fn(cell) }),
        }),
      }),
    }));
  };
  const anchorCell = () => (selected ? rows[selected.r]?.cells[selected.c] : null);
  const toggleBold = () => { const a = anchorCell(); if (!a) return; const v = !a.bold; patchRange(() => ({ bold: v })); };
  const toggleItalic = () => { const a = anchorCell(); if (!a) return; const v = !a.italic; patchRange(() => ({ italic: v })); };
  const toggleUnderline = () => { const a = anchorCell(); if (!a) return; const v = !a.underline; patchRange(() => ({ underline: v })); };
  const setFontSizeAll = (n) => { const v = Math.max(6, Math.min(48, Number(n) || 10)); patchRange(() => ({ fontSize: v })); };
  const setAlignAll = (al) => patchRange(() => ({ align: al }));
  const setValignAll = (v) => patchRange(() => ({ valign: v }));
  // 그림 크기 — imageBox: { left(%), top(px), width(%, 칸 폭 대비), height(px) }
  const IMG_DEFAULT_BOX = { left: 0, top: 0, width: 100, height: 60 };
  const setImageBox = (patch) => {
    if (!selected) return;
    const cell = rows[selected.r]?.cells[selected.c];
    if (!cell || !cell.image) return;
    const cur = cell.imageBox || IMG_DEFAULT_BOX;
    const next = { ...cur, ...patch };
    next.width = Math.max(1, Math.min(2000, Number(next.width) || 1));
    next.height = Math.max(4, Math.min(2000, Number(next.height) || 4));
    updateCell(selected.r, selected.c, { imageBox: next });
  };
  const scaleImage = (f) => {
    const cell = selected ? rows[selected.r]?.cells[selected.c] : null;
    if (!cell || !cell.image) return;
    const cur = cell.imageBox || IMG_DEFAULT_BOX;
    setImageBox({ width: +(cur.width * f).toFixed(2), height: Math.round(cur.height * f) });
  };
  // 셀 테두리 — mode: all(모든) / outer(바깥) / none(없음) / top·bottom·left·right(선택 범위의 해당 변, 이미 모두 있으면 해제)
  // 병합된 칸은 병합 범위 전체의 끝(rowSpan/colSpan)을 기준으로 바깥변을 판단한다.
  // borders가 null인 칸은 PDF 기본값(사방 얇은 검정선)과 같게 취급해서 시작한다.
  const BORDER_LINE = { style: 'thin', color: '#000000' };
  const applyBorder = (mode) => {
    const rect = selectionRect();
    if (!rect) return;
    const { r1, r2, c1, c2 } = rect;
    const full = () => ({ top: { ...BORDER_LINE }, right: { ...BORDER_LINE }, bottom: { ...BORDER_LINE }, left: { ...BORDER_LINE } });
    const baseOf = (cell) => ({ ...(cell.borders || full()) });
    // 단일 변 토글 여부 판정 — 해당 변 대상 칸이 전부 이미 선이 있으면 해제, 아니면 추가
    let removeSide = false;
    if (['top', 'bottom', 'left', 'right'].includes(mode)) {
      let all = true, any = false;
      rows.forEach((row, ri) => row.cells.forEach((cell, ci) => {
        if (ri < r1 || ri > r2 || ci < c1 || ci > c2 || cell.merged) return;
        const edge = mode === 'top' ? ri === r1
          : mode === 'bottom' ? (ri + (cell.rowSpan || 1) - 1) === r2
          : mode === 'left' ? ci === c1
          : (ci + (cell.colSpan || 1) - 1) === c2;
        if (!edge) return;
        any = true;
        if (!(cell.borders && cell.borders[mode])) all = false;
      }));
      removeSide = any && all;
    }
    setLayout(prev => ({
      ...prev,
      pages: prev.pages.map((page, pi) => pi !== currentPage ? page : {
        ...page,
        rows: page.rows.map((row, ri) => (ri < r1 || ri > r2) ? row : {
          ...row,
          cells: row.cells.map((cell, ci) => {
            if (ci < c1 || ci > c2 || cell.merged) return cell;
            const isTop = ri === r1, isLeft = ci === c1;
            const isBottom = (ri + (cell.rowSpan || 1) - 1) === r2, isRight = (ci + (cell.colSpan || 1) - 1) === c2;
            if (mode === 'all') return { ...cell, borders: full() };
            if (mode === 'none') return { ...cell, borders: { top: null, right: null, bottom: null, left: null } };
            const b = baseOf(cell);
            if (mode === 'outer') {
              if (isTop) b.top = { ...BORDER_LINE };
              if (isBottom) b.bottom = { ...BORDER_LINE };
              if (isLeft) b.left = { ...BORDER_LINE };
              if (isRight) b.right = { ...BORDER_LINE };
              return { ...cell, borders: b };
            }
            const edge = mode === 'top' ? isTop : mode === 'bottom' ? isBottom : mode === 'left' ? isLeft : isRight;
            if (!edge) return cell;
            b[mode] = removeSide ? null : { ...BORDER_LINE };
            return { ...cell, borders: b };
          }),
        }),
      }),
    }));
  };
  const cycleAlign = () => {
    if (!selected) return;
    const cell = rows[selected.r].cells[selected.c];
    const next = { left: 'center', center: 'right', right: 'left' }[cell.align] || 'left';
    updateCell(selected.r, selected.c, { align: next });
  };
  const bumpFontSize = (delta) => {
    if (!selected) return;
    const cell = rows[selected.r].cells[selected.c];
    updateCell(selected.r, selected.c, { fontSize: Math.max(6, Math.min(24, (cell.fontSize || 10) + delta)) });
  };
  // 2026-09(엑셀 100% 동일 서식편집) — 배경색/글자색/글꼴/줄바꿈/세로정렬 편집 컨트롤.
  const setBgColor = (hex) => patchRange(() => ({ bgColor: hex || null }));
  const setTextColor = (hex) => patchRange(() => ({ textColor: hex || null }));
  const setFontFamily = (name) => patchRange(() => ({ fontFamily: name || null }));
  const toggleWrapText = () => {
    if (!selected) return;
    const cell = rows[selected.r].cells[selected.c];
    updateCell(selected.r, selected.c, { wrapText: !cell.wrapText });
  };
  const cycleValign = () => {
    if (!selected) return;
    const cell = rows[selected.r].cells[selected.c];
    const next = { top: 'middle', middle: 'bottom', bottom: 'top' }[cell.valign || 'middle'] || 'middle';
    updateCell(selected.r, selected.c, { valign: next });
  };
  const FONT_CHOICES = ['맑은 고딕', '나눔고딕', '바탕', '돋움', '굴림', 'Arial', 'Times New Roman'];
  // 자동입력 ID 9종(백엔드 utils/approvalAutoFields.js와 동일) — 시스템이 자동으로 채우는 값
  const AUTO_FIELD_CHOICES = [
    ['year', '년'], ['month', '월'], ['day', '일'], ['ymd_dot', '년월일 (년.월.일)'],
    ['form_no', '양식번호'], ['company_name', '회사명'], ['form_spec', '양식규격'],
    ['doc_no', '문서번호'], ['printer', '출력자'],
    ['hq_address', '본사주소'], ['factory_address', '공장주소'],
  ];
  // 출고증/거래명세서/견적서 연동 전용 입력란 ID (백엔드 utils/docLinkFields.js와 동일).
  // 같은 이름("출고증"/"거래명세서"/"견적서")으로 등록된 양식의 해당 칸에 시스템 값이 자동으로 채워진다.
  const LINK_HEADER_CHOICES = [
    ['customer', '고객사'], ['customer_manager', '고객측 담당자'], ['customer_phone', '고객측 연락처'],
    ['our_manager', '공급자측 담당자'], ['our_phone', '공급자측 연락처'], ['doc_date', '일자(등록/공급/견적일)'],
    ['pay_method', '결재방법'], ['destination', '배송지/인도장소'], ['note', '비고'],
    ['total_qty', '수량 합계'], ['total_supply', '공급가액 합계'], ['total_tax', '세액 합계'], ['total_amount', '합계금액'],
  ];
  const LINK_RECEIPT_CHOICES = [
    ['reg_by', '등록자'], ['ship_date', '출고일자'], ['complete_date', '출고완료일자'],
    ['complete_by', '출고완료자'], ['ship_place', '출고장소'], ['ship_method', '출고방법'],
  ];
  const [fieldPicking, setFieldPicking] = useState(false);
  const toggleField = () => {
    if (!selected) return;
    const cell = rows[selected.r].cells[selected.c];
    if (cell.isField) { updateCell(selected.r, selected.c, { isField: false, fieldKey: null }); return; }
    setFieldPicking(true);
  };
  const applyFieldPick = (val) => {
    setFieldPicking(false);
    if (!selected || !val) return;
    let key = val;
    if (val === '__custom') {
      const cell = rows[selected.r].cells[selected.c];
      key = window.prompt('입력값을 구분할 키(fieldKey)를 입력해주세요. (자동입력이 아닌 직접 입력란)', (cell.text || '').trim() || `field${selected.r}_${selected.c}`);
      if (!key) return;
    }
    updateCell(selected.r, selected.c, { isField: true, fieldKey: key });
  };
  // 선택 범위(선택한 칸 ~ Shift+클릭한 칸)의 사각형 좌표를 계산한다.
  const selectionRect = () => {
    if (!selected) return null;
    const end = rangeEnd || selected;
    return {
      r1: Math.min(selected.r, end.r), r2: Math.max(selected.r, end.r),
      c1: Math.min(selected.c, end.c), c2: Math.max(selected.c, end.c),
    };
  };
  const inRange = (r, c) => {
    const rect = selectionRect();
    return !!rect && r >= rect.r1 && r <= rect.r2 && c >= rect.c1 && c <= rect.c2;
  };
  // 엑셀처럼: Shift+클릭으로 지정한 사각형 범위를 한 번에 병합한다(가로/세로 모두 지원).
  const mergeSelection = () => {
    const rect = selectionRect();
    if (!rect) return;
    const { r1, r2, c1, c2 } = rect;
    if (r1 === r2 && c1 === c2) { alert('Shift+클릭으로 병합할 범위를 먼저 지정해주세요.'); return; }
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        const cell = rows[r]?.cells[c];
        if (!cell || cell.merged || cell.rowSpan > 1 || cell.colSpan > 1) {
          alert('이미 병합된 칸이 포함되어 있습니다. 먼저 병합을 해제한 뒤 다시 시도해주세요.');
          return;
        }
      }
    }
    setLayout(prev => ({
      ...prev,
      pages: prev.pages.map((page, pi) => {
        if (pi !== currentPage) return page;
        const rows = page.rows.map((row, ri) => {
          if (ri < r1 || ri > r2) return row;
          const cells = row.cells.map((cell, ci) => {
            if (ci < c1 || ci > c2) return cell;
            if (ri === r1 && ci === c1) return { ...cell, rowSpan: r2 - r1 + 1, colSpan: c2 - c1 + 1 };
            return { ...cell, merged: true };
          });
          return { ...row, cells };
        });
        return { ...page, rows };
      }),
    }));
    setSelected({ r: r1, c: c1 });
    setRangeEnd(null);
  };
  // 병합 해제 — colSpan/rowSpan 모두 원래 크기(1×1)로 되돌린다.
  const splitCell = () => {
    if (!selected) return;
    const { r, c } = selected;
    const cell = rows[r].cells[c];
    if (cell.colSpan <= 1 && cell.rowSpan <= 1) { alert('병합된 칸이 아닙니다.'); return; }
    setLayout(prev => ({
      ...prev,
      pages: prev.pages.map((page, pi) => {
        if (pi !== currentPage) return page;
        const pageRows = page.rows.map((row, ri) => {
          if (ri < r || ri >= r + cell.rowSpan) return row;
          const cells = row.cells.map((cc, ci) => {
            if (ci < c || ci >= c + cell.colSpan) return cc;
            if (ri === r && ci === c) return { ...cc, rowSpan: 1, colSpan: 1 };
            return blankCell();
          });
          return { ...row, cells };
        });
        return { ...page, rows: pageRows };
      }),
    }));
  };
  // 엑셀의 Ctrl+C/V에 해당 — 텍스트/스타일/이미지를 다른 칸에 그대로 복사한다
  // (입력란 지정/병합 상태는 복사하지 않아 fieldKey 중복을 막는다).
  const copyCell = () => {
    if (!selected) return;
    clipboardCellRef.current = rows[selected.r].cells[selected.c];
    showMsg('셀을 복사했습니다. 붙여넣을 칸을 선택한 뒤 "붙여넣기"를 누르세요.');
  };
  const pasteCell = () => {
    if (!selected || !clipboardCellRef.current) return;
    const src = clipboardCellRef.current;
    updateCell(selected.r, selected.c, {
      text: src.text, bold: src.bold, align: src.align, fontSize: src.fontSize, image: src.image || null, imageBox: src.imageBox || null, textBoxes: src.textBoxes || null,
      bgColor: src.bgColor || null, textColor: src.textColor || null, fontFamily: src.fontFamily || null,
      wrapText: !!src.wrapText, valign: src.valign || 'middle', borders: src.borders || null,
      italic: !!src.italic, underline: !!src.underline,
    });
  };
  // 엑셀의 Delete에 해당 — 선택 범위(또는 선택한 한 칸)의 내용/이미지를 비운다.
  const clearSelection = () => {
    const rect = selectionRect();
    if (!rect) return;
    const { r1, r2, c1, c2 } = rect;
    setLayout(prev => ({
      ...prev,
      pages: prev.pages.map((page, pi) => {
        if (pi !== currentPage) return page;
        const pageRows = page.rows.map((row, ri) => {
          if (ri < r1 || ri > r2) return row;
          const cells = row.cells.map((cell, ci) => (ci < c1 || ci > c2) ? cell : { ...cell, text: '', image: null });
          return { ...row, cells };
        });
        return { ...page, rows: pageRows };
      }),
    }));
  };
  // "결재라인을 추가할 경우 회의록 구성과 유사하게 좌측에는 양식이름, 우측에는 결재라인을
  // 추가"(요청사항) — 예전에는 결재 구분 칸을 표 안에 행으로 삽입했지만, 이제는 업무관리의
  // 회의록 미리보기(Tasks.js "제목 + 결재란" 영역)와 동일하게 A4 프레임 상단에 좌측
  // 양식이름 / 우측 결재란을 페이지 레벨로 배치한다. 결재라인 구성(담당/팀장/대표 등)은
  // 표 데이터와 별도로 layout.approvalLine 배열에 저장한다.
  const insertApprovalBox = () => {
    const input = window.prompt('결재라인에 넣을 구분을 콤마(,)로 입력해주세요 (예: 담당,팀장,대표)', (layout.approvalLine || ['담당', '팀장', '대표']).join(','));
    if (!input) return;
    const labels = input.split(',').map(s => s.trim()).filter(Boolean);
    if (!labels.length) return;
    setLayout(prev => ({ ...prev, approvalLine: labels }));
  };
  const removeApprovalBox = () => {
    if (!window.confirm('결재라인을 삭제하시겠습니까?')) return;
    setLayout(prev => ({ ...prev, approvalLine: null }));
  };
  // 방향키/Tab/Enter로 셀 이동 — 병합되어 숨겨진 칸(merged:true)은 건너뛴다.
  const moveSelection = (dr, dc) => {
    if (!selected) return;
    let { r, c } = selected;
    const maxR = rows.length - 1, maxC = layout.colWidthsPct.length - 1;
    let nr = r, nc = c;
    do {
      nr = Math.max(0, Math.min(maxR, nr + dr));
      nc = Math.max(0, Math.min(maxC, nc + dc));
      if (nr === r && nc === c) break; // 더 이동할 수 없음(경계)
      r = nr; c = nc;
    } while (rows[nr]?.cells[nc]?.merged);
    setSelected({ r: nr, c: nc });
    setRangeEnd(null);
    requestAnimationFrame(() => {
      const el = tableWrapRef.current && tableWrapRef.current.querySelector(`[data-cell="${nr}-${nc}"]`);
      if (el) el.focus();
    });
  };
  const handleCellKeyDown = (e) => {
    if (e.key === 'Tab') { e.preventDefault(); moveSelection(0, e.shiftKey ? -1 : 1); return; }
    if (e.key === 'Enter') {
      if (e.altKey && e.target.tagName === 'TEXTAREA') return; // Alt+Enter: 칸 안 줄바꿈
      e.preventDefault(); moveSelection(1, 0); return;
    }
    if (e.key === 'ArrowUp') { e.preventDefault(); moveSelection(-1, 0); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); moveSelection(1, 0); return; }
    if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) { e.preventDefault(); moveSelection(0, -1); return; }
    if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length) { e.preventDefault(); moveSelection(0, 1); return; }
  };
  // 이미지 붙여넣기 — 선택된 칸에 포커스된 입력창에서 Ctrl+V 하면 클립보드의 이미지를
  // base64 데이터URL로 변환해 그 칸에 그대로 저장한다(텍스트는 비워짐).
  // 이미지 파일 → 칸에 삽입(원본 비율 유지, 칸 폭에 맞춘 기본 크기)
  const insertImageFile = (file, r, c, opts) => {
    if (!file || r == null || c == null) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      const img = new Image();
      img.onload = () => {
        const nw = img.naturalWidth || 200, nh = img.naturalHeight || 60;
        const w = 70; // 칸(병합 포함) 폭 대비 %
        // 병합된 칸이면 병합 폭 전체를 기준으로 실제 표시 폭(px)을 계산해 원본 비율을 유지한다.
        const cellObj = rows?.[r]?.cells?.[c];
        const span = (cellObj && cellObj.colSpan) || 1;
        const pcts = layout?.colWidthsPct || [];
        const pxs = layout?.colWidthsPx;
        const spanPct = pcts.slice(c, c + span).reduce((x, y) => x + y, 0) || 10;
        let cellPx;
        if (Array.isArray(pxs) && pxs.length === pcts.length) cellPx = pxs.slice(c, c + span).reduce((x, y) => x + y, 0);
        else cellPx = Math.max(60, (tableWrapRef.current ? tableWrapRef.current.getBoundingClientRect().width : 700) * spanPct / 100);
        if (opts && opts.fitPct) {
          // 로고: 칸(병합 포함) 면적의 pct% 안에 원본 비율로 맞추고 가로·세로 가운데 정렬
          const rspan = (cellObj && cellObj.rowSpan) || 1;
          let cellH = 0;
          for (let k = 0; k < rspan; k++) cellH += (rows?.[r + k]?.heightPx) || 32;
          const f = Math.max(1, Math.min(100, Number(opts.fitPct) || 90)) / 100;
          const maxW = cellPx * f, maxH = cellH * f;
          const sc = Math.min(maxW / nw, maxH / nh);
          const lw = nw * sc, lh = Math.max(4, nh * sc);
          updateCell(r, c, { image: src, text: '', logoType: opts.logoType || null, logoPct: Math.round(f * 100),
            imageBox: { left: +((100 - lw / cellPx * 100) / 2).toFixed(2), top: Math.max(0, Math.round((cellH - lh) / 2)), width: +(lw / cellPx * 100).toFixed(2), height: Math.round(lh) } });
          return;
        }
        const pxW = cellPx * w / 100;
        const h = Math.max(10, Math.round(pxW * nh / nw));
        updateCell(r, c, { image: src, text: '', imageBox: { left: (100 - w) / 2, top: 0, width: w, height: h } });
      };
      img.onerror = () => updateCell(r, c, { image: src, text: '' });
      img.src = src;
    };
    reader.readAsDataURL(file);
  };
  // 클립보드에서 이미지 추출 — 엑셀/한글 등에서 복사한 그림은 items(image/*), files, 또는 html 안의 data URL 로 올 수 있다.
  const getClipboardImage = (cd) => {
    if (!cd) return null;
    const items = cd.items || [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file' && items[i].type && items[i].type.startsWith('image/')) { const f = items[i].getAsFile(); if (f) return f; }
    }
    const files = cd.files || [];
    for (let i = 0; i < files.length; i++) if (files[i].type && files[i].type.startsWith('image/')) return files[i];
    try {
      const html = cd.getData && cd.getData('text/html');
      const m = html && /<img[^>]+src=["'](data:image\/[^"']+)["']/i.exec(html);
      if (m) {
        const [head, b64] = m[1].split(',');
        const mime = /data:([^;]+)/.exec(head)[1];
        const bin = atob(b64); const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        return new Blob([arr], { type: mime });
      }
    } catch (_) { /* noop */ }
    return null;
  };
  const handleCellPaste = (e, r, c) => {
    const imageFile = getClipboardImage(e.clipboardData);
    if (!imageFile) return; // 이미지가 아니면 기본 텍스트 붙여넣기 동작 유지
    e.preventDefault();
    e.stopPropagation();
    insertImageFile(imageFile, r, c);
  };
  // 칸이 선택만 된 상태(입력창 포커스 없음)나 이미 그림이 있는 칸에서도 Ctrl+V 로 이미지 붙여넣기
  const pasteStateRef = useRef({});
  pasteStateRef.current = { selected, insertImageFile, getClipboardImage };
  useEffect(() => {
    const onDocPaste = (e) => {
      const st = pasteStateRef.current;
      if (!st.selected || e.defaultPrevented) return;
      const f = st.getClipboardImage(e.clipboardData);
      if (!f) return;
      e.preventDefault();
      st.insertImageFile(f, st.selected.r, st.selected.c);
    };
    document.addEventListener('paste', onDocPaste);
    return () => document.removeEventListener('paste', onDocPaste);
  }, []);
  const imgFileInputRef = useRef(null);
  const [logoPct, setLogoPct] = useState(90);
  const insertCompanyLogo = async (kind) => {
    if (!selected) { alert('로고를 넣을 칸을 먼저 선택하세요.'); return; }
    try {
      const r = await API.get('/company-info/logos');
      const list = r.data || [];
      const order = kind === 'v' ? ['kr_v', 'en_v'] : ['kr_h', 'en_h'];
      const pick = order.map(t => list.find(l => l.logo_type === t)).find(Boolean);
      if (!pick) { alert(`등록된 ${kind === 'v' ? '세로형' : '가로형'} 로고가 없습니다. 환경설정 > 로고관리에서 먼저 등록하세요.`); return; }
      const resp = await fetch(`${API.defaults.baseURL}/../uploads/company_logos/${pick.file_name}`, { credentials: 'include' });
      const blob = await resp.blob();
      insertImageFile(blob, selected.r, selected.c, { fitPct: logoPct, logoType: kind === 'v' ? 'v' : 'h' });
    } catch (e) { alert('회사 로고를 불러오지 못했습니다.'); }
  };

  // 열 폭 드래그 조정 — 컬럼 경계를 잡고 좌우로 끌면 양쪽 열 폭(%)이 함께 바뀐다.
  const startColResize = (e, i) => {
    e.preventDefault();
    const tableWidth = tableWrapRef.current ? tableWrapRef.current.getBoundingClientRect().width : 1;
    setColResize({ index: i, startX: e.clientX, startWidths: [...layout.colWidthsPct], tableWidth });
  };
  useEffect(() => {
    if (!colResize) return;
    const onMove = (e) => {
      const deltaPct = ((e.clientX - colResize.startX) / colResize.tableWidth) * 100;
      const widths = [...colResize.startWidths];
      const i = colResize.index;
      const minW = 3;
      let newLeft = widths[i] + deltaPct;
      let newRight = widths[i + 1] - deltaPct;
      if (newLeft < minW) { newRight -= (minW - newLeft); newLeft = minW; }
      if (newRight < minW) { newLeft -= (minW - newRight); newRight = minW; }
      widths[i] = +newLeft.toFixed(2); widths[i + 1] = +newRight.toFixed(2);
      setLayout(prev => {
        const next = { ...prev, colWidthsPct: widths };
        // 엑셀 px 열폭을 쓰는 서식이면 같은 비율로 px도 갱신(표 전체 폭은 유지)
        if (Array.isArray(prev.colWidthsPx) && prev.colWidthsPx.length === widths.length) {
          const total = prev.colWidthsPx.reduce((a, b) => a + b, 0);
          next.colWidthsPx = widths.map(w => Math.max(8, Math.round(total * w / 100)));
        }
        return next;
      });
    };
    const onUp = () => setColResize(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [colResize]);

  // 행 높이 드래그 조정 — 각 행 첫 칸의 우측하단 그립을 위아래로 끌면 그 행의 높이가 바뀐다.
  const startRowResize = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    const row = rows[i];
    setRowResize({ index: i, startY: e.clientY, startHeight: row.heightPx || 32 });
  };
  useEffect(() => {
    if (!rowResize) return;
    const onMove = (e) => {
      const newHeight = Math.max(20, rowResize.startHeight + (e.clientY - rowResize.startY));
      setLayout(prev => ({
        ...prev,
        pages: prev.pages.map((page, pi) => pi !== currentPage ? page : {
          ...page,
          rows: page.rows.map((row, ri) => ri === rowResize.index ? { ...row, heightPx: Math.round(newHeight) } : row),
        }),
      }));
    };
    const onUp = () => setRowResize(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [rowResize]);

  // 2026-09 요청: "행과 열의 셀 이름(A,B,C,1,2,3)을 마우스 우측클릭을 하면 행높이를
  // 변경할 수 있도록" — 기존 드래그(⋰ 그립) 방식은 유지하되, 좌측 행번호(1,2,3) 머리글을
  // 우클릭하면 그 행의 높이를 숫자로 직접 입력해 정확히 지정할 수 있게 한다. 상단 열문자
  // (A,B,C) 머리글의 우클릭도 대응되도록 함께 지원 — 이쪽은 그 열의 폭(%)을 입력받는다.
  const promptRowHeight = (e, r) => {
    e.preventDefault();
    const row = rows[r];
    const current = row?.heightPx || 32;
    const input = window.prompt(`${r + 1}행의 높이(px)를 입력하세요.`, String(current));
    if (input === null) return;
    const val = Math.max(20, parseInt(input, 10) || current);
    setLayout(prev => ({
      ...prev,
      pages: prev.pages.map((page, pi) => pi !== currentPage ? page : {
        ...page,
        rows: page.rows.map((rw, ri) => ri === r ? { ...rw, heightPx: val } : rw),
      }),
    }));
  };
  const promptColWidth = (e, i) => {
    e.preventDefault();
    const current = layout.colWidthsPct[i];
    const input = window.prompt(`${colLetter(i)}열의 폭(%)을 입력하세요. (전체 100% 기준)`, String(current));
    if (input === null) return;
    const raw = parseFloat(input);
    if (!raw || raw <= 0) return;
    setLayout(prev => {
      const widths = [...prev.colWidthsPct];
      const minW = 3;
      const newVal = Math.max(minW, raw);
      const diff = newVal - widths[i];
      // 나머지 열들에서 폭 차이를 비례 배분해 전체 합이 100%를 유지하도록 한다.
      const others = widths.map((w, idx) => idx).filter(idx => idx !== i);
      const othersSum = others.reduce((s, idx) => s + widths[idx], 0) || 1;
      widths[i] = +newVal.toFixed(2);
      others.forEach(idx => {
        const share = widths[idx] / othersSum;
        widths[idx] = +Math.max(minW, widths[idx] - diff * share).toFixed(2);
      });
      const next = { ...prev, colWidthsPct: widths };
      if (Array.isArray(prev.colWidthsPx) && prev.colWidthsPx.length === widths.length) {
        const total = prev.colWidthsPx.reduce((a, b) => a + b, 0);
        next.colWidthsPx = widths.map(w => Math.max(8, Math.round(total * w / 100)));
      }
      return next;
    });
  };

  // 열 경계선 위치(%) — 컬럼 폭 조정 드래그바를 그 위치에 겹쳐 그린다.
  const colBoundaries = (() => {
    const arr = []; let cum = 0;
    for (let i = 0; i < (layout?.colWidthsPct.length || 0) - 1; i++) {
      cum += layout.colWidthsPct[i];
      arr.push(cum);
    }
    return arr;
  })();

  const save = () => {
    // 2026-09(엑셀 원본 100% 반영) — 방금 업로드했던 원본 xlsx 파일이 아직 남아 있으면
    // multipart로 함께 전송해 서버가 원본 파일 자체를 보관하게 한다(이후 PDF는 이 원본에
    // 값만 채워서 만들어지므로 서식이 100% 그대로 유지된다). 원본 파일이 없는 일반 저장은
    // 기존과 동일하게 JSON으로 보낸다.
    const layoutToSave = flagged || layout; // 원본 화면에서 직접 고친 칸 표시(xEdit) 포함
    const req = uploadedTemplateFile
      ? (() => {
          const fd = new FormData();
          fd.append('layout', JSON.stringify(layoutToSave));
          fd.append('layout_source', layoutSource);
          if (detectedApprovalSteps) fd.append('approval_steps', JSON.stringify(detectedApprovalSteps));
          fd.append('template_file', uploadedTemplateFile);
          // 2026-09(docx 원본 100% 반영) — 원본 파일 확장자를 함께 보내 서버가 xlsx/docx 중
          // 어느 컬럼(template_xlsx_path/template_docx_path)에 저장할지 판단하게 한다.
          fd.append('template_ext', (uploadedTemplateFile.name.split('.').pop() || '').toLowerCase());
          if (isRevision) fd.append('is_revision', '1');
          return API.put(`/approval/forms/${formId}/layout`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        })()
      : API.put(`/approval/forms/${formId}/layout`, {
          layout: layoutToSave, layout_source: layoutSource,
          approval_steps: detectedApprovalSteps || undefined,
          is_revision: isRevision || undefined,
        });
    req
      .then(() => {
        showMsg(isRevision ? `양식이 개정되었습니다. (${revNext})` : '양식 레이아웃이 저장되었습니다.');
        setUploadedTemplateFile(null);
        onClose();
      })
      .catch(err => alert(err.response?.data?.message || '저장 실패'));
  };
  const downloadBlankPdf = () => {
    API.get(`/approval/forms/${formId}/layout/pdf`, { responseType: 'blob' })
      .then(res => {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = blobUrl; a.download = `${formName}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(async err => alert(await blobErrorMessage(err, '먼저 레이아웃을 저장해주세요.')));
  };

  const selectedCell = selected ? rows?.[selected.r]?.cells?.[selected.c] : null;
  const isLandscapeForm = layout?.pageSetup?.orientation === 'landscape';
  // 업로드 원본에서 가져온 서식(엑셀 한 장 등)은 원본 그대로이므로 "1페이지 초과" 경고를 내지 않는다.
  const overflowWarning = layout && layoutSource === 'manual'
    && pageContentHeight(rows) > (isLandscapeForm ? PAGE_HEIGHT_LIMIT_LANDSCAPE_PX : PAGE_HEIGHT_LIMIT_PX);
  const usePxCols = !!(layout && Array.isArray(layout.colWidthsPx) && layout.colWidthsPx.length === layout.colWidthsPct.length);
  const tablePxWidth = usePxCols ? layout.colWidthsPx.reduce((a, b) => a + b, 0) : null;
  // 용지 프레임 폭(px) — 표가 용지보다 넓으면 표 + 좌우 여백(15mm×2)만큼 넓혀 오른쪽이 잘리지 않게 한다.
  const MM = 96 / 25.4;
  const pagePx = Math.round((isLandscapeForm ? 297 : 210) * MM);
  const frameWidthPx = usePxCols ? Math.max(pagePx, Math.round(tablePxWidth + 30 * MM) + 4) : pagePx;
  const modalWidthPx = Math.max(isLandscapeForm ? 1240 : 1020, frameWidthPx + 44 + 24);

  return (
    <Modal title={(
      <span style={{ display:'inline-flex', flexWrap:'wrap', alignItems:'center', gap:10 }}>
        <span>{isRevision ? `🔄 양식개정 — ${formName} (${revLabelText} → ${revNext})` : `📐 서식 편집 — ${formName}`}</span>
        {layout?.pages && (
          <span style={{ display:'inline-flex', flexWrap:'wrap', alignItems:'center', gap:6, fontWeight:400 }}>
            {layout.pages.map((_, i) => (
              <span key={i} style={{ display:'inline-flex', alignItems:'center' }}>
                <button onClick={() => { setCurrentPage(i); setSelected(null); setRangeEnd(null); }}
                  style={{ ...btn(i===currentPage?C.primary:'#fff', i===currentPage?'#fff':C.primary),
                    border:`1px solid ${C.border}`, padding:'4px 10px', fontSize:12 }}>
                  {i+1}페이지
                </button>
                {layout.pages.length > 1 && (
                  <button onClick={() => removePage(i)} title="이 페이지 삭제"
                    style={{ background:'none', border:'none', color:'#dc2626', fontSize:13, cursor:'pointer', marginLeft:2, padding:'0 4px' }}>✕</button>
                )}
              </span>
            ))}
            <button onClick={addPage} style={{ ...btn('#fff', '#16a34a'), border:'1px dashed #16a34a', padding:'4px 10px', fontSize:12 }}>+ 페이지 추가(동일양식)</button>
          </span>
        )}
      </span>
    )} onClose={onClose} width={modalWidthPx} maxWidth="97vw">
      {loading ? <div style={{ padding:20, textAlign:'center', color:'#888' }}>불러오는 중...</div> : (
        <>

          {/* 서식 도구(2026-10) — 입력란 지정 바로 위. 칸을 선택(Shift+클릭으로 범위 지정)한 뒤 사용한다.
              1줄: 셀 테두리 아이콘 / 2줄: 글자체·크기·굵게·기울임·밑줄·색·정렬 */}
          {(() => {
            const off = !selected;
            const a = selectedCell || {};
            const ib = (active) => ({
              width: 30, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${active ? C.primary : C.border}`, borderRadius: 6, cursor: off ? 'not-allowed' : 'pointer',
              background: active ? '#e0f2fe' : '#fff', opacity: off ? 0.4 : 1, padding: 0, fontSize: 13, color: '#222',
            });
            const lbl = { fontSize: 12, color: '#666', marginRight: 2 };
            const sep = <span style={{ width: 1, height: 18, background: '#e2e8f0', margin: '0 4px' }} />;
            const borderBtns = [
              ['all', '모든 테두리'], ['outer', '바깥 테두리'], ['none', '테두리 없음'],
              ['top', '위쪽 테두리'], ['bottom', '아래쪽 테두리'], ['left', '왼쪽 테두리'], ['right', '오른쪽 테두리'],
            ];
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginBottom: 8, padding: '8px 10px', background: '#f8fafc', borderRadius: 8 }}>
                {borderBtns.map(([k, t]) => (
                  <button key={k} type="button" disabled={off} title={t} onClick={() => applyBorder(k)} style={ib(false)}>
                    <BorderIcon kind={k} />
                  </button>
                ))}
                {sep}
                  <select disabled={off} value={a.fontFamily || ''} onChange={e => setFontFamily(e.target.value)} title="글자체"
                    style={{ ...input, width: 118, padding: '4px 6px', fontSize: 12, opacity: off ? 0.4 : 1 }}>
                    <option value="">기본 글자체</option>
                    {FONT_CHOICES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <select disabled={off} value={a.fontSize || 10} onChange={e => setFontSizeAll(e.target.value)} title="글자 크기"
                    style={{ ...input, width: 62, padding: '4px 6px', fontSize: 12, opacity: off ? 0.4 : 1 }}>
                    {[...new Set([6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32, a.fontSize || 10])].sort((x, y) => x - y)
                      .map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  {sep}
                  <button type="button" disabled={off} title="굵게" onClick={toggleBold} style={{ ...ib(!!a.bold), fontWeight: 800 }}>B</button>
                  <button type="button" disabled={off} title="기울임" onClick={toggleItalic} style={{ ...ib(!!a.italic), fontStyle: 'italic', fontFamily: 'serif' }}>I</button>
                  <button type="button" disabled={off} title="밑줄" onClick={toggleUnderline} style={{ ...ib(!!a.underline), textDecoration: 'underline' }}>U</button>
                  {sep}
                  <button type="button" disabled={off} title="왼쪽 정렬" onClick={() => setAlignAll('left')} style={ib(a.align === 'left')}>≡</button>
                  <button type="button" disabled={off} title="가운데 정렬" onClick={() => setAlignAll('center')} style={ib(a.align === 'center')}>☰</button>
                  <button type="button" disabled={off} title="오른쪽 정렬" onClick={() => setAlignAll('right')} style={ib(a.align === 'right')}>≣</button>
                  {sep}
                  <button type="button" disabled={off} title="셀 위쪽 정렬" onClick={() => setValignAll('top')} style={{ ...ib(a.valign === 'top'), width: 'auto', padding: '0 6px', fontSize: 12 }}>위</button>
                  <button type="button" disabled={off} title="셀 가운데 정렬" onClick={() => setValignAll('middle')} style={{ ...ib((a.valign || 'middle') === 'middle'), width: 'auto', padding: '0 6px', fontSize: 12 }}>중간</button>
                  <button type="button" disabled={off} title="셀 아래쪽 정렬" onClick={() => setValignAll('bottom')} style={{ ...ib(a.valign === 'bottom'), width: 'auto', padding: '0 6px', fontSize: 12 }}>아래</button>
                  {sep}
                  <button type="button" disabled={off} title="이미지 파일 첨부 (또는 칸 선택 후 Ctrl+V 붙여넣기)" onClick={() => imgFileInputRef.current && imgFileInputRef.current.click()} style={{ ...ib(false), width: 'auto', padding: '0 6px', fontSize: 12 }}>🖼 이미지</button>
                  <button type="button" disabled={off} title="로고관리의 가로형 로고를 선택한 칸에 넣습니다(칸 면적 대비 비율, 원본 비율 유지, 가운데 정렬)" onClick={() => insertCompanyLogo('h')} style={{ ...ib(false), width: 'auto', padding: '0 6px', fontSize: 12 }}>로고(가로형)</button>
                  <button type="button" disabled={off} title="로고관리의 세로형 로고를 선택한 칸에 넣습니다(칸 면적 대비 비율, 원본 비율 유지, 가운데 정렬)" onClick={() => insertCompanyLogo('v')} style={{ ...ib(false), width: 'auto', padding: '0 6px', fontSize: 12 }}>로고(세로형)</button>
                  <input type="number" min={10} max={100} value={logoPct} title="셀 면적 대비 로고 크기(%)" onChange={e => setLogoPct(Math.max(1, Math.min(100, Number(e.target.value) || 90)))}
                    style={{ width: 50, height: 26, border: `1px solid ${C.border}`, borderRadius: 4, padding: '0 4px' }} /><span style={lbl}>%</span>
                  {layout?.pageSetup?.linkedImages > 0 && (
                    <span style={{ fontSize: 11, color: '#d46b08', marginLeft: 6 }} title="엑셀 파일에 그림 데이터가 포함되지 않은 링크 그림입니다">⚠ 링크 그림 {layout.pageSetup.linkedImages}개 누락 — 칸 선택 후 Ctrl+V로 추가</span>
                  )}
                  <input ref={imgFileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => { const f = e.target.files && e.target.files[0]; if (f && selected) insertImageFile(f, selected.r, selected.c); e.target.value = ''; }} />
                  {a.image && (<>
                    {sep}
                    <span style={lbl}>그림</span>
                    <button type="button" title="그림 10% 축소" onClick={() => scaleImage(0.9)} style={{ ...ib(false), width: 24 }}>−</button>
                    <button type="button" title="그림 10% 확대" onClick={() => scaleImage(1.1)} style={{ ...ib(false), width: 24 }}>+</button>
                    <span style={lbl}>너비%</span>
                    <input type="number" value={(a.imageBox || IMG_DEFAULT_BOX).width} onChange={e => setImageBox({ width: e.target.value })}
                      style={{ width: 62, height: 26, border: `1px solid ${C.border}`, borderRadius: 4, padding: '0 4px' }} />
                    <span style={lbl}>높이px</span>
                    <input type="number" value={(a.imageBox || IMG_DEFAULT_BOX).height} onChange={e => setImageBox({ height: e.target.value })}
                      style={{ width: 62, height: 26, border: `1px solid ${C.border}`, borderRadius: 4, padding: '0 4px' }} />
                    <span style={lbl}>가로위치%</span>
                    <input type="number" value={(a.imageBox || IMG_DEFAULT_BOX).left} onChange={e => setImageBox({ left: Number(e.target.value) || 0 })}
                      style={{ width: 56, height: 26, border: `1px solid ${C.border}`, borderRadius: 4, padding: '0 4px' }} />
                    <span style={lbl}>세로위치px</span>
                    <input type="number" value={(a.imageBox || IMG_DEFAULT_BOX).top} onChange={e => setImageBox({ top: Number(e.target.value) || 0 })}
                      style={{ width: 56, height: 26, border: `1px solid ${C.border}`, borderRadius: 4, padding: '0 4px' }} />
                  </>)}
                  {sep}
                  <span style={lbl}>글자색</span>
                  <input type="color" disabled={off} value={a.textColor || '#000000'} onChange={e => setTextColor(e.target.value)}
                    style={{ width: 28, height: 26, padding: 0, border: `1px solid ${C.border}`, borderRadius: 4, background: '#fff', opacity: off ? 0.4 : 1 }} />
                  <button type="button" disabled={off} title="글자색 기본값" onClick={() => setTextColor(null)} style={{ ...ib(false), width: 22, fontSize: 11 }}>✕</button>
                  <span style={lbl}>배경색</span>
                  <input type="color" disabled={off} value={a.bgColor || '#ffffff'} onChange={e => setBgColor(e.target.value)}
                    style={{ width: 28, height: 26, padding: 0, border: `1px solid ${C.border}`, borderRadius: 4, background: '#fff', opacity: off ? 0.4 : 1 }} />
                  <button type="button" disabled={off} title="배경색 없음" onClick={() => setBgColor(null)} style={{ ...ib(false), width: 22, fontSize: 11 }}>✕</button>
              </div>
            );
          })()}
          {/* 입력란 지정 — 구분 없이 한 줄(줄바꿈)로 나열. 칸을 선택한 뒤 버튼을 누르면 그 칸이 해당 입력란이 된다. */}
          {(() => {
            const pick = (k) => {
              if (!selected) return;
              const active = !!(selectedCell?.isField && selectedCell.fieldKey === k);
              updateCell(selected.r, selected.c, active ? { isField: false, fieldKey: null } : { isField: true, fieldKey: k });
            };
            const seen = new Set();
            const all = [
              ...AUTO_FIELD_CHOICES.map(([k, l]) => [k, l, null]),
              ...LINK_HEADER_CHOICES.map(([k, l]) => [k, l, null]),
              ...LINK_RECEIPT_CHOICES.map(([k, l]) => [k, l, '#b45309']),
            ].filter(([k]) => (seen.has(k) ? false : (seen.add(k), true)));
            return (
              <div style={{ display:'flex', flexWrap:'wrap', gap:5, alignItems:'center', marginBottom:10, padding:'8px 10px', background:'#f8fafc', borderRadius:8 }}>
                {all.map(([k, l, extra]) => {
                  const active = !!(selectedCell?.isField && selectedCell.fieldKey === k);
                  return (
                    <button key={k} disabled={!selected} onClick={() => pick(k)} title={k}
                      style={{ ...btn(active ? '#16a34a' : '#fff', active ? '#fff' : (extra || C.primary)),
                        border:`1px solid ${C.border}`, padding:'4px 9px', fontSize:12, opacity: selected?1:0.4 }}>{l}</button>
                  );
                })}
                <button disabled={!selected} onClick={() => { if (selected) applyFieldPick('__custom'); }}
                  style={{ ...btn('#fff', '#7c3aed'), border:`1px solid ${C.border}`, padding:'4px 9px', fontSize:12, opacity: selected?1:0.4 }}>
                  직접 입력란
                </button>
                {selectedCell?.isField && (
                  <button onClick={() => updateCell(selected.r, selected.c, { isField: false, fieldKey: null })}
                    style={{ ...btn('#fff', '#dc2626'), border:`1px solid ${C.border}`, padding:'4px 9px', fontSize:12 }}>
                    입력란 해제({selectedCell.fieldKey})
                  </button>
                )}
              </div>
            );
          })()}
          {/* A4 용지 프레임 — 요청사항 1) 가로뿐 아니라 세로도 A4(297mm)로 고정해 실제 인쇄
              한 장의 영역을 그대로 보여준다. 요청사항 4) 상단에 문서번호, 하단에 작성일/Rev를
              기본 화면에도 항상 표시(PDF 출력물에서만 보이던 것을 편집화면으로 확장).
              요청사항 2) 업로드한 엑셀 원본의 실제 용지방향(pageSetup.orientation)이 있으면
              그 방향대로 프레임을 가로/세로로 그려, 편집화면에서부터 원본과 동일하게 보인다. */}
          {/* 원본(엑셀 그대로)과 편집(표)을 하나로 합침 — 엑셀 원본 화면에서 칸을 직접 클릭해 편집하고 저장하면
              같은 원본 파일에 반영되어 인쇄·각 메뉴(견적서/거래명세서/출고증/발주서/전자결재)에 그대로 적용된다.
              엑셀 원본이 없는 서식(docx/hwp 등)이나 원본 미리보기 실패 시에만 기존 표 편집 화면을 쓴다. */}
          {renderLoading && <div style={{ padding:20, textAlign:'center', color:'#888' }}>엑셀 원본 화면을 만드는 중...</div>}
          {renderErr && !renderData && <div style={{ padding:10, color:'#dc2626', fontSize:12 }}>{renderErr} (표 편집 화면으로 대신 표시합니다)</div>}
          {renderData && (
            <>
              <div style={{ fontSize:11, color:'#888', marginBottom:6 }}>
                엑셀 화면에서 칸을 클릭(Shift+클릭=범위)해 선택한 뒤, 위의 서식 도구·입력란 지정으로 편집하세요. 글자는 칸을 더블클릭해 직접 고칠 수 있습니다. 파란 칸은 입력란, 주황 테두리는 선택한 칸입니다. 저장하면 각 메뉴에 적용됩니다.
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12, background:'#e5e7eb', padding:12, borderRadius:6, marginBottom:8, opacity: renderBusy ? 0.85 : 1 }}>
                {renderData.pages.map((p, pi) => (
                  <div key={pi} style={{ position:'relative', width:'100%', maxWidth: Math.round(p.width / 1.5) + 200, boxShadow:'0 1px 6px rgba(0,0,0,.25)', background:'#fff' }}>
                    <img src={p.image} alt={`${pi+1}페이지`} draggable={false} style={{ width:'100%', display:'block', userSelect:'none' }} />
                    {(renderData.cells || []).filter(f => f.page === pi).slice().sort((a, b) => (b.w * b.h) - (a.w * a.h)).map(f => {
                      const pos = addrMap[f.address];
                      if (!pos) return null;
                      const lc = layout?.pages?.[pos.p]?.rows?.[pos.r]?.cells?.[pos.c];
                      const isSel = !!selected && pos.p === currentPage && inRange(pos.r, pos.c);
                      const isFld = !!lc?.isField;
                      const isEdit = !!editing && editing.p === pos.p && editing.r === pos.r && editing.c === pos.c;
                      return (
                        <div key={f.address}
                          title={isFld ? `입력란: ${lc.fieldKey || ''} (${f.address}) — 더블클릭하면 글자를 직접 고칠 수 있습니다` : `${f.address} — 더블클릭하면 글자를 직접 고칠 수 있습니다`}
                          onDoubleClick={e => {
                            e.stopPropagation();
                            if (lc?.image) return;
                            if (pos.p !== currentPage) setCurrentPage(pos.p);
                            setSelected({ r: pos.r, c: pos.c }); setRangeEnd(null);
                            setEditing({ p: pos.p, r: pos.r, c: pos.c });
                          }}
                          onClick={e => {
                            if (pos.p !== currentPage) setCurrentPage(pos.p);
                            if (e.shiftKey && selected) setRangeEnd({ r: pos.r, c: pos.c });
                            else { setSelected({ r: pos.r, c: pos.c }); setRangeEnd(null); }
                          }}
                          style={{ position:'absolute', left:`${f.x*100}%`, top:`${f.y*100}%`, width:`${f.w*100}%`, height:`${f.h*100}%`,
                            boxSizing:'border-box', cursor:'cell',
                            border: isSel ? '2px solid #f97316' : (isFld ? '1px solid #3b82f6' : '1px solid transparent'),
                            background: isSel ? 'rgba(249,115,22,.14)' : (isFld ? 'rgba(59,130,246,.12)' : 'transparent') }}
                          onMouseEnter={e => { if (!isSel && !isFld) e.currentTarget.style.background = 'rgba(100,116,139,.12)'; }}
                          onMouseLeave={e => { if (!isSel && !isFld) e.currentTarget.style.background = 'transparent'; }}>
                          {isEdit && (
                            <input autoFocus value={lc?.text || ''}
                              onChange={e => updateCell(pos.r, pos.c, { text: e.target.value })}
                              onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}
                              onBlur={() => setEditing(null)}
                              onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter' || e.key === 'Escape') setEditing(null); }}
                              style={{ position:'absolute', left:0, top:0, width:'100%', height:'100%', minWidth:60, boxSizing:'border-box',
                                border:'2px solid #f97316', background:'#fffbeb', color:'#000', padding:'0 4px', margin:0, outline:'none',
                                fontSize:13, zIndex:5 }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
          <div style={{ display: renderData ? 'none' : 'flex', justifyContent:'safe center', overflowX:'auto', paddingBottom:4 }}>
          <div style={{ position:'relative', border: usePxCols ? '1px solid #cbd5e1' : '2px dashed #94a3b8', borderRadius: usePxCols ? 0 : 8, background:'#fff',
            boxShadow: usePxCols ? '0 1px 6px rgba(0,0,0,.15)' : undefined,
            padding: usePxCols ? '19mm 15mm 21mm' : '16mm 8mm 14mm',
            width: frameWidthPx,
            minHeight: layout.pageSetup?.orientation === 'landscape' ? '210mm' : '297mm',
            flex:'0 0 auto', boxSizing:'border-box',
            margin:'0 auto 8px', overflow:'visible', display:'flex', flexDirection:'column' }}>

            {/* 상단: 문서번호 — approvalFormLayoutRender.js의 .doc-no-slot과 동일한 위치/스타일 규칙 */}
            {!usePxCols && (
            <div style={{ position:'absolute', top:'6mm', left:'8mm', fontSize:9, color:'#999' }}>
              {docNo ? `문서번호: ${docNo}` : '문서번호: (미채번)'}
            </div>
            )}

            {/* 요청사항 3) 결재라인이 있으면 회의록 미리보기(업무관리)와 동일하게 좌측 양식이름 /
                우측 결재란 구성을 페이지 상단에 배치한다. 엑셀 원본(usePxCols)은 결재란이 표 안에
                이미 있으므로 별도 박스를 그리지 않는다(엑셀과 100% 동일). */}
            {!usePxCols && !!layout.approvalLine?.length && (
              <div style={{ display:'flex', border:'2px solid #000', marginBottom:8, flexShrink:0 }}>
                <div style={{ flex:1, padding:'10px 0', textAlign:'center', fontSize:16, fontWeight:800, letterSpacing:4 }}>
                  {formName}
                </div>
                <table style={{ borderCollapse:'collapse', borderLeft:'2px solid #000' }}>
                  <tbody>
                    <tr>
                      <td rowSpan={2} style={{ padding:'2px 6px', width:15, fontSize:11, fontWeight:700, textAlign:'center', border:'1px solid #000', lineHeight:1.3 }}><div>결</div><div>재</div></td>
                      {layout.approvalLine.map((label, i) => (
                        <td key={i} style={{ padding:'3px 10px', fontSize:11, fontWeight:700, textAlign:'center', border:'1px solid #000', whiteSpace:'nowrap' }}>{label}</td>
                      ))}
                    </tr>
                    <tr>
                      {layout.approvalLine.map((_, i) => (
                        <td key={i} style={{ padding:'3px 8px', height:38, minWidth:48, border:'1px solid #000' }} />
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* 요청사항 1) 엑셀 화면처럼 보이던 열문자(A,B,C...)/행번호(1,2,3...) 머리글을
                편집화면에서 제거했다 — 실제 인쇄되는 서식과 동일하게 표만 보이도록 한다.
                열 폭/행 높이 조정은 기존과 동일하게 경계선 드래그, 또는 표 안쪽을
                우클릭(행 높이는 행의 아무 칸, 열 폭은 그 열의 경계 드래그바)으로 할 수 있다. */}
            <div style={{ display:'flex', flex:1, minHeight:0 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div ref={tableWrapRef} style={{ position:'relative', width: usePxCols ? tablePxWidth : undefined, paddingBottom: 2, margin: usePxCols ? '0 auto' : undefined }}>
                  {/* 열 폭 조정 드래그바 — 컬럼 경계 위치에 겹쳐 그린다. 우클릭하면 폭(%) 직접입력 */}
                  {colBoundaries.map((leftPct, i) => (
                    <div key={i} onMouseDown={(e) => startColResize(e, i)} onContextMenu={(e) => promptColWidth(e, i)}
                      title="드래그: 열 폭 조정 / 우클릭: 폭 직접 입력"
                      style={{ position:'absolute', top:0, bottom:0, left:`${leftPct}%`, width:8, marginLeft:-4,
                        cursor:'col-resize', zIndex:5 }} />
                  ))}
                  <table style={{ width: usePxCols ? tablePxWidth : '100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
                    <colgroup>{layout.colWidthsPct.map((w, i) => <col key={i} style={{ width: usePxCols ? `${layout.colWidthsPx[i]}px` : `${w}%` }} />)}</colgroup>
                    <tbody>
                      {rows.map((row, r) => (
                        <tr key={r} style={row.heightPx ? { height: row.heightPx } : undefined}>
                          {row.cells.map((cell, c) => cell.merged ? null : (
                            <td key={c} rowSpan={cell.rowSpan} colSpan={cell.colSpan}
                              onMouseDown={(e) => {
                                // Shift+클릭: 입력창 포커스가 기준 셀을 덮어쓰기 전에 가로채 범위 선택으로 처리
                                if (e.shiftKey && selected) {
                                  e.preventDefault();
                                  setRangeEnd({ r, c });
                                }
                              }}
                              onClick={(e) => {
                                if (e.shiftKey && selected) setRangeEnd({ r, c });
                                else { setSelected({ r, c }); setRangeEnd(null); }
                              }}
                              style={(() => {
                                // 2026-09(엑셀 100% 동일 서식편집) — 배경색/테두리/세로정렬을 편집기
                                // 미리보기에도 반영해 실제 인쇄/PDF 결과와 최대한 동일하게 보이도록 한다.
                                const isSel = selected && ((selected.r === r && selected.c === c) || inRange(r, c));
                                const base = {
                                  padding: usePxCols ? '0 2px' : 4,
                                  background: cell.bgColor || ((cell.isField && !usePxCols) ? '#eff8ff' : '#fff'),
                                  verticalAlign: cell.valign || 'middle',
                                  position: 'relative',
                                  overflow: 'hidden',
                                };
                                // 얇은 구분 공란(엑셀 행 높이 < 22px)은 패딩 0 + 낮은 줄높이로 엑셀과 같은 높이 유지
                                if (row.heightPx && row.heightPx < 22) {
                                  base.padding = 0; base.lineHeight = 1; base.height = row.heightPx; base.fontSize = 1;
                                }
                                if (cell.borders) {
                                  ['Top', 'Right', 'Bottom', 'Left'].forEach((Side) => {
                                    const side = Side.toLowerCase();
                                    const b = cell.borders[side];
                                    if (!b) { base[`border${Side}`] = 'none'; return; }
                                    const st = String(b.style || 'thin');
                                    let width = '1px', kind = 'solid';
                                    if (st === 'thick') width = '3px';
                                    else if (st === 'medium') width = '2px';
                                    else if (st === 'double') { width = '3px'; kind = 'double'; }
                                    else if (/dot|hair/i.test(st)) kind = 'dotted';
                                    else if (/dash/i.test(st)) kind = 'dashed';
                                    base[`border${Side}`] = `${width} ${kind} ${b.color || '#000'}`;
                                  });
                                } else {
                                  base.border = usePxCols ? 'none' : '1px solid #ccc';
                                }
                                if (cell.image && cell.imageBox) base.overflow = 'visible';
                                if (cell.textBoxes && cell.textBoxes.length) base.overflow = 'visible';
                                if (isSel) { base.outline = `2px solid ${C.primary}`; base.outlineOffset = -2; }
                                return base;
                              })()}>
                              {cell.image && cell.imageBox ? (
                                <img src={cell.image} alt="" style={{ position:'absolute', left:`${cell.imageBox.left}%`, top:cell.imageBox.top, width:`${cell.imageBox.width}%`, height:cell.imageBox.height, maxWidth:'none', zIndex:2, pointerEvents:'none' }} />
                              ) : cell.image ? (
                                <img src={cell.image} alt="" style={{ maxWidth:'100%', maxHeight:80, display:'block', margin:'0 auto' }} />
                              ) : (cell.wrapText || String(cell.text || '').includes('\n')) ? (
                                <textarea
                                  data-cell={`${r}-${c}`}
                                  value={cell.text}
                                  rows={1}
                                  ref={el => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }}
                                  onChange={e => updateCell(r, c, { text: e.target.value })}
                                  onFocus={() => setSelected({ r, c })}
                                  onKeyDown={handleCellKeyDown}
                                  onPaste={e => handleCellPaste(e, r, c)}
                                  style={{
                                    display:'block', width:'100%', border:'none', outline:'none', padding:0, margin:0, boxSizing:'border-box',
                                    resize:'none', overflow:'hidden', background:'transparent', lineHeight:1.3,
                                    color: cell.textColor || 'inherit',
                                    fontFamily: cell.fontFamily ? `'${cell.fontFamily}', inherit` : 'inherit',
                                    whiteSpace:'pre-wrap', overflowWrap:'anywhere', wordBreak:'break-all',
                                    textAlign: cell.align, fontWeight: cell.bold ? 700 : 400, fontSize: cell.fontSize || 10,
                                    fontStyle: cell.italic ? 'italic' : 'normal', textDecoration: cell.underline ? 'underline' : 'none',
                                  }}
                                  placeholder={cell.isField ? `[입력란:${cell.fieldKey}]` : ''}
                                />
                              ) : (
                                <input
                                  data-cell={`${r}-${c}`}
                                  value={cell.text}
                                  onChange={e => updateCell(r, c, { text: e.target.value })}
                                  onFocus={() => setSelected({ r, c })}
                                  onKeyDown={handleCellKeyDown}
                                  onPaste={e => handleCellPaste(e, r, c)}
                                  style={{
                                    width:'100%', border:'none', outline:'none', padding:0, margin:0, boxSizing:'border-box',
                                    background: 'transparent',
                                    color: cell.textColor || 'inherit',
                                    fontFamily: cell.fontFamily || 'inherit',
                                    whiteSpace: 'nowrap',
                                    ...((row.heightPx && row.heightPx < 22) ? { height: Math.max(row.heightPx - 2, 1), lineHeight: 1, display: 'block' } : {}),
                                    textAlign: cell.align, fontWeight: cell.bold ? 700 : 400,
                                    fontSize: (row.heightPx && row.heightPx < 22) ? Math.max(1, Math.min(cell.fontSize || 10, row.heightPx - 2)) : (cell.fontSize || 10),
                                    fontStyle: cell.italic ? 'italic' : 'normal', textDecoration: cell.underline ? 'underline' : 'none',
                                  }}
                                  placeholder={cell.isField ? `[입력란:${cell.fieldKey}]` : ''}
                                />
                              )}
                              {(cell.textBoxes || []).map((tb, ti) => (
                                <div key={`tb${ti}`} title="엑셀 텍스트 상자"
                                  style={{ position:'absolute', left:`${tb.left}%`, top:tb.top, width:`${tb.width}%`, minHeight:tb.height, zIndex:3, pointerEvents:'none', overflow:'visible', textAlign:'left', fontWeight:'normal' }}>
                                  {(tb.lines || []).map((l, li) => (
                                    <div key={li} style={{ fontSize:`${l.fontSize || 10}pt`, fontWeight:l.bold ? 'bold' : 'normal', color:l.color || '#000', textAlign:l.align || 'left', whiteSpace:'nowrap', lineHeight:1.25, minHeight:'1em' }}>{l.text || '\u00a0'}</div>
                                  ))}
                                </div>
                              ))}
                              {c === 0 && (
                                <div onMouseDown={(e) => startRowResize(e, r)} onContextMenu={(e) => promptRowHeight(e, r)}
                                  title="드래그: 행 높이 조정 / 우클릭: 높이 직접 입력"
                                  style={{ position:'absolute', right:0, bottom:0, width:12, height:12,
                                    cursor:'ns-resize', color:'#bbb', fontSize:10, lineHeight:'12px', textAlign:'center', zIndex:4 }}>⋰</div>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 하단: 작성일 + Rev 버전 — approvalFormLayoutRender.js의 .footer와 같은 위치
                규칙(좌우 하단)을 재사용하되, 표시 항목은 요청사항대로 작성일/Rev로 지정 */}
            <div style={{ position:'absolute', bottom:'6mm', left:'8mm', right:'8mm',
              display: usePxCols ? 'none' : 'flex', justifyContent:'space-between', paddingTop:6, borderTop:'1px solid #ddd',
              fontSize:10, color:'#666' }}>
              <span>작성일: {enactedDate || '-'}</span>
              <span>{revLabelText === '-' ? '개정: -' : revLabelText}</span>
            </div>
          </div>
          </div>
          {overflowWarning && (
            <div style={{ color:'#dc2626', fontSize:12, marginBottom:12 }}>
              ⚠ 이 페이지 내용이 1장 인쇄 분량을 초과했습니다 — "+ 페이지 추가(동일양식)"로 이어서 작성해주세요
              (행을 계속 추가하면 자동으로도 다음 페이지가 생성됩니다).
            </div>
          )}

          <div style={{ display:'flex', gap:8, marginTop:16 }}>
            <button onClick={onClose} style={{ ...btn('#f0f0f0', '#555'), flex:1 }}>닫기</button>
            <button onClick={downloadBlankPdf} style={{ ...btn('#0ea5e9'), flex:1 }}>📥 빈 서식 PDF 다운로드</button>
            <button onClick={save} style={{ ...btn(C.primary), flex:1 }}>저장</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/* eslint-enable no-unused-vars */

// ── 로그기록 탭(2026-09 신규) ────────────────────────────────────────────────
// 요청사항: "환경설정 - 양식관리 우측에 로그기록 탭을 추가
//  1. 전체 아이디가 접속한 기록(ip, 사내, 사외포함)을 표시
//  2. 로그인 없이 접속한 기록도 아이피를 포함하여 기록
//  3. 비정상 로그인 및 로그시도가 발생할 경우 해당 기록을 빨간색으로 표기하고 비정상접속이라고 표시
//  4. (경고 모달은 계정관리 관리자모드 진입 시 별도 컴포넌트에서 처리 — MyPage.js
//     SecurityAlertModal 참고)"
// GET /api/admin/security-logs?year=&month=가 login_success/login_fail/no_auth 세 종류를
// 한 번에 내려주므로, 여기서는 년월 필터 + 표 렌더링만 담당한다.
function SecurityLogsTab({ showMsg, year, month, onlyAbnormal, onAbnormalCount }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (year) params.year = year;
    if (month) params.month = month;
    API.get('/admin/security-logs', { params })
      .then(r => setLogs(Array.isArray(r.data) ? r.data : []))
      .catch(() => showMsg && showMsg('로그기록을 불러오지 못했습니다.', 'error'))
      .finally(() => setLoading(false));
  }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const LOG_TYPE_LABEL = {
    login_success: '로그인 성공',
    login_fail: '로그인 실패',
    no_auth: '미인증 접근',
  };

  const visibleLogs = onlyAbnormal ? logs.filter(l => l.is_abnormal) : logs;

  // 2026-09(로그기록 탭 컨트롤 위치 개편) — 비정상접속 건수는 상위 Settings의 탭바 버튼에
  // 배지로 표시되므로, logs가 갱신될 때마다 부모로 건수를 올려보낸다.
  useEffect(() => {
    onAbnormalCount && onAbnormalCount(logs.filter(l => l.is_abnormal).length);
  }, [logs]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#999' }}>총 {visibleLogs.length}건</span>
      </div>

      <div style={{ maxHeight: 640, overflowY: 'auto', border: '1px solid #eee', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
              <th style={secLogTh}>구분</th>
              <th style={secLogTh}>아이디</th>
              <th style={secLogTh}>이름</th>
              <th style={secLogTh}>부서/직급</th>
              <th style={secLogTh}>접속일자</th>
              <th style={secLogTh}>접속시간</th>
              <th style={secLogTh}>로그아웃시간</th>
              <th style={secLogTh}>접속IP</th>
              <th style={secLogTh}>구분(사내/사외)</th>
              <th style={secLogTh}>아이피공급자</th>
              <th style={secLogTh}>지역</th>
              <th style={secLogTh}>상태</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} style={{ ...secLogTd, color: '#aaa', padding: '24px 0' }}>불러오는 중...</td></tr>
            ) : visibleLogs.length === 0 ? (
              <tr><td colSpan={12} style={{ ...secLogTd, color: '#aaa', padding: '24px 0' }}>조회된 로그기록이 없습니다.</td></tr>
            ) : (
              visibleLogs.map(l => {
                const abnormal = !!l.is_abnormal;
                const rowStyle = abnormal ? { background: '#fef2f2' } : undefined;
                const textStyle = abnormal ? { color: '#dc2626', fontWeight: 600 } : undefined;
                return (
                  <tr key={l.id} style={rowStyle}>
                    <td style={{ ...secLogTd, ...textStyle }}>{LOG_TYPE_LABEL[l.log_type] || (l.log_type || '-')}</td>
                    <td style={{ ...secLogTd, ...textStyle }}>{l.username || '-'}</td>
                    <td style={{ ...secLogTd, ...textStyle }}>{l.name || '-'}</td>
                    <td style={{ ...secLogTd, ...textStyle }}>{[l.department, l.position].filter(Boolean).join(' / ') || '-'}</td>
                    <td style={{ ...secLogTd, ...textStyle }}>{l.login_date || '-'}</td>
                    <td style={{ ...secLogTd, ...textStyle }}>{(l.login_time || '').slice(11, 19) || '-'}</td>
                    <td style={{ ...secLogTd, ...textStyle }}>{l.logout_time ? l.logout_time.slice(11, 19) : '-'}</td>
                    <td style={{ ...secLogTd, ...textStyle }}>{l.ip || '-'}</td>
                    <td style={secLogTd}>
                      <span style={{ padding: '1px 7px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                        background: l.location === '사내' ? '#dbeafe' : '#fef3c7',
                        color: l.location === '사내' ? '#1d4ed8' : '#b45309' }}>
                        {l.location || '-'}
                      </span>
                    </td>
                    <td style={{ ...secLogTd, ...textStyle }}>{l.isp || '-'}</td>
                    <td style={{ ...secLogTd, ...textStyle }}>{l.region || '-'}</td>
                    <td style={{ ...secLogTd, ...textStyle }}>
                      {abnormal ? (
                        <span title={l.abnormal_reason || ''}>🔴 비정상접속</span>
                      ) : '정상'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
const secLogTh = { padding: '9px 6px', border: '1px solid #eee', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' };
const secLogTd = { padding: '7px 6px', border: '1px solid #f0f0f0', textAlign: 'center', whiteSpace: 'nowrap' };

function FormsTab({ showMsg, showAdd, setShowAdd, showBulkDelete, setShowBulkDelete, onCount }) {
  // 2026-09(15차) — 이 화면(환경설정)으로 이전되면서, 양식 목록은 더 이상 상위(전자결재)에서
  // props로 받지 않고 이 화면에서 직접 조회/재조회한다(전자결재 기안하기 탭은 별도로
  // GET /approval/forms를 조회해 자체 forms 상태를 유지한다).
  const [forms, setForms] = useState([]);
  const reload = () => API.get('/approval/forms').then(r => setForms(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  useEffect(() => { onCount && onCount(Array.isArray(forms) ? forms.length : 0); }, [forms]); // eslint-disable-line
  const [lineCount, setLineCount] = useState('3');
  const [editingId, setEditingId] = useState(null);
  const [editCategory, setEditCategory] = useState('');
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editingLayoutFor, setEditingLayoutFor] = useState(null);
  // 2026-09(양식추가 대량등록 개편) — 기존 "한 번에 한 양식만" 추가하던 방식을, 파일을 한꺼번에
  // 드롭/선택하면 각 파일명에서 대분류+중분류+양식명을 자동으로 읽어 표(행) 단위로 여러 양식을
  // 한 번에 등록하는 방식으로 바꿨다. 파일명 규칙: "H200_F001_교육훈련 계획서.xlsx"처럼
  // {대분류이니셜}{중분류코드}_F{일련}_{양식명}.확장자 — 대분류/중분류는 이미 등록된
  // 문서 분류 코드(doc_reg_classes)와 대조해 자동으로 매칭한다. 행은 파일 없이 "빈 행 추가"로도
  // 만들 수 있어 기존처럼 수동 입력도 그대로 가능하다. 문서번호 등록(POST /doc-reg, 단일체계·
  // doc_type='F')과 실제 양식 생성(POST /approval/forms)을 행마다 순서대로(등록 순서 = 자동채번
  // 순서) 처리한다. 개정번호(Rev)는 이 화면에서 입력할 수 없고 등록 시 항상 0(Rev.00)에서 시작한다.
  const [classes, setClasses] = useState([]);   // 환경설정 > 문서번호의 "문서 분류 코드"(doc_reg_classes)
  const [bulkItems, setBulkItems] = useState([]); // [{ key, file, majorCode, minorCode, name, desc }]
  const [previewBase, setPreviewBase] = useState({}); // { "H200": 3 } — 조합별 다음 채번 시작값
  const [registering, setRegistering] = useState(false);
  const { sorted, sortKey, sortDir, toggleSort } = useSort(forms);

  // 2026-10: 문서번호 설정이 "분야별관리"이고 브랜드가 1개 이상이면, 양식번호의 회사약어를
  // 선택한 브랜드(브랜드 설정에서 연결한 분야 약어, 없으면 브랜드명)로 쓴다. 단일관리는 기존 그대로.
  const [schemeMode, setSchemeMode] = useState('single');
  const [brandList, setBrandList] = useState([]);
  const [brandId, setBrandId] = useState('');
  useEffect(() => {
    if (!showAdd) return;
    API.get('/approval/doc-reg/classes').then(r => setClasses(r.data || [])).catch(() => setClasses([]));
    API.get('/approval/doc-reg/scheme').then(r => setSchemeMode((r.data && r.data.mode) || 'single')).catch(() => setSchemeMode('single'));
    API.get('/company-info/brands').then(r => {
      const list = (r.data || []).map(b => ({ id: b.id, name: b.name, abbr: String(b.doc_field_abbr || b.name || '').trim() })).filter(b => b.abbr);
      setBrandList(list);
      setBrandId(prev => prev || (list[0] ? String(list[0].id) : ''));
    }).catch(() => setBrandList([]));
  }, [showAdd]);
  const useBrandPrefix = schemeMode === 'byType' && brandList.length > 0;
  const prefixOverride = useBrandPrefix ? ((brandList.find(b => String(b.id) === String(brandId)) || brandList[0]).abbr) : '';

  // 대분류 목록(major_code 기준 중복 제거, sort_order 순서 그대로 유지)
  const majors = [];
  const seenMajor = new Set();
  classes.forEach(c => { if (!seenMajor.has(c.major_code)) { seenMajor.add(c.major_code); majors.push(c); } });
  const minorsFor = (major) => classes.filter(c => c.major_code === major);

  // 파일명 "H200_F001_교육훈련 계획서.xlsx" -> 대분류 이니셜(H) + 중분류코드(200) + 양식명을
  // 추출해 기존 문서 분류 코드 목록과 대조한다. 일치하는 항목이 있으면 그 코드를, 없으면
  // 파일명에서 읽은 값을 그대로 넣어두고(관리자가 직접 확인/수정) 매칭에 실패했음을 표시한다.
  // 목록의 인쇄 버튼 — 저장된 서식 PDF를 받아 숨은 iframe으로 바로 인쇄창을 띄운다.
  const printFormPdf = (f) => {
    API.get(`/approval/forms/${f.id}/layout/pdf`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const ifr = document.createElement('iframe');
        ifr.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
        ifr.src = url;
        ifr.onload = () => {
          try { ifr.contentWindow.focus(); ifr.contentWindow.print(); } catch (_) { window.open(url, '_blank'); }
          setTimeout(() => { ifr.remove(); window.URL.revokeObjectURL(url); }, 60000);
        };
        document.body.appendChild(ifr);
      })
      .catch(async err => alert(await blobErrorMessage(err, '인쇄할 서식 PDF를 만들지 못했습니다. 먼저 서식을 저장해주세요.')));
  };

  const parseBulkFile = (file) => {
    const base = file.name.replace(/\.[^./\\]+$/, '').trim();
    // 지원하는 파일명 형식(구분자 "_" 또는 "-", 회사약어는 있어도/없어도 됨):
    //   H200_F001_교육훈련 계획서.xlsx            → 대분류 H, 중분류 200
    //   NSP_0102_F01_수입검사 기준서.xlsx          → (회사약어 NS) 대분류 P, 중분류 0102
    // 영문자 덩어리 앞쪽에 회사약어가 붙어 있을 수 있어, 영문자 덩어리의 "뒤쪽부터" 줄여가며
    // 등록된 문서분류코드와 대조해 일치하는 대분류를 찾는다(일치가 없으면 회사약어 2자를 뗀 값).
    const m = base.match(/^([A-Za-z]+)[_-]?(\d+)[_-]F\d+[_-](.+)$/) || base.match(/^([A-Za-z]+)[_-]?(\d+)[_-](.+)$/);
    let majorCode = '', minorCode = '', name = base, matched = false;
    if (m) {
      const letters = m[1].toUpperCase();
      const rawMinor = m[2];
      name = m[3].trim();
      const norm = (v) => String(v || '').replace(/^0+(?=\d)/, '');
      let found = null;
      for (let i = 0; i < letters.length && !found; i++) {
        const cand = letters.slice(i);
        found = classes.find(c => (c.major_code || '').toUpperCase() === cand && norm(c.minor_code) === norm(rawMinor)) || null;
      }
      if (found) { majorCode = found.major_code; minorCode = found.minor_code; matched = true; }
      else { majorCode = letters.length >= 3 ? letters.slice(2) : letters; minorCode = norm(rawMinor); }
    }
    return { key: `${file.name}_${Date.now()}_${Math.random().toString(36).slice(2)}`, file, majorCode, minorCode, name, desc: '', matched, lineCount: '0', detectedLine: null };
  };
  // 파일을 추가하면 서버의 표 구조 인식(/forms/layout/parse)으로 결재란(작성/검토/승인...)을
  // 미리 찾아, 있으면 단계수(3/4/5)를 행별로 자동 지정한다(없으면 "없음"). 사용자가 직접 바꿀 수 있다.
  const detectLineCount = async (key, file) => {
    if (!/\.(xlsx|docx|hwp)$/i.test(file.name)) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await API.post('/approval/forms/layout/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const steps = r.data?.approval_steps;
      const n = Array.isArray(steps) ? steps.length : 0;
      const lc = n >= 2 ? String(Math.min(5, Math.max(3, n))) : '0';
      setBulkItems(prev => prev.map(it => it.key === key ? { ...it, lineCount: lc, detectedLine: n >= 2 ? n : 0 } : it));
    } catch (_) { /* 인식 실패 시 기본값(없음) 유지 */ }
  };
  const addFilesToBulk = (files) => {
    const items = files.map(parseBulkFile);
    setBulkItems(prev => [...prev, ...items]);
    items.forEach(it => it.file && detectLineCount(it.key, it.file));
  };
  const addBlankBulkRow = () => setBulkItems(prev => [...prev, {
    key: `blank_${Date.now()}_${Math.random().toString(36).slice(2)}`, file: null, majorCode: '', minorCode: '', name: '', desc: '', matched: false, lineCount: '3', detectedLine: null,
  }]);
  const updateBulkRow = (key, patch) => setBulkItems(prev => prev.map(it => it.key === key ? { ...it, ...patch, ...(patch.majorCode !== undefined ? { minorCode: '' } : {}) } : it));
  const removeBulkRow = (key) => setBulkItems(prev => prev.filter(it => it.key !== key));

  // 조합(대분류+중분류)별 다음 채번 시작값을 미리 조회해두고, 같은 조합이 여러 행에 걸쳐
  // 있으면 실제 등록 순서(위→아래)대로 1씩 증가한다고 가정해 미리보기 번호를 계산한다.
  useEffect(() => {
    const keys = [...new Set(bulkItems.filter(it => it.majorCode && it.minorCode).map(it => `${it.majorCode}${it.minorCode}`))];
    const missing = keys.filter(k => previewBase[`${prefixOverride}|${k}`] === undefined);
    if (!missing.length) return;
    missing.forEach(key => {
      const pk = `${prefixOverride}|${key}`;
      API.get('/approval/doc-reg/next-serial', { params: { scheme: 'single', category: key, doc_type: 'F', company_prefix: prefixOverride || undefined } })
        .then(r => setPreviewBase(prev => ({ ...prev, [pk]: r.data?.serial_no == null ? null : { serial: r.data.serial_no, head: String(r.data.preview || '').replace(/\d+$/, '') } })))
        .catch(() => setPreviewBase(prev => ({ ...prev, [pk]: null })));
    });
  }, [bulkItems, prefixOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  const previewFor = (item, idx) => {
    if (!item.majorCode || !item.minorCode) return '-';
    const key = `${item.majorCode}${item.minorCode}`;
    const base = previewBase[`${prefixOverride}|${key}`];
    if (base == null) return '...';
    const offset = bulkItems.slice(0, idx).filter(x => `${x.majorCode}${x.minorCode}` === key).length;
    return `${base.head || 'F'}${String(base.serial + offset).padStart(3, '0')}`;
  };

  const closeAdd = () => {
    setShowAdd(false); setBulkItems([]); setPreviewBase({}); setLineCount('3');
  };
  const bulkAdd = async () => {
    if (!bulkItems.length) return alert('추가할 양식이 없습니다. 파일을 드래그하거나 "빈 행 추가"를 눌러주세요.');
    const bad = bulkItems.find(it => !it.majorCode || !it.minorCode || !it.name.trim());
    if (bad) return alert('모든 행의 대분류/중분류/양식명을 입력해주세요.');
    setRegistering(true);
    let ok = 0; const fails = [];
    for (const it of bulkItems) {
      let regId = null;
      try {
        const r = await API.post('/approval/doc-reg', { scheme: 'single', category_code: `${it.majorCode}${it.minorCode}`, doc_type: 'F', doc_name: it.name.trim(), company_prefix: prefixOverride || undefined });
        regId = r.data.id;
        const fd = new FormData();
        fd.append('category', '');
        fd.append('name', it.name.trim());
        fd.append('description', it.desc || '');
        fd.append('approval_line_count', it.lineCount || '0');
        fd.append('doc_registry_id', r.data.id);
        if (it.file) fd.append('files', it.file);
        await API.post('/approval/forms', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch (err) {
        // 양식 생성이 실패하면 먼저 만든 문서번호 행이 번호만 차지하지 않도록 되돌린다.
        if (regId) { try { await API.delete(`/approval/doc-reg/${regId}`); } catch (_) { /* 무시 */ } }
        fails.push(`${it.name || it.file?.name || '항목'}: ${err.response?.data?.message || '실패'}`);
      }
    }
    setRegistering(false);
    if (fails.length) alert(`${ok}건 등록 완료, ${fails.length}건 실패\n${fails.join('\n')}`);
    else showMsg(`${ok}건의 양식이 추가되었습니다.`);
    closeAdd(); reload();
  };
  const toggleActive = (f) => API.patch(`/approval/forms/${f.id}`, { active: f.active ? 0 : 1 }).then(reload);
  const startEdit = (f) => {
    setEditingId(f.id); setEditCategory(f.category || '');
    setEditName(f.name || ''); setEditDesc(f.description || '');
  };
  const saveEdit = (id) => {
    API.patch(`/approval/forms/${id}`, { category: editCategory, name: editName, description: editDesc })
      .then(() => { setEditingId(null); showMsg('양식이 수정되었습니다.(개정 반영)'); reload(); })
      .catch(err => alert(err.response?.data?.message || '수정 실패'));
  };
  // 일괄삭제 — 체크한 서식을 한 번에 삭제(서버에서도 관리자/최고관리자만 허용)
  const [bulkSel, setBulkSel] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => { if (showBulkDelete) setBulkSel([]); }, [showBulkDelete]);
  const toggleBulk = (id) => setBulkSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const runBulkDelete = () => {
    if (!bulkSel.length) { alert('삭제할 서식을 선택해주세요.'); return; }
    if (!window.confirm(`선택한 서식 ${bulkSel.length}건을 삭제하시겠습니까?\n삭제한 서식은 복구할 수 없습니다.`)) return;
    setBulkBusy(true);
    API.post('/approval/forms/bulk-delete', { ids: bulkSel })
      .then(r => { showMsg((r.data && r.data.message) || '삭제되었습니다.'); setShowBulkDelete(false); reload(); })
      .catch(err => alert(err.response?.data?.message || '일괄삭제에 실패했습니다.'))
      .finally(() => setBulkBusy(false));
  };
  const del = (id) => { if (!window.confirm('삭제하시겠습니까?')) return; API.delete(`/approval/forms/${id}`).then(() => { showMsg('삭제되었습니다.'); reload(); }); };
  // 이미 등록된 구분값이 새 드롭다운 목록(FORM_CATEGORIES)에 없는 경우(예전에 자유입력으로
  // 저장된 값)에도 선택창에서 사라지지 않도록 목록 끝에 임시로 추가해준다.
  const categoryOptionsFor = (val) => (val && !FORM_CATEGORIES.includes(val)) ? [...FORM_CATEGORIES, val] : FORM_CATEGORIES;

  // 개정 표시: 개정 이력이 없으면 '-', 있으면 "Rev01 2026-08-23" 형식
  const revLabel = (f) => f.rev_no ? `Rev${String(f.rev_no).padStart(2, '0')}${f.rev_date ? ' ' + f.rev_date : ''}` : '-';
  // 2026-09(양식관리 목차개편) — 개정/Rev를 별도 열로 분리해 표시한다.
  const revDateLabel = (f) => f.rev_no ? (f.rev_date || '-') : '-';
  const revNoLabel = (f) => f.rev_no ? `Rev${String(f.rev_no).padStart(2, '0')}` : 'Rev00';
  // 양식번호 표시에서 맨 앞 회사 약어(첫 '-' 이전 구간)는 생략하고 그 뒤만 보여준다.
  // 예) "NS-Q200-F001" -> "Q200-F001". 저장된 doc_no 값 자체는 그대로 유지된다.
  // 2026-09(양식관리 "(Rev.00)" 중복표기 제거) — 개정번호는 이미 별도 열(제정/개정/Rev)로
  // 표시하고 있으므로, doc_no 뒤에 붙어있는 "(Rev.NN)" 접미사는 여기서 한 번 더 제거한다.
  // (신규 등록분은 백엔드에서 애초에 접미사 없이 저장하지만, 개편 이전에 저장된 값도 있어
  // 화면 표시 단계에서 안전하게 걸러낸다.)
  // 2026-10: 구분문자(M/P/I/F) 뒤 3자리 일련번호는 "P102" → "P-0102" 형태로 표시한다(옛 저장값 호환).
  // 2026-10: 양식번호는 회사약어가 대분류와 붙은 "NSP-0102-F001" 형태라 전체를 그대로 표시한다.
  const formNoDisplay = (docNo) => docNo
    ? String(docNo).replace(/\s*\(Rev\.\d+\)\s*$/i, '')
    : '-';
  // 2026-09(양식관리 크기/방향 자동표기) — 서식편집(FormLayoutEditorModal)에서 저장한 표 구조
  // (열 개수 vs 행 개수)로 가로형/세로형을 자동 판별한다. 별도 방향 지정 필드 없이, 실제 작성된
  // 서식의 칸 구성만으로 분류한다 — 열이 행보다 많으면 가로로 넓은 표로 보고 "가로", 그 외(아직
  // 서식을 작성하지 않은 경우 포함)에는 기본값인 "세로"로 분류한다.
  // 2026-09(환경설정-양식관리 개선 2) — 엑셀 원본을 그대로 가져온 양식이면 실제 워크시트의
  // pageSetup.orientation(용지방향)을 그대로 신뢰한다. 수기로 만든 양식 등 이 정보가 없을
  // 때만 열/행 개수 비교 추정으로 보조한다(기존 로직, fallback으로 유지).
  const formOrientation = (f) => {
    if (!f.layout_json) return '세로';
    try {
      const raw = JSON.parse(f.layout_json);
      if (raw?.pageSetup?.orientation === 'landscape') return '가로';
      if (raw?.pageSetup?.orientation === 'portrait') return '세로';
      const layout = normalizeLayoutClient(raw);
      const cols = (layout.colWidthsPct || []).length || 1;
      const rows = (layout.pages || []).reduce((s, p) => s + ((p.rows || []).length), 0) || 1;
      return cols > rows ? '가로' : '세로';
    } catch { return '세로'; }
  };
  const sizeLabel = (f) => formOrientation(f) === '가로' ? 'A4(297mmX210mm)' : 'A4(210mmX297mm)';

  return (
    <Section>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>
            <SortTh label="양식번호" sortKey="doc_no" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="left" />
            <SortTh label="양식명" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="left" />
            <th style={th}>크기</th>
            <th style={th}>서식방향</th>
            <th style={th}>설명</th>
            <SortTh label="제정" sortKey="enacted_date" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
            <SortTh label="개정" sortKey="rev_date" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
            <SortTh label="Rev" sortKey="rev_no" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
            <th style={th}>사용여부</th>
            <th style={th}>서식관리</th>
          </tr></thead>
          <tbody>
            {sorted.length === 0 && <EmptyRow span={10} text="등록된 양식이 없습니다." />}
            {sorted.map(f => (
              <tr key={f.id}>
                <td style={{ ...td, textAlign:'left' }}>{formNoDisplay(f.doc_no)}</td>
                <td style={{ ...td, textAlign:'left', fontWeight:600 }}>
                  {editingId === f.id
                    ? <input value={editName} onChange={e => setEditName(e.target.value)} style={{ ...input, width:180 }} autoFocus onKeyDown={e => e.key==='Enter' && saveEdit(f.id)} />
                    : f.name}
                </td>
                <td style={td}>{sizeLabel(f)}</td>
                <td style={td}>{formOrientation(f)}</td>
                <td style={{ ...td, textAlign:'left' }}>
                  {editingId === f.id
                    ? <input value={editDesc} onChange={e => setEditDesc(e.target.value)} style={{ ...input, width:180 }} onKeyDown={e => e.key==='Enter' && saveEdit(f.id)} />
                    : (f.description || '-')}
                </td>
                <td style={td}>{f.enacted_date || '-'}</td>
                <td style={td}>{revDateLabel(f)}</td>
                <td style={td}>{revNoLabel(f)}</td>
                <td style={td}>
                  <label style={{ display:'inline-flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!f.active} onChange={() => toggleActive(f)} /> 사용
                  </label>
                </td>
                <td style={td}>
                  {editingId === f.id ? (
                    <>
                      <button onClick={() => saveEdit(f.id)} style={{ background:'none', border:'none', color:C.primary, fontSize:14, cursor:'pointer', fontWeight:600, marginRight:8 }}>저장</button>
                      <button onClick={() => setEditingId(null)} style={{ background:'none', border:'none', color:'#888', fontSize:14, cursor:'pointer' }}>취소</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditingLayoutFor({
                        id: f.id, name: f.name, doc_no: f.doc_no,
                        enacted_date: f.enacted_date, rev_no: f.rev_no, rev_date: f.rev_date,
                        isRevision: true,
                      })} style={{ background:'none', border:'none', color:'#f59e0b', fontSize:14, cursor:'pointer', fontWeight:600, marginRight:8 }}>🔄 양식개정</button>
                      <button onClick={() => setEditingLayoutFor({
                        id: f.id, name: f.name, doc_no: f.doc_no,
                        enacted_date: f.enacted_date, rev_no: f.rev_no, rev_date: f.rev_date,
                        isRevision: false,
                      })} style={{ background:'none', border:'none', color:'#0ea5e9', fontSize:14, cursor:'pointer', fontWeight:600, marginRight:8 }}>📐 서식수정</button>
                      <button onClick={() => printFormPdf(f)} style={{ background:'none', border:'none', color:'#475569', fontSize:14, cursor:'pointer', fontWeight:600, marginRight:8 }}>🖨 인쇄</button>
                      <button onClick={() => startEdit(f)} style={{ background:'none', border:'none', color:C.primary, fontSize:14, cursor:'pointer', fontWeight:600, marginRight:8 }}>수정</button>
                      <button onClick={() => del(f.id)} style={{ background:'none', border:'none', color:'#dc2626', fontSize:14, cursor:'pointer', fontWeight:600 }}>삭제</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showBulkDelete && (
        <Modal title="서식 일괄삭제" onClose={() => setShowBulkDelete(false)} width={720}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <label style={{ fontSize:13, display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
              <input type="checkbox" checked={forms.length > 0 && bulkSel.length === forms.length}
                onChange={e => setBulkSel(e.target.checked ? forms.map(f => f.id) : [])} />
              전체선택 ({bulkSel.length}/{forms.length})
            </label>
            <button onClick={runBulkDelete} disabled={bulkBusy || !bulkSel.length}
              style={{ ...btn(bulkBusy || !bulkSel.length ? '#aaa' : '#dc2626'), padding:'8px 14px', fontSize:13 }}>
              {bulkBusy ? '삭제 중...' : `선택 삭제 (${bulkSel.length})`}
            </button>
          </div>
          <div style={{ maxHeight:'60vh', overflowY:'auto', border:'1px solid #e5e7eb', borderRadius:8 }}>
            {forms.map(f => (
              <label key={f.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderBottom:'1px solid #f1f5f9', fontSize:13, cursor:'pointer', background: bulkSel.includes(f.id) ? '#fef2f2' : '#fff' }}>
                <input type="checkbox" checked={bulkSel.includes(f.id)} onChange={() => toggleBulk(f.id)} />
                <span style={{ width:110, color:'#666', flexShrink:0 }}>{f.doc_no || '-'}</span>
                <span style={{ flex:1 }}>{f.name}</span>
                <span style={{ color:'#999' }}>{f.category || ''}</span>
              </label>
            ))}
            {forms.length === 0 && <div style={{ padding:20, textAlign:'center', color:'#999' }}>등록된 서식이 없습니다.</div>}
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="서식추가" onClose={closeAdd} width={920}>
          {useBrandPrefix && (
            <Field label="브랜드(회사약어)">
              <select value={brandId} onChange={e => setBrandId(e.target.value)} style={{ ...input, padding:'6px 8px', width:260 }}>
                {brandList.map(b => <option key={b.id} value={b.id}>{b.name} ({b.abbr})</option>)}
              </select>
            </Field>
          )}
          <Field label="파일로 일괄추가(선택)">
            <FileDropZone
              multiple
              fileNames={[]}
              onFiles={addFilesToBulk}
              style={{ marginBottom: 8 }}
            />
            <div style={{ fontSize:11.5, color:'#888', marginTop:-2, marginBottom:10 }}>
              파일명 "H200_F001_교육훈련 계획서.xlsx"처럼 등록하면 대분류·중분류·양식명이 아래 표에 자동으로 채워집니다.
              대분류/중분류는 환경설정 &gt; 문서번호의 "문서 분류 코드"와 대조해 자동 매칭됩니다.
            </div>
            {classes.length === 0 && (
              <div style={{ fontSize:11.5, color:'#e67700', marginBottom:10 }}>
                등록된 분류코드가 없습니다. 환경설정 &gt; 문서번호의 "문서 분류 코드"에서 먼저 등록해주세요.
              </div>
            )}
          </Field>

          <div style={{ overflowX:'auto', border:'1px solid #eee', borderRadius:8, marginBottom:14 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={{ ...th, width:110 }}>대분류</th>
                <th style={{ ...th, width:110 }}>중분류</th>
                <th style={{ ...th, width:150 }}>양식번호</th>
                <th style={{ ...th, textAlign:'left', width:150 }}>양식명</th>
                <th style={{ ...th, textAlign:'left', width:110 }}>설명(선택)</th>
                <th style={{ ...th, textAlign:'left' }}>파일</th>
                <th style={{ ...th, width:96 }}>결재란</th>
                <th style={{ ...th, width:36 }}></th>
              </tr></thead>
              <tbody>
                {bulkItems.length === 0 && (
                  <tr><td colSpan={8} style={{ ...td, color:'#aaa', padding:'18px 0' }}>등록할 양식이 없습니다. 위에서 파일을 추가하거나 "빈 행 추가"를 눌러주세요.</td></tr>
                )}
                {bulkItems.map((it, idx) => (
                  <tr key={it.key}>
                    <td style={td}>
                      <select value={it.majorCode} onChange={e => updateBulkRow(it.key, { majorCode: e.target.value })} style={{ ...input, padding:'6px 8px', width:'100%' }}>
                        <option value="">선택</option>
                        {it.majorCode && !majors.some(m => m.major_code === it.majorCode) && <option value={it.majorCode}>{it.majorCode}</option>}
                        {majors.map(m => <option key={m.major_code} value={m.major_code}>{m.major_code}</option>)}
                      </select>
                    </td>
                    <td style={td}>
                      <select value={it.minorCode} onChange={e => updateBulkRow(it.key, { minorCode: e.target.value })} disabled={!it.majorCode} style={{ ...input, padding:'6px 8px', width:'100%' }}>
                        <option value="">선택</option>
                        {it.minorCode && !minorsFor(it.majorCode).some(m => m.minor_code === it.minorCode) && <option value={it.minorCode}>{it.minorCode}</option>}
                        {minorsFor(it.majorCode).map(m => <option key={m.id} value={m.minor_code}>{m.minor_code}</option>)}
                      </select>
                    </td>
                    <td style={{ ...td, fontFamily:'monospace', fontWeight:600, color: (it.majorCode && it.minorCode) ? C.primary : '#bbb' }}>
                      {previewFor(it, idx)}
                    </td>
                    <td style={{ ...td, textAlign:'left' }}>
                      <input value={it.name} onChange={e => updateBulkRow(it.key, { name: e.target.value })} placeholder="예: 지출결의서" style={{ ...input, padding:'6px 8px', width:'100%' }} />
                    </td>
                    <td style={{ ...td, textAlign:'left' }}>
                      <input value={it.desc} onChange={e => updateBulkRow(it.key, { desc: e.target.value })} style={{ ...input, padding:'6px 8px', width:'100%' }} />
                    </td>
                    <td style={{ ...td, fontSize:16, color:'#16a34a', textAlign:'center' }} title={it.file ? it.file.name : ''}>{it.file ? '📎' : '-'}</td>
                    <td style={td}>
                      <select value={it.lineCount || '0'} onChange={e => updateBulkRow(it.key, { lineCount: e.target.value })}
                        title={it.detectedLine ? `파일에서 결재란 ${it.detectedLine}단계 자동 인식` : ''}
                        style={{ ...input, padding:'6px 4px', width:'100%' }}>
                        <option value="0">없음</option>
                        <option value="3">3단계</option>
                        <option value="4">4단계</option>
                        <option value="5">5단계</option>
                      </select>
                    </td>
                    <td style={{ ...td, textAlign:'center' }}>
                      <button onClick={() => removeBulkRow(it.key)} title="삭제" style={{ background:'none', border:'none', color:'#dc2626', fontSize:16, cursor:'pointer', lineHeight:1 }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={addBlankBulkRow} style={{ display:'block', width:'100%', padding:'8px 0', background:'#f8fafc', border:'none', borderTop:'1px solid #eee', color:C.primary, fontSize:12.5, fontWeight:600, cursor:'pointer' }}>+ 빈 행 추가</button>
          </div>

          <div style={{ fontSize:11.5, color:'#888', marginBottom:10 }}>
            결재란은 파일에서 자동 인식됩니다(3·4·5단계, 없으면 "없음"). 필요하면 행별로 직접 바꿀 수 있습니다.
          </div>
          <div style={{ display:'flex', gap:8, marginTop:4 }}>
            <button onClick={closeAdd} style={{ ...btn('#f0f0f0', '#555'), flex:1 }}>취소</button>
            <button onClick={bulkAdd} disabled={registering} style={{ ...btn(C.primary), flex:2, opacity: registering ? 0.6 : 1 }}>
              {registering ? '추가 중...' : `${bulkItems.length || ''}건 추가`}
            </button>
          </div>
        </Modal>
      )}

      {editingLayoutFor && (
        <FormLayoutEditorModal
          formId={editingLayoutFor.id}
          formName={editingLayoutFor.name}
          docNo={editingLayoutFor.doc_no}
          enactedDate={editingLayoutFor.enacted_date}
          revNo={editingLayoutFor.rev_no}
          revDate={editingLayoutFor.rev_date}
          isRevision={!!editingLayoutFor.isRevision}
          onClose={() => { setEditingLayoutFor(null); reload(); }}
          showMsg={showMsg}
        />
      )}
    </Section>
  );
}

function DocNumberSection({ showMsg, registerApply, docMode, onSchemeLoaded, registerAddItem }) {
  // 상단 3개 미리보기 패널이 공유하는 예시값. "회사약어"는 세 패널 모두 같은 state를 참조하므로
  // 한 군데만 입력해도 나머지 두 곳에 자동 반영된다(요청사항).
  // 요청사항: "양식번호, 문서번호, 기록발생, 외부등록문서에서 대분류 중분류는 각각 따로
  // 사용자가 입력할 수 있도록 수정 (회사약어는 공통으로 사용)" — 대분류/중분류를 패널별로
  // 독립된 값(formMajor/docMajor/recMajor/extMajor 등)으로 분리한다. 회사약어(abbr)만
  // 4개 패널이 공유하는 값으로 남긴다.
  const [ex, setEx] = useState({
    abbr: 'NS',
    formMajor: 'Q', formMinor: '107', form: 'F001',
    docMajor: 'Q', docMinor: '107', serial: '0001',
    recMajor: 'Q', recMinor: '107', yymm: '2609',
    extMajor: 'Q', extMinor: '107',
    rev: '01',
  });
  const [classes, setClasses] = useState([]);
  const [newRow, setNewRow] = useState({ major_code: '', minor_code: '', name: '', example: '', major_name: '' });

  const load = () => API.get('/approval/doc-reg/classes').then(r => setClasses(r.data || [])).catch(() => setClasses([]));
  useEffect(() => { load(); }, []);

  // 요청사항: "적용버튼을 누르면 현재 구성되어 있는 사항이 저장 및 각 문서에 적용될 수 있도록
  // 반영" — 실제 문서번호 생성 로직(backend/utils/docNumberFormat.js)이 참조하는 회사공용 접두어
  // (doc_number_scheme.single_prefix)에 회사약어(ex.abbr)를 반영한다. 분류코드 표는 각 셀이
  // onBlur 즉시 저장되므로 별도 처리가 필요없다. 화면을 처음 열 때는 기존에 저장된 접두어를
  // 불러와 회사약어 입력칸의 시작값으로 채운다.
  useEffect(() => {
    API.get('/approval/doc-reg/scheme')
      .then(r => {
        if (r.data?.single_prefix) setEx(v => ({ ...v, abbr: r.data.single_prefix }));
        if (onSchemeLoaded) onSchemeLoaded(r.data?.mode || 'single');
      })
      .catch(() => {});
  }, []);

  // 2026-09 요청(수정): "분야별 관리는 단일관리 화면에서 제일 상단에 있는 양식번호, 문서번호,
  // 기록발생, 외부문서등록 입력칸만 추가로 생성해서 적용하는거야(단, 분야1/분야2 글자는
  // 수정이 가능해야 함)" — byType 관리항목 등록 UI로 확장했던 것을 되돌린다. 분야별관리는
  // 단순히 단일관리와 동일한 4패널(양식번호/문서번호/기록발생/외부등록문서) 묶음을
  // "항목추가"를 누를 때마다 하나씩 더 만들 수 있는 기능이다.
  // 2026-09 요청(2차): "전자결재-양식관리에서 양식등록시 환경설정-문서번호에서 설정된 값에
  // 따라 문서를 등록할 수 있도록 구성" — 분야1/분야2 라벨을 이제 프론트 state로만 들고
  // 있으면 전자결재 화면(다른 컴포넌트)에서 알 수 없으므로, doc_reg_fields 테이블에 실제
  // 저장한다(name=화면에 보이는 라벨, abbr=문서번호 접두어). 전자결재 > 양식관리 > 양식추가의
  // "문서번호 선택" 화면이 조직탭(NINE/NS/NC) 대신 이 목록을 그대로 가져와 쓴다. 4패널의
  // 대분류/중분류/양식번호 등은 여전히 화면 예시용(illustrative)로 남긴다(실제 문서번호는
  // 아래 "📋 문서 분류 코드" 표의 대분류/중분류를 골라 만든다).
  const makeItemState = (overrides = {}) => ({
    id: null, name: '', abbr: ex.abbr || 'NS',
    formMajor: 'Q', formMinor: '107', form: 'F001',
    docMajor: 'Q', docMinor: '107', serial: '0001',
    recMajor: 'Q', recMinor: '107', yymm: '2609',
    extMajor: 'Q', extMinor: '107',
    rev: '01',
    ...overrides,
  });
  const [items, setItems] = useState([]);
  const loadFields = () => API.get('/approval/doc-reg/fields').then(r => {
    const rows = r.data || [];
    setItems(rows.map(f => makeItemState({ id: f.id, name: f.name, abbr: f.abbr })));
  }).catch(() => {});
  useEffect(() => { loadFields(); }, []);

  const addItem = () => {
    API.post('/approval/doc-reg/fields', { name: `분야${items.length + 1}`, abbr: ex.abbr || 'NS' })
      .then(loadFields)
      .catch(e => showMsg(e.response?.data?.message || '분야 추가 실패', 'error'));
  };
  useEffect(() => {
    if (registerAddItem) registerAddItem(addItem);
  }, [registerAddItem, items.length]);

  const setItemField = (idx, k) => (e) => setItems(v => v.map((it, i) => (i === idx ? { ...it, [k]: e.target.value } : it)));
  const setItemRev = (idx) => (e) => setItems(v => v.map((it, i) => (i === idx ? { ...it, rev: e.target.value.replace(/[^0-9]/g, '').slice(0, 2) || '00' } : it)));
  // 분야명/회사약어는 실제 doc_reg_fields에 저장되는 값이므로, 로컬 state를 즉시 갱신하고
  // onBlur 시점에 서버로도 반영한다(그 외 대분류/중분류 등 예시값은 로컬에만 남긴다).
  const setItemName = (idx) => (e) => setItems(v => v.map((it, i) => (i === idx ? { ...it, name: e.target.value } : it)));
  const commitItemField = (idx, field) => () => {
    const it = items[idx];
    if (!it || !it.id) return;
    const val = (it[field] || '').trim();
    if (!val) return;
    API.put(`/approval/doc-reg/fields/${it.id}`, { [field]: val }).catch(e => showMsg(e.response?.data?.message || '분야 수정 실패', 'error'));
  };
  const deleteItem = (idx) => {
    const it = items[idx];
    if (!it) return;
    if (!window.confirm(`'${it.name}' 분야를 삭제하시겠습니까?`)) return;
    if (!it.id) { setItems(v => v.filter((_, i) => i !== idx)); return; }
    API.delete(`/approval/doc-reg/fields/${it.id}`).then(loadFields).catch(e => showMsg(e.response?.data?.message || '삭제 실패', 'error'));
  };

  useEffect(() => {
    if (!registerApply) return;
    registerApply(() => {
      if (docMode === 'byType') {
        return API.put('/approval/doc-reg/scheme', { mode: 'byType' })
          .then(() => showMsg('현재 구성(분야별관리)이 저장되어 문서번호 생성에 적용되었습니다.', 'success'))
          .catch(e => showMsg(e.response?.data?.message || '적용 중 오류가 발생했습니다.', 'error'));
      }
      const abbr = (ex.abbr || '').trim();
      if (!abbr) { showMsg('회사약어를 입력해주세요.', 'error'); return Promise.resolve(); }
      return API.put('/approval/doc-reg/scheme', { mode: 'single', single_prefix: abbr })
        .then(() => showMsg('현재 구성이 저장되어 문서번호 생성에 적용되었습니다.', 'success'))
        .catch(e => showMsg(e.response?.data?.message || '적용 중 오류가 발생했습니다.', 'error'));
    });
  }, [registerApply, ex.abbr, showMsg, docMode]);

  const setField = (k) => (e) => setEx(v => ({ ...v, [k]: e.target.value }));
  const setRev = (e) => setEx(v => ({ ...v, rev: e.target.value.replace(/[^0-9]/g, '').slice(0, 2) || '00' }));

  const addRow = () => {
    if (!newRow.major_code.trim() || !newRow.minor_code.trim()) return showMsg('대분류/중분류를 입력해주세요.', 'error');
    API.post('/approval/doc-reg/classes', newRow)
      .then(() => { setNewRow({ major_code: '', minor_code: '', name: '', example: '', major_name: '' }); load(); showMsg('분류가 추가되었습니다.'); })
      .catch(e => showMsg(e.response?.data?.message || '추가 실패', 'error'));
  };
  const updateRow = (id, patch) => {
    API.put(`/approval/doc-reg/classes/${id}`, patch).then(load).catch(e => showMsg(e.response?.data?.message || '수정 실패', 'error'));
  };
  const deleteRow = (id) => {
    if (!window.confirm('이 분류를 삭제하시겠습니까?')) return;
    API.delete(`/approval/doc-reg/classes/${id}`).then(() => { load(); showMsg('삭제되었습니다.'); }).catch(e => showMsg(e.response?.data?.message || '삭제 실패', 'error'));
  };
  // 대분류를 기준으로 중분류를 한번에 묶기 — 병합된 대분류 칸을 수정하면 같은 그룹의
  // 모든 행에 일괄 반영한다.
  const renameMajor = (ids, newCode) => {
    Promise.all(ids.map(id => API.put(`/approval/doc-reg/classes/${id}`, { major_code: newCode })))
      .then(() => { load(); showMsg('대분류가 일괄 수정되었습니다.'); })
      .catch(e => showMsg(e.response?.data?.message || '대분류 수정 실패', 'error'));
  };
  // 요청사항 — "대분류 아래쪽 이니셜 칸을 2칸으로 나누고 앞쪽에 입력칸을 추가"(예: "품질매뉴얼 M").
  // 대분류명(major_name)도 major_code처럼 그룹 전체에 일괄 반영한다.
  const renameMajorName = (ids, newName) => {
    Promise.all(ids.map(id => API.put(`/approval/doc-reg/classes/${id}`, { major_name: newName })))
      .then(() => { load(); showMsg('대분류명이 일괄 수정되었습니다.'); })
      .catch(e => showMsg(e.response?.data?.message || '대분류명 수정 실패', 'error'));
  };

  const groups = buildClassGroups(classes);
  const [leftGroups, rightGroups] = splitClassGroups(groups);
  const addInputStyle = { ...inp, marginBottom: 0, padding: '7px 8px', fontSize: 12.5 };

  // 요청사항: "대분류에 선택하는 칸을 지우고 임의로 대분류 이니셜이 입력되게 수정" — 문서분류코드
  // 표와 매칭되는 select 방식을 걷어내고, 다시 자유 입력(텍스트)으로 되돌린다.
  // 요청사항(신규): "양식번호, 문서번호, 기록발생, 외부등록문서에서 대분류 중분류는 각각 따로
  // 사용자가 입력할 수 있도록 수정 (회사약어는 공통으로 사용)" — 패널별로 독립된 major/minor
  // state(formMajor/docMajor/recMajor/extMajor 등)를 참조하도록 필드를 개별 생성한다.
  // 요청사항: "회사약어와 대분류 사이에 '-' 삭제" — 회사약어(첫 필드) 바로 다음에 오는
  // 대분류 필드에만 noDash를 줘서 그 사이의 구분선만 없앤다(대분류-중분류 사이는 유지).
  const majorFieldFor = (panelKey) => ({ key: 'major', label: '대분류', value: ex[`${panelKey}Major`], onChange: setField(`${panelKey}Major`), width: 48, noDash: true });
  const minorFieldFor = (panelKey) => ({ key: 'minor', label: '중분류', value: ex[`${panelKey}Minor`], onChange: setField(`${panelKey}Minor`), width: 56 });

  // 2026-09 요청: 단일관리(ex)와 분야별관리(items[idx])가 동일한 4패널 구성을 공유하도록
  // 임의의 항목 state + setter를 받아 panels 배열을 만들어주는 공용 함수로 추출한다.
  // 요청사항: "양식번호, 문서번호, 기록발생, 외부등록문서에서 글자뒤에 '(생성규칙 예시)' 글자 입력"
  const buildPanels = (state, setF, setR) => [
    { title: '양식번호 (생성규칙 예시)', fields: [
      { key: 'abbr', label: '회사약어', value: state.abbr, onChange: setF('abbr'), width: 56 },
      { key: 'major', label: '대분류', value: state.formMajor, onChange: setF('formMajor'), width: 48, noDash: true },
      { key: 'minor', label: '중분류', value: state.formMinor, onChange: setF('formMinor'), width: 56 },
      { key: 'form', label: '양식번호', value: state.form, onChange: setF('form'), width: 64 },
      { key: 'rev', label: '개정번호', value: `(Rev.${state.rev})`, onChange: setR, width: 80, noDash: true },
    ]},
    { title: '문서번호 (생성규칙 예시)', fields: [
      { key: 'abbr', label: '회사약어', value: state.abbr, onChange: setF('abbr'), width: 56 },
      { key: 'major', label: '대분류', value: state.docMajor, onChange: setF('docMajor'), width: 48, noDash: true },
      { key: 'minor', label: '중분류', value: state.docMinor, onChange: setF('docMinor'), width: 56 },
      { key: 'serial', label: '일련번호', value: state.serial, onChange: setF('serial'), width: 64 },
      { key: 'rev', label: '개정번호', value: `(Rev.${state.rev})`, onChange: setR, width: 80, noDash: true },
    ]},
    { title: '기록·발생 (생성규칙 예시)', fields: [
      { key: 'abbr', label: '회사약어', value: state.abbr, onChange: setF('abbr'), width: 56 },
      { key: 'major', label: '대분류', value: state.recMajor, onChange: setF('recMajor'), width: 48, noDash: true },
      { key: 'minor', label: '중분류', value: state.recMinor, onChange: setF('recMinor'), width: 56 },
      { key: 'yymm', label: '년월', value: state.yymm, onChange: setF('yymm'), width: 56 },
      { key: 'serial', label: '일련번호', value: state.serial, onChange: setF('serial'), width: 64 },
    ]},
    // 요청사항: 외부등록문서는 개정번호 필드 삭제 (회사약어-대분류-중분류-년월-일련번호)
    { title: '외부등록문서 (생성규칙 예시)', fields: [
      { key: 'abbr', label: '회사약어', value: state.abbr, onChange: setF('abbr'), width: 56 },
      { key: 'major', label: '대분류', value: state.extMajor, onChange: setF('extMajor'), width: 48, noDash: true },
      { key: 'minor', label: '중분류', value: state.extMinor, onChange: setF('extMinor'), width: 56 },
      { key: 'yymm', label: '년월', value: state.yymm, onChange: setF('yymm'), width: 56 },
      { key: 'serial', label: '일련번호', value: state.serial, onChange: setF('serial'), width: 64 },
    ]},
  ];

  const panels = buildPanels(ex, setField, setRev);

  return (
    <div>
      {docMode === 'byType' ? (
        items.map((item, idx) => (
          <div key={item.id ?? idx} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <input value={item.name} onChange={setItemName(idx)} onBlur={commitItemField(idx, 'name')}
                placeholder={`분야${idx + 1}`} spellCheck={false}
                style={{ fontSize: 12.5, fontWeight: 700, color: PRIMARY, border: '1px solid #ddd', borderRadius: 6, padding: '4px 8px', width: 100 }} />
              <span style={{ fontSize: 11, color: '#999' }}>회사약어</span>
              <input value={item.abbr} onChange={setItemField(idx, 'abbr')} onBlur={commitItemField(idx, 'abbr')}
                spellCheck={false} style={{ fontSize: 12.5, fontWeight: 700, border: '1px solid #ddd', borderRadius: 6, padding: '4px 8px', width: 64 }} />
              <button type="button" onClick={() => deleteItem(idx)}
                style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>삭제</button>
            </div>
            <div style={{ display: 'flex', gap: 14, marginBottom: 8, flexWrap: 'wrap' }}>
              {buildPanels(item, (k) => setItemField(idx, k), setItemRev(idx)).map(p => (
                <NumFormatPanel key={p.title} title={p.title} fields={p.fields} />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
          {panels.map(p => <NumFormatPanel key={p.title} title={p.title} fields={p.fields} />)}
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 10 }}>📋 문서 분류 코드</div>
      <div style={{ display: 'grid', gridTemplateColumns: '90px 64px 64px 1fr 1.5fr 64px', gap: 6, marginBottom: 10 }}>
        <input placeholder="대분류명 (예: 품질매뉴얼)" value={newRow.major_name} onChange={e => setNewRow(v => ({ ...v, major_name: e.target.value }))}
          spellCheck={false} style={{ ...addInputStyle, textAlign: 'center' }} />
        <input placeholder="대분류" value={newRow.major_code} onChange={e => setNewRow(v => ({ ...v, major_code: e.target.value }))}
          spellCheck={false} style={{ ...addInputStyle, fontFamily: 'monospace', fontWeight: 700, textAlign: 'center' }} />
        <input placeholder="중분류" value={newRow.minor_code} onChange={e => setNewRow(v => ({ ...v, minor_code: e.target.value }))}
          spellCheck={false} style={{ ...addInputStyle, fontFamily: 'monospace', fontWeight: 700, textAlign: 'center' }} />
        <input placeholder="분류명" value={newRow.name} onChange={e => setNewRow(v => ({ ...v, name: e.target.value }))} spellCheck={false} style={addInputStyle} />
        <input placeholder="주요문서" value={newRow.example} onChange={e => setNewRow(v => ({ ...v, example: e.target.value }))} spellCheck={false} style={addInputStyle} />
        <button onClick={addRow} style={{ ...btn(PRIMARY), padding: '8px 10px', fontSize: 12 }}>+ 추가</button>
      </div>

      <div style={{ display: 'flex', gap: 20 }}>
        <ClassTable groups={leftGroups} onUpdate={updateRow} onDelete={deleteRow} onRenameMajor={renameMajor} onRenameMajorName={renameMajorName} />
        <ClassTable groups={rightGroups} onUpdate={updateRow} onDelete={deleteRow} onRenameMajor={renameMajor} onRenameMajorName={renameMajorName} />
      </div>
    </div>
  );
}

// ── 6. 기타 정보 — 자유 서술 입력란(company_info.etc_info) ─────────────────────────────
function EtcInfoSection({ showMsg }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/company-info').then(r => setText(r.data?.etc_info || '')).catch(() => {});
  }, []);

  const save = () => {
    setSaving(true);
    API.put('/company-info', { etc_info: text })
      .then(() => showMsg('저장되었습니다.', 'success'))
      .catch(err => showMsg(err.response?.data?.message || '저장 중 오류가 발생했습니다.', 'error'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
        위 항목들 외에 회사와 관련해 기록해두고 싶은 정보를 자유롭게 입력할 수 있습니다.
      </div>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={12}
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
      <button onClick={save} disabled={saving}
        style={{ width: '100%', marginTop: 12, padding: '10px 18px', background: saving ? '#aaa' : '#185FA5', color: '#fff', border: 'none',
          borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
        {saving ? '저장 중...' : '저장'}
      </button>
    </div>
  );
}
